import React, { useState, useMemo, useRef, useEffect } from "react";
import { Star, TrendingUp, BedDouble, ChevronDown, ChevronsUpDown, X, Eraser, RotateCcw, CalendarDays, Search, Menu, RefreshCw, ClipboardList } from "lucide-react";
import "./Dashboard.css";
import MainDashboard from "./MainDashboard.jsx";
import AgodaPickupDashboard from "./AgodaPickupDashboard.jsx";
import VarianceDashboard from "./VarianceDashboard.jsx";
import Task from "./Task.jsx";
import Calendar from "./Calendar.jsx";
import BookingMetricsDashboard from "./BookingMetricsDashboard.jsx"; // NEW: Booking Metrics dashboard
import MmtMetricsDashboard from "./MmtMetricsDashboard.jsx"; // NEW: MMT Metrics dashboard
const PROPERTY_COL_WIDTH = 200;
const SIDE_COL_WIDTH = 130;
const RATING_COL_WIDTH = 90;
const LOCATION_COL_LEFT = PROPERTY_COL_WIDTH;
const AGENT_COL_LEFT = PROPERTY_COL_WIDTH + SIDE_COL_WIDTH;
const MAX_DATE_COLUMNS = 31;
const TAG_COLOR_PALETTE = [
  "#fde2e2", "#e0f2fe", "#fef9c3", "#dcfce7", "#ede9fe", "#ffe4e6",
  "#e0e7ff", "#fce7f3", "#d1fae5", "#fef3c7", "#e2e8f0", "#cffafe",
];
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
}
function colorForValue(value) {
  if (!value) return undefined;
  const idx = hashString(value) % TAG_COLOR_PALETTE.length;
  return TAG_COLOR_PALETTE[idx];
}
const RATING_OUT_OF_10_PLATFORMS = new Set(["agoda", "booking", "booking.com", "expedia", "hotels"]);
const RATING_OUT_OF_5_PLATFORMS = new Set(["makemytrip", "mmt", "goibibo", "google", "tripadvisor"]);
function getRatingColor(platform, rating) {
  if (rating === null || rating === undefined || rating === "") return "#FFFFFF";
  const plat = String(platform || "").trim().toLowerCase();
  // RATING COLOUR CODE:
  //   out of 10: white 0 | red 0.1–6.0 | yellow 6.1–8.0 | light green 8.1–9.0 | dark green 9.1–10
  //   out of 5:  white 0 | red 0.1–3.0 | yellow 3.1–4.0 | light green 4.1–4.5 | dark green 4.6–5.0
  // Judged on the value as SHOWN (1 decimal), so "8.0" is always yellow
  // and "8.1" always light green.
  const value = Math.round(Number(rating) * 10) / 10;
  if (!(value > 0)) return "#FFFFFF";
  if (RATING_OUT_OF_10_PLATFORMS.has(plat)) {
    if (value >= 9.1) return "#66BB6A";
    if (value >= 8.1) return "#A5D6A7";
    if (value >= 6.1) return "#FFF59D";
    return "#EF9A9A";
  }
  if (RATING_OUT_OF_5_PLATFORMS.has(plat)) {
    if (value >= 4.6) return "#66BB6A";
    if (value >= 4.1) return "#A5D6A7";
    if (value >= 3.1) return "#FFF59D";
    return "#EF9A9A";
  }
  return "#FFFFFF";
}
function formatDateLabel(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function useLastSyncedTracking(storageKey) {
  const [lastSyncedAt, setLastSyncedAt] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.timestamp) return new Date(parsed.timestamp);
      }
    } catch {
    }
    return null;
  });

  function recordSync(rows) {
    const newCount = Array.isArray(rows) ? rows.length : null;
    try {
      const stored = localStorage.getItem(storageKey);
      const prev = stored ? JSON.parse(stored) : null;
      const prevCount = prev ? prev.count : null;
      if (prevCount === null || newCount !== prevCount) {
        const now = new Date();
        localStorage.setItem(storageKey, JSON.stringify({ timestamp: now.toISOString(), count: newCount }));
        setLastSyncedAt(now);
      }
    } catch {
      setLastSyncedAt(new Date());
    }
  }

  return [lastSyncedAt, recordSync];
}
function formatSyncTimestamp(date) {
  if (!date) return "Not yet synced";
  return `Last synced: ${date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`;
}
function LastSyncedLabel({ lastSyncedAt }) {
  return (
    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4, whiteSpace: "nowrap" }}>
      {formatSyncTimestamp(lastSyncedAt)}
    </div>
  );
}
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}

const SYNC_ALLOWED_ROLES = new Set(["SUPER ADMIN", "ADMIN", "OTA MANAGER"]);
function canUseSync() {
  try {
    const role = (localStorage.getItem("loggedInRole") || "").trim().toUpperCase();
    return SYNC_ALLOWED_ROLES.has(role);
  } catch {
    return false;
  }
}

function propertyMatchesFilters(meta, filters, excludeKey) {
  const checks = [
    ["name", filters.properties],
    ["location", filters.locations],
    ["agent", filters.agents],
  ];
  return checks.every(([key, selectedArr]) => {
    if (key === excludeKey) return true;
    if (!selectedArr || selectedArr.length === 0) return true;
    return selectedArr.includes(meta[key]);
  });
}
function getAvailableOptions(field, filters, metaList) {
  const filtered = metaList.filter((m) => propertyMatchesFilters(m, filters, field));
  return Array.from(new Set(filtered.map((m) => m[field]))).sort();
}
function matchesSearch(meta, query) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    (meta.name || "").toLowerCase().includes(q) ||
    (meta.location || "").toLowerCase().includes(q) ||
    (meta.agent || "").toLowerCase().includes(q)
  );
}
const NAV_ITEMS = [
  { id: "rankly", label: "Rankly", icon: Star },
  { id: "revenue", label: "Revenue Dashboard", icon: TrendingUp },
  // The old "Booking Dashboard" (id "booking") and "MMT Dashboard" (id "mmt") are retired from the
  // menu: Booking Metrics and MMT Metrics have everything they had. Their code is still below
  // (render branches kept) so this is easy to undo; see RETIRED_NAV for where old links go.
  { id: "bookingmetrics", label: "Booking Metrics", icon: BedDouble },
  { id: "mmtmetrics", label: "MMT Metrics", icon: BedDouble },
  { id: "mmtcomp", label: "MMT Comp Data", icon: BedDouble },
  { id: "occupancy", label: "Occupancy Dashboard", icon: TrendingUp },
  { id: "bookperday", label: "Book Per Day", icon: BedDouble },
  { id: "agodapickup", label: "Market Demand", icon: TrendingUp },
  { id: "rateparity", label: "Rate Parity", icon: TrendingUp },
  { id: "maindashboard", label: "Master Dashboard", icon: Star },
  { id: "variancedashboard", label: "Variance", icon: TrendingUp },
  { id: "tasks", label: "Tasks", icon: ClipboardList },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
];
const PLATFORM_ORDER = ["agoda", "booking", "makemytrip", "goibibo", "google", "expedia", "hotels", "tripadvisor"];
function sortPlatformsByPreferredOrder(platforms) {
  return [...platforms].sort((a, b) => {
    const ai = PLATFORM_ORDER.indexOf(String(a).toLowerCase());
    const bi = PLATFORM_ORDER.indexOf(String(b).toLowerCase());
    const aRank = ai === -1 ? PLATFORM_ORDER.length : ai;
    const bRank = bi === -1 ? PLATFORM_ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return String(a).localeCompare(String(b));
  });
}
const NAME_MATCH_STOPWORDS = new Set([
  "hotel", "resort", "resorts", "inn", "retreat", "retreats", "residency",
  "palace", "castle", "view", "house", "camp", "camps", "lodge", "villa",
  "villas", "near", "by", "the", "a", "an", "at", "of", "and", "stay",
  "stays", "homestay", "homestays", "guest", "guesthouse", "cottage",
  "cottages", "farm", "farms", "point", "road", "international", "airport",
  "behind", "opp", "opposite", "apartment", "apartments", "studio",
  "wellness",
]);
function meaningfulWords(str) {
  const words = String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const filtered = words.filter((w) => !NAME_MATCH_STOPWORDS.has(w));
  return filtered.length > 0 ? filtered : words;
}
function nameOverlapScore(a, b) {
  const wordsA = new Set(meaningfulWords(a));
  const wordsB = new Set(meaningfulWords(b));
  if (wordsA.size === 0 || wordsB.size === 0) return 0;
  let overlap = 0;
  wordsA.forEach((w) => {
    if (wordsB.has(w)) overlap += 1;
  });
  return overlap / Math.min(wordsA.size, wordsB.size);
}
function useRanklyRatingsLookup() {
  const [rawRows, setRawRows] = useState([]);
  useEffect(() => {
fetch(apiUrl("/api/rankly/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRawRows(json.data);
      })
      .catch(() => {});
  }, []);
  const ratingsData = useMemo(() => {
    const dates = Array.from(new Set(rawRows.map((r) => r.record_date).filter(Boolean))).sort();
    const latestDate = dates[dates.length - 1];
    const map = {};
    if (!latestDate) return { map, names: [], lowerToOriginal: {} };
    rawRows.forEach((r) => {
      if (r.record_date !== latestDate) return;
      const name = r.location_name;
      if (!name) return;
      if (!map[name]) map[name] = {};
      const hasRating = r.avg_rating !== null && r.avg_rating !== "";
      map[name][String(r.platform || "").toLowerCase()] = hasRating
        ? { rating: Number(r.avg_rating), reviews: Number(r.total_reviews) || 0 }
        : null;
    });
    const names = Object.keys(map);
    const lowerToOriginal = {};
    names.forEach((n) => {
      lowerToOriginal[n.trim().toLowerCase()] = n;
    });
    return { map, names, lowerToOriginal };
  }, [rawRows]);
  const resolveName = useMemo(() => {
    const { map, names, lowerToOriginal } = ratingsData;
    const cache = new Map();
    return (hotelName) => {
      if (!hotelName) return null;
      if (cache.has(hotelName)) return cache.get(hotelName);
      let resolved = null;
      if (map[hotelName]) {
        resolved = hotelName;
      } else {
        const lower = hotelName.trim().toLowerCase();
        if (lowerToOriginal[lower]) {
          resolved = lowerToOriginal[lower];
        } else {
          let bestName = null;
          let bestScore = 0;
          names.forEach((n) => {
            const score = nameOverlapScore(hotelName, n);
            if (score > bestScore) {
              bestScore = score;
              bestName = n;
            }
          });
          resolved = bestScore >= 0.6 ? bestName : null;
        }
      }
      cache.set(hotelName, resolved);
      return resolved;
    };
  }, [ratingsData]);
  return useMemo(() => {
    function getRating(hotelName, platform) {
      const resolved = resolveName(hotelName);
      if (!resolved) return null;
      return ratingsData.map[resolved]?.[platform] ?? null;
    }
    return { getRating };
  }, [ratingsData, resolveName]);
}
function RanklyRatingCell({ entry, platform, style, className }) {
  const bg = getRatingColor(platform, entry ? entry.rating : null);
  return (
    <td style={{ ...style, backgroundColor: bg }} className={className}>
      {entry ? (
        <>
          <span className="rd-rating-value">{entry.rating.toFixed(1)}</span>
          <span className="rd-rating-reviews">{entry.reviews} reviews</span>
        </>
      ) : (
        "—"
      )}
    </td>
  );
}
function Sidebar({ active, onSelect, isOpen, onClose }) {
  return (
    <aside className={`rd-sidebar${isOpen ? " rd-sidebar-open" : ""}`}>
      <div className="rd-sidebar-header">
        <div>
          <div className="rd-sidebar-title">Dashboards</div>
          <div className="rd-sidebar-subtitle">Connected data sources</div>
        </div>
        <button className="rd-sidebar-close-btn" onClick={onClose} aria-label="Close menu">
          <X size={18} />
        </button>
      </div>
      <nav className="rd-sidebar-nav">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                onSelect(item.id);
                onClose();
              }}
              className={`rd-nav-item${isActive ? " rd-active" : ""}`}
            >
              <Icon size={16} strokeWidth={2} />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="rd-sidebar-footer">Copyright © 2026 RevX Hospitality</div>
    </aside>
  );
}
function FilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
        <Eraser size={12} />
      </button>
    </div>
  );
}
function MultiSelectFilter({ label, options, selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleOption(opt) {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selected.includes(o));
  const someFilteredSelected = filteredOptions.some((o) => selected.includes(o));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  function toggleSelectAll() {
    if (allFilteredSelected) {
      onChange(selected.filter((s) => !filteredOptions.includes(s)));
    } else {
      const merged = new Set([...selected, ...filteredOptions]);
      onChange(Array.from(merged));
    }
  }

  const summary =
    selected.length === 0
      ? "All"
      : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <FilterLabel label={label} onClear={onClear} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="rd-dropdown-search-clear"
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {filteredOptions.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input
                type="checkbox"
                ref={selectAllRef}
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
              />
              <span>Select All{query.trim() ? ` (${filteredOptions.length} shown)` : ""}</span>
            </label>
          )}

          {filteredOptions.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <label
                  key={opt}
                  className={`rd-option${isSelected ? " rd-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(opt)}
                  />
                  <span>{opt}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
function SearchBox({ value, onChange, onClear }) {
  return (
    <div className="rd-filter-group rd-search-group">
      <FilterLabel label="Search" onClear={onClear} />
      <div className="rd-search-input-wrap">
        <Search size={14} className="rd-search-icon" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Property, location, agent..."
          className="rd-search-input"
        />
        {value && (
          <button onClick={onClear} className="rd-search-clear-btn" title="Clear search">
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
function AvailableDateRangeFilter({ availableDates, fromDate, toDate, onFromChange, onToChange, onClear, onToday }) {
  const toDateOptions = availableDates.filter((d) => d >= fromDate);
  return (
    <div className="rd-filter-group rd-date-range-group">
      <FilterLabel label="Date range" onClear={onClear} />
      <div className="rd-date-inputs">
        <select value={fromDate} onChange={(e) => onFromChange(e.target.value)} className="rd-dropdown-btn">
          {availableDates.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <span className="rd-date-sep">to</span>
        <select value={toDate} onChange={(e) => onToChange(e.target.value)} className="rd-dropdown-btn">
          {toDateOptions.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today">
          <RotateCcw size={12} />
          TODAY
        </button>
      </div>
    </div>
  );
}
function BlankTab({ label }) {
  return <div className="rd-blank-tab">{label} — coming soon</div>;
}
const REV_PROPERTY_COL_WIDTH = 210;
const REV_METRIC_COL_WIDTH = 78;
const REV_TOTAL_COL_WIDTH = 100;
const REV_MAX_DATE_COLUMNS = 31;
const REV_TRAILING_SPACER_WIDTH = 1200;
function getRevenueColor(pct) {
  if (pct === null || pct === undefined) return undefined;
  if (pct < 70) return "#EF9A9A";
  if (pct < 80) return "#FFCC80";
  if (pct < 90) return "#FFF59D";
  if (pct < 100) return "#A5D6A7";
  return "#66BB6A";
}
function getSoldInventoryColor(sold, total) {
  return sold === total ? "#A5D6A7" : "#FFCC80";
}
function formatRevCurrency(value) {
  const num = Number(value) || 0;
  return `₹${Math.round(num).toLocaleString("en-IN")}`;
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diffToMonday);
  return d;
}
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
// Shared OTA/REVX source-classification lists — used by classifySubSource
// (Revenue Dashboard) AND classifyBookPerDaySource (Book Per Day
// dashboard, further down this file). Keep these as the ONE definition:
// they used to be duplicated per-dashboard and drifted out of sync
// (Book Per Day's copy was missing AIRBNB, HOSTEL_WORLD and SIDDARTH),
// silently misclassifying those sources as HOTEL there while Revenue
// correctly grouped them as OTA/REVX.
const SUB_SOURCE_OTA = new Set([
  "BOOKINGDOTCOM", "GOIBIBO", "MMT", "EXPEDIA", "AGODA",
  "EASEMYTRIP", "YATRA", "STAYFLEXI_GHA", "CLEARTRIP",
  "BOOKING", "BOOKMYBOOKING", "AGODA B2B", "AGODA.COM",
  "BOOKING.COM", "CLEARTRIPXML", "EXPEDIA QUICKCONNECT®",
  "GO-MMT", "MAKEMYTRIP", "HOSTELWORLD", "TRIPADVISOR", "TRAVELGURU", "AIRBNB", "HOSTEL_WORLD",
]);
const SUB_SOURCE_REVX = new Set([
  "ALOK GOVIND", "ANSH", "BRIJESH", "DEEPANSHU", "HARSHA",
  "LATA", "NARENDRA", "NARENDRA PANWAR", "SANDEEP",
  "SIDDHARTH", "DS", "NARENDAR NEGI", "SUDHIR",
  "SOCIAL MEDIA", "GOOGLE HOTEL AD", "ALOK", "KAUSHALJHA", "KAUSHAL", "SIDDARTH",
]);
function classifySubSource(source) {
  const s = String(source || "").trim().toUpperCase();
  if (!s) return null;
  if (SUB_SOURCE_OTA.has(s)) return "OTA";
  if (SUB_SOURCE_REVX.has(s)) return "REVX";
  return "HOTEL";
}
function RevenueDashboardTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedPms, setSelectedPms] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [revPctFilter, setRevPctFilter] = useState({ operator: "lte", value: "" });
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedSubSources, setSelectedSubSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activePreset, setActivePreset] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const totalDisplayMode = "sum";
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPopup, setSyncPopup] = useState(null);
  const [lastSyncedAt, recordSync] = useLastSyncedTracking("revx_lastsync_revenue");
  const syncPollRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  useEffect(() => {
    fetch(apiUrl("/api/revenue/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) { setRawRows(json.data); recordSync(json.data); }
        else setLoadError(json.error || "Failed to load data");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  const availableDates = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.record_date).filter(Boolean))).sort(),
    [rawRows]
  );
  const availableMonths = useMemo(
    () => Array.from(new Set(availableDates.map((d) => d.slice(0, 7)))).sort(),
    [availableDates]
  );
  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current && availableDates.length > 0) {
      didInit.current = true;
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFromDate(toISODate(monthStart));
      setToDate(toISODate(monthEnd));
      setSelectedMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
    }
  }, [availableDates]);
  function getWeekBucketsForMonth(monthStr) {
    if (!monthStr) return [];
    const [y, m] = monthStr.split("-").map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const ranges = [
      [1, Math.min(7, daysInMonth)],
      [8, Math.min(14, daysInMonth)],
      [15, Math.min(21, daysInMonth)],
      [22, daysInMonth],
    ].filter(([from]) => from <= daysInMonth);
    return ranges.map(([fromDay, toDay], idx) => {
      const pad = (n) => String(n).padStart(2, "0");
      return {
        label: `WEEK ${idx + 1}`,
        fromDate: `${monthStr}-${pad(fromDay)}`,
        toDate: `${monthStr}-${pad(toDay)}`,
      };
    });
  }
  function applyPreset(preset) {
    setActivePreset(preset);
  }
  function handleMonthChange(month) {
    setSelectedMonth(month);
    setActivePreset(null);
    const [y, m] = month.split("-").map(Number);
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m, 0);
    setFromDate(toISODate(monthStart));
    setToDate(toISODate(monthEnd));
  }
  function handleFromDateChange(v) {
    setActivePreset(null);
    setFromDate(v);
    if (toDate && toDate < v) setToDate(v);
  }
  function handleToDateChange(v) {
    setActivePreset(null);
    setToDate(v);
  }
  function showCurrentMonthRevenue() {
    setActivePreset(null);
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    setFromDate(toISODate(monthStart));
    setToDate(toISODate(monthEnd));
    setSelectedMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  }
  function resetDateRange() {
    if (availableDates.length > 0) {
      const last = availableDates[availableDates.length - 1];
      setFromDate(last);
      setToDate(last);
    }
    setActivePreset(null);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      const key = r.hotel_id || r.hotel_name;
      if (!map.has(key)) {
        map.set(key, {
          name: r.hotel_name || `Unmapped Hotel ${r.hotel_id}`,
          location: r.location || "Unmapped",
          agent: r.agent || "Unmapped",
          pms: r.pms,
          hotelId: r.hotel_id,
        });
      }
    });
    return Array.from(map.values());
  }, [rawRows]);
  const filters = { properties: selectedProperties, locations: selectedLocations, agents: selectedAgents };
  const propertyOptions = useMemo(() => getAvailableOptions("name", filters, propertyMeta), [propertyMeta, selectedLocations, selectedAgents]);
  const locationOptions = useMemo(() => getAvailableOptions("location", filters, propertyMeta), [propertyMeta, selectedProperties, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", filters, propertyMeta), [propertyMeta, selectedProperties, selectedLocations]);
  const pmsOptions = useMemo(() => Array.from(new Set(propertyMeta.map((m) => m.pms).filter(Boolean))).sort(), [propertyMeta]);
  const sourceOptions = useMemo(() => Array.from(new Set(rawRows.map((r) => classifySubSource(r.source)).filter(Boolean))).sort(), [rawRows]);
  const visiblePropertyKeysForSubSource = useMemo(() => {
    return new Set(
      propertyMeta
        .filter((m) => propertyMatchesFilters(m, filters, null))
        .map((m) => m.hotelId || m.name)
    );
  }, [propertyMeta, selectedProperties, selectedLocations, selectedAgents]);
  const subSourceOptions = useMemo(() => {
    let sources = rawRows.filter(
      (r) => r.source && visiblePropertyKeysForSubSource.has(r.hotel_id || r.hotel_name)
    );
    if (selectedSources.length > 0) {
      sources = sources.filter((r) => selectedSources.includes(classifySubSource(r.source)));
    }
    return Array.from(new Set(sources.map((r) => r.source))).sort();
  }, [rawRows, selectedSources, visiblePropertyKeysForSubSource]);

  useEffect(() => {
    setSelectedSubSources((prev) => prev.filter((s) => subSourceOptions.includes(s)));
  }, [subSourceOptions]);
  const modelOptions = useMemo(() => Array.from(new Set(rawRows.map((r) => r.model).filter(Boolean))).sort(), [rawRows]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate).slice(0, REV_MAX_DATE_COLUMNS);
  }, [availableDates, fromDate, toDate]);
  const valuesByDateHotel = useMemo(() => {
    const map = {};
    rawRows.forEach((r) => {
      if (selectedModels.length > 0 && !selectedModels.includes(r.model)) return;
      if (selectedPms.length > 0 && !selectedPms.includes(r.pms)) return;

      const d = r.record_date;
      const key = r.hotel_id || r.hotel_name;
      if (!map[d]) map[d] = {};
      if (!map[d][key]) map[d][key] = { target: 0, revenue: 0 };

      map[d][key].target = Number(r.target) || 0;

      if (selectedSources.length > 0 && !selectedSources.includes(classifySubSource(r.source))) return;
      if (selectedSubSources.length > 0 && !selectedSubSources.includes(r.source)) return;

      map[d][key].revenue += Number(r.revenue) || 0;
    });
    return map;
  }, [rawRows, selectedSubSources, selectedSources, selectedModels, selectedPms]);
  const visibleMeta = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, filters, null)),
    [propertyMeta, selectedProperties, selectedLocations, selectedAgents]
  );
  const rows = useMemo(() => {
    return visibleMeta.map((meta) => {
      const lookupKey = meta.hotelId || meta.name;
      const perDate = dateColumns.map((d) => {
        const cell = valuesByDateHotel[d]?.[lookupKey] || { target: 0, revenue: 0 };
        const pct = cell.target > 0 ? (cell.revenue / cell.target) * 100 : null;
        return { target: cell.target, revenue: cell.revenue, pct };
      });
      const targetSum = perDate.reduce((s, c) => s + c.target, 0);
      const revenueSum = perDate.reduce((s, c) => s + c.revenue, 0);
      const totalPct = targetSum > 0 ? (revenueSum / targetSum) * 100 : null;
      return { property: meta.name, location: meta.location, agent: meta.agent, pms: meta.pms, perDate, total: { targetSum, revenueSum, pct: totalPct } };
    });
  }, [visibleMeta, dateColumns, valuesByDateHotel]);
  const sortedRows = useMemo(() => {
    let arr = [...rows];
    const revPctValue = revPctFilter.value === "" ? null : Number(revPctFilter.value);
    if (revPctValue !== null && Number.isFinite(revPctValue)) {
      arr = arr.filter((r) => {
        if (r.total.pct === null) return false;
        switch (revPctFilter.operator) {
          case "lt": return r.total.pct < revPctValue;
          case "lte": return r.total.pct <= revPctValue;
          case "eq": return r.total.pct === revPctValue;
          case "gt": return r.total.pct > revPctValue;
          case "gte": return r.total.pct >= revPctValue;
          default: return true;
        }
      });
    }
    const dateMatch = sortKey.match(/^revdate:(\d+):(target|revenue|pct)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (sortKey === "totalTarget") {
        const va = a.total.targetSum ?? -Infinity;
        const vb = b.total.targetSum ?? -Infinity;
        cmp = va - vb;
      } else if (sortKey === "totalRevenue") {
        const va = a.total.revenueSum ?? -Infinity;
        const vb = b.total.revenueSum ?? -Infinity;
        cmp = va - vb;
      } else if (sortKey === "totalPct") {
        const va = a.total.pct ?? -Infinity;
        const vb = b.total.pct ?? -Infinity;
        cmp = (va === null ? -Infinity : va) - (vb === null ? -Infinity : vb);
      } else if (dateMatch) {
        const dIdx = Number(dateMatch[1]);
        const field = dateMatch[2];
        const va = a.perDate[dIdx]?.[field] ?? -Infinity;
        const vb = b.perDate[dIdx]?.[field] ?? -Infinity;
        cmp = (va === null ? -Infinity : va) - (vb === null ? -Infinity : vb);
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortKey, sortDirection, revPctFilter]);
  const totals = useMemo(() => {
    return dateColumns.map((d, dIdx) => {
      let target = 0;
      let revenue = 0;
      sortedRows.forEach((row) => {
        target += row.perDate[dIdx]?.target || 0;
        revenue += row.perDate[dIdx]?.revenue || 0;
      });
      const pct = target > 0 ? (revenue / target) * 100 : null;
      return { target, revenue, pct };
    });
  }, [sortedRows, dateColumns]);
  const grandTotal = useMemo(() => {
    let target = 0;
    let revenue = 0;
    sortedRows.forEach((row) => {
      target += row.total.targetSum;
      revenue += row.total.revenueSum;
    });
    const pct = target > 0 ? (revenue / target) * 100 : null;
    return { targetSum: target, revenueSum: revenue, pct };
  }, [sortedRows]);
  const weekBuckets = useMemo(() => getWeekBucketsForMonth(selectedMonth), [selectedMonth]);
  // Shared "% REV" filter (≤30 / ≤50 / ≤70 / ≤90 buttons + custom
  // operator/value) — same rule the REVENUE view applies in sortedRows,
  // reused here so WEEKLY and MTD respect it too. A property with no %
  // (no target) is hidden while a filter is active, same as REVENUE.
  function applyRevPctFilter(list, getPct) {
    const revPctValue = revPctFilter.value === "" ? null : Number(revPctFilter.value);
    if (revPctValue === null || !Number.isFinite(revPctValue)) return list;
    return list.filter((r) => {
      const pct = getPct(r);
      if (pct === null || pct === undefined) return false;
      switch (revPctFilter.operator) {
        case "lt": return pct < revPctValue;
        case "lte": return pct <= revPctValue;
        case "eq": return pct === revPctValue;
        case "gt": return pct > revPctValue;
        case "gte": return pct >= revPctValue;
        default: return true;
      }
    });
  }
  function sumRangeForHotel(lookupKey, fromD, toD) {
    let target = 0;
    let revenue = 0;
    Object.keys(valuesByDateHotel).forEach((d) => {
      if (d < fromD || d > toD) return;
      const cell = valuesByDateHotel[d]?.[lookupKey];
      if (!cell) return;
      target += cell.target || 0;
      revenue += cell.revenue || 0;
    });
    return { target, revenue };
  }
  const allWeeklyRows = useMemo(() => {
    if (weekBuckets.length === 0) return [];
    return visibleMeta.map((meta) => {
      const lookupKey = meta.hotelId || meta.name;
      const perWeek = weekBuckets.map((wk) => {
        const { target, revenue } = sumRangeForHotel(lookupKey, wk.fromDate, wk.toDate);
        const pct = target > 0 ? (revenue / target) * 100 : null;
        return { target, revenue, pct };
      });
      const targetSum = perWeek.reduce((s, c) => s + c.target, 0);
      const revenueSum = perWeek.reduce((s, c) => s + c.revenue, 0);
      const totalPct = targetSum > 0 ? (revenueSum / targetSum) * 100 : null;
      return { property: meta.name, location: meta.location, agent: meta.agent, perWeek, total: { targetSum, revenueSum, pct: totalPct } };
    });
  }, [visibleMeta, weekBuckets, valuesByDateHotel]);
  // WEEKLY: filtered on the month's overall % (the TOTAL % column). The
  // weekly totals row, grand total and count badge all read this
  // filtered list, so they match what's shown.
  const weeklyRows = useMemo(
    () => applyRevPctFilter(allWeeklyRows, (r) => r.total.pct),
    [allWeeklyRows, revPctFilter]
  );
  const weeklyTotals = useMemo(() => {
    return weekBuckets.map((wk, wIdx) => {
      let target = 0;
      let revenue = 0;
      weeklyRows.forEach((row) => {
        target += row.perWeek[wIdx]?.target || 0;
        revenue += row.perWeek[wIdx]?.revenue || 0;
      });
      const pct = target > 0 ? (revenue / target) * 100 : null;
      return { target, revenue, pct };
    });
  }, [weeklyRows, weekBuckets]);
  const weeklyGrandTotal = useMemo(() => {
    let target = 0;
    let revenue = 0;
    weeklyRows.forEach((row) => {
      target += row.total.targetSum;
      revenue += row.total.revenueSum;
    });
    const pct = target > 0 ? (revenue / target) * 100 : null;
    return { targetSum: target, revenueSum: revenue, pct };
  }, [weeklyRows]);
  const sortedWeeklyRows = useMemo(() => {
    const arr = [...weeklyRows];
    const weekMatch = sortKey.match(/^weekdate:(\d+):(target|revenue|pct)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "weekProperty") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "weekTotalTarget") {
        cmp = (a.total.targetSum ?? -Infinity) - (b.total.targetSum ?? -Infinity);
      } else if (sortKey === "weekTotalRevenue") {
        cmp = (a.total.revenueSum ?? -Infinity) - (b.total.revenueSum ?? -Infinity);
      } else if (sortKey === "weekTotalPct") {
        const va = a.total.pct === null ? -Infinity : a.total.pct;
        const vb = b.total.pct === null ? -Infinity : b.total.pct;
        cmp = va - vb;
      } else if (weekMatch) {
        const wIdx = Number(weekMatch[1]);
        const field = weekMatch[2];
        const va = a.perWeek[wIdx]?.[field];
        const vb = b.perWeek[wIdx]?.[field];
        cmp = (va === null || va === undefined ? -Infinity : va) - (vb === null || vb === undefined ? -Infinity : vb);
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [weeklyRows, sortKey, sortDirection]);
  const mtdRange = useMemo(() => {
    if (!selectedMonth) return null;
    const [y, m] = selectedMonth.split("-").map(Number);
    const monthStart = `${selectedMonth}-01`;
    const daysInMonth = new Date(y, m, 0).getDate();
    const pad = (n) => String(n).padStart(2, "0");
    const monthEnd = `${selectedMonth}-${pad(daysInMonth)}`;
    const today = new Date();
    const todayStr = toISODate(today);
    const isCurrentRealMonth = selectedMonth === `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
    const mtdEnd = isCurrentRealMonth ? todayStr : monthEnd;
    return { monthStart, monthEnd, mtdEnd };
  }, [selectedMonth]);
  const allMtdRows = useMemo(() => {
    if (!mtdRange) return [];
    return visibleMeta.map((meta) => {
      const lookupKey = meta.hotelId || meta.name;
      const full = sumRangeForHotel(lookupKey, mtdRange.monthStart, mtdRange.monthEnd);
      const mtd = sumRangeForHotel(lookupKey, mtdRange.monthStart, mtdRange.mtdEnd);
      const fullPct = full.target > 0 ? (full.revenue / full.target) * 100 : null;
      const mtdPct = mtd.target > 0 ? (mtd.revenue / mtd.target) * 100 : null;
      return {
        property: meta.name, location: meta.location, agent: meta.agent,
        target: full.target, revenue: full.revenue, pct: fullPct,
        mtdTarget: mtd.target, mtdRevenue: mtd.revenue, mtdPct,
      };
    });
  }, [visibleMeta, mtdRange, valuesByDateHotel]);
  // MTD: filtered on "MTD % ACHIEVED" (month start -> today). Totals and
  // the count badge read this filtered list.
  const mtdRows = useMemo(
    () => applyRevPctFilter(allMtdRows, (r) => r.mtdPct),
    [allMtdRows, revPctFilter]
  );
  const mtdTotals = useMemo(() => {
    const t = { target: 0, revenue: 0, mtdTarget: 0, mtdRevenue: 0 };
    mtdRows.forEach((row) => {
      t.target += row.target; t.revenue += row.revenue;
      t.mtdTarget += row.mtdTarget; t.mtdRevenue += row.mtdRevenue;
    });
    return {
      ...t,
      pct: t.target > 0 ? (t.revenue / t.target) * 100 : null,
      mtdPct: t.mtdTarget > 0 ? (t.mtdRevenue / t.mtdTarget) * 100 : null,
    };
  }, [mtdRows]);
  const sortedMtdRows = useMemo(() => {
    const arr = [...mtdRows];
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "mtdProperty") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "mtdFullTarget") cmp = (a.target ?? -Infinity) - (b.target ?? -Infinity);
      else if (sortKey === "mtdFullRevenue") cmp = (a.revenue ?? -Infinity) - (b.revenue ?? -Infinity);
      else if (sortKey === "mtdFullPct") {
        const va = a.pct === null ? -Infinity : a.pct;
        const vb = b.pct === null ? -Infinity : b.pct;
        cmp = va - vb;
      } else if (sortKey === "mtdMtdTarget") cmp = (a.mtdTarget ?? -Infinity) - (b.mtdTarget ?? -Infinity);
      else if (sortKey === "mtdMtdRevenue") cmp = (a.mtdRevenue ?? -Infinity) - (b.mtdRevenue ?? -Infinity);
      else if (sortKey === "mtdMtdPct") {
        const va = a.mtdPct === null ? -Infinity : a.mtdPct;
        const vb = b.mtdPct === null ? -Infinity : b.mtdPct;
        cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [mtdRows, sortKey, sortDirection]);
  const legendLocations = useMemo(
    () => Array.from(new Set(propertyMeta.map((m) => m.location).filter(Boolean))).sort(),
    [propertyMeta]
  );
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }
  function clearAllFilters() {
    setSelectedPms([]); setSelectedAgents([]); setSelectedLocations([]);
    setSelectedProperties([]); setSelectedSubSources([]); setSelectedSources([]);
    setSelectedModels([]); setSearchDummy();
    setRevPctFilter({ operator: "lte", value: "" });
    resetDateRange();
  }
  function setSearchDummy() {}
  const REVENUE_SYNC_POLL_INTERVAL_MS = 30000;
  const REVENUE_SYNC_WAIT_MS = 15.5 * 60 * 1000;
  function stopSyncPolling() {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
  }
  async function refreshRevenueOnce() {
    const res = await fetch(apiUrl("/api/revenue/summary/refresh"));
    const json = await res.json();
    if (!json.success) throw new Error(json.error || "Refresh failed");
    return json.data;
  }
  async function handleSyncClick() {
    if (syncing) return;
    setSyncing(true);
    setSyncPopup(null);
    try {
      const res = await fetch("/api/revenue/sync", { method: "POST" });
      const json = await res.json();
      if (!json.success) {
        setSyncing(false);
        setSyncPopup({ type: "error", message: json.error || "Couldn't start sync." });
        return;
      }
    } catch (err) {
      setSyncing(false);
      setSyncPopup({ type: "error", message: err.message });
      return;
    }
    syncPollRef.current = setInterval(async () => {
      try {
        const data = await refreshRevenueOnce();
        setRawRows(data); recordSync(data);
      } catch {
      }
    }, REVENUE_SYNC_POLL_INTERVAL_MS);
    syncTimeoutRef.current = setTimeout(async () => {
      stopSyncPolling();
      try {
        const data = await refreshRevenueOnce();
        setRawRows(data); recordSync(data);
        setSyncPopup({ type: "success", message: "Data has been refreshed." });
      } catch (err) {
        setSyncPopup({ type: "error", message: err.message });
      } finally {
        setSyncing(false);
      }
    }, REVENUE_SYNC_WAIT_MS);
  }
  useEffect(() => {
    return () => stopSyncPolling();
  }, []);
  useEffect(() => {
    if (!syncPopup) return;
    const t = setTimeout(() => setSyncPopup(null), 8000);
    return () => clearTimeout(t);
  }, [syncPopup]);
  if (loading) return <div className="rd-blank-tab">Loading revenue data...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load revenue data: {loadError}</div>;
  return (
    <>
      {syncPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${syncPopup.type}`}>
          <span>{syncPopup.message}</span>
          <button onClick={() => setSyncPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <div className="rd-filter-group rd-date-range-group">
              <div className="rd-filter-label-row">
                <label className="rd-filter-label">
                  <CalendarDays size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
                  DATE
                </label>
                <button onClick={resetDateRange} title="Clear date range" className="rd-clear-btn">
                  <Eraser size={12} />
                </button>
              </div>
              <div className="occ-date-inputs">
                <input type="date" value={fromDate} onChange={(e) => handleFromDateChange(e.target.value)} className="occ-date-input" />
                <span className="rd-date-sep">to</span>
                <input type="date" value={toDate} onChange={(e) => handleToDateChange(e.target.value)} className="occ-date-input" />
              </div>
            </div>
            <div className="rd-filter-group">
              <FilterLabel label="Month" onClear={() => {}} />
              <select value={selectedMonth} onChange={(e) => handleMonthChange(e.target.value)} className="rd-dropdown-btn">
                {availableMonths.map((m) => (
                  <option key={m} value={m}>
                    {new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  </option>
                ))}
              </select>
            </div>
            <div className="rev-preset-group">
              <button onClick={showCurrentMonthRevenue} className={`booking-tab-btn${activePreset === null ? " booking-tab-btn-active" : ""}`}>REVENUE</button>
              <button onClick={() => applyPreset("weekly")} className={`booking-tab-btn${activePreset === "weekly" ? " booking-tab-btn-active" : ""}`}>WEEKLY</button>
              <button onClick={() => applyPreset("mtd")} className={`booking-tab-btn${activePreset === "mtd" ? " booking-tab-btn-active" : ""}`}>MTD</button>
            </div>
            <MultiSelectFilter label="PMS" options={pmsOptions} selected={selectedPms} onChange={setSelectedPms} onClear={() => setSelectedPms([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Property Name" options={propertyOptions} selected={selectedProperties} onChange={setSelectedProperties} onClear={() => setSelectedProperties([])} />
            <MultiSelectFilter label="Source" options={sourceOptions} selected={selectedSources} onChange={setSelectedSources} onClear={() => setSelectedSources([])} />
            <MultiSelectFilter label="Sub Source" options={subSourceOptions} selected={selectedSubSources} onChange={setSelectedSubSources} onClear={() => setSelectedSubSources([])} />
            {modelOptions.length > 0 && (
              <MultiSelectFilter label="Model" options={modelOptions} selected={selectedModels} onChange={setSelectedModels} onClear={() => setSelectedModels([])} />
            )}
            <div className="rd-filter-group rd-pct-filter-group">
              <FilterLabel label="% Rev" onClear={() => setRevPctFilter({ operator: "lte", value: "" })} />
              <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                {[30, 50, 70, 90].map((threshold) => {
                  const isActive = revPctFilter.operator === "lte" && Number(revPctFilter.value) === threshold;
                  return (
                    <button
                      key={threshold}
                      onClick={() =>
                        setRevPctFilter((prev) =>
                          prev.operator === "lte" && Number(prev.value) === threshold
                            ? { operator: "lte", value: "" }
                            : { operator: "lte", value: threshold }
                        )
                      }
                      className="rd-dropdown-btn"
                      style={{
                        width: "auto",
                        flex: "0 0 auto",
                        ...(isActive ? { backgroundColor: "#1e293b", color: "#fff", borderColor: "#1e293b" } : {}),
                      }}
                    >
                      ≤{threshold}%
                    </button>
                  );
                })}
                <select
                  value={revPctFilter.operator}
                  onChange={(e) => setRevPctFilter((prev) => ({ ...prev, operator: e.target.value }))}
                  className="rd-dropdown-btn"
                  title="Comparison"
                  style={{ width: "auto", flex: "0 0 auto" }}
                >
                  <option value="eq">=</option>
                  <option value="gt">&gt;</option>
                  <option value="gte">&ge;</option>
                  <option value="lt">&lt;</option>
                  <option value="lte">&le;</option>
                </select>
                <input
                  type="number"
                  value={revPctFilter.value}
                  onChange={(e) => setRevPctFilter((prev) => ({ ...prev, value: e.target.value }))}
                  placeholder="%"
                  className="rd-dropdown-btn"
                  style={{ width: 70, flex: "0 0 auto" }}
                />
              </div>
            </div>
            <div className="rd-actions">
              {canUseSync() && (
                <button
                  onClick={handleSyncClick}
                  disabled={syncing}
                  className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`}
                  title="Fetch the latest StayFlexi bookings now"
                >
                  <RefreshCw size={14} className={syncing ? "rd-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync"}
                </button>
              )}
              <LastSyncedLabel lastSyncedAt={lastSyncedAt} />
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
          {legendLocations.length > 0 && (
            <div className="rev-location-legend">
              {legendLocations.map((loc) => (
                <span key={loc} className="rev-legend-item">
                  <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(loc) }} />
                  {loc}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            {activePreset === "weekly" ? (
              <table className="rd-table occ-table">
                <colgroup>
                  <col style={{ width: REV_PROPERTY_COL_WIDTH }} />
                  {weekBuckets.map((wk) => [...Array(3)].map((_, i) => <col key={`${wk.label}-${i}-col`} style={{ width: REV_METRIC_COL_WIDTH }} />))}
                  {[...Array(3)].map((_, i) => <col key={`weekly-total-${i}-col`} style={{ width: REV_TOTAL_COL_WIDTH }} />)}
                  <col style={{ width: REV_TRAILING_SPACER_WIDTH }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} onClick={() => handleHeaderClick("weekProperty")} style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className="rd-th-fixed">
                      <span className="rd-count-badge">{weeklyRows.length}</span>
                      PROPERTY NAME
                      {sortKey === "weekProperty" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    {weekBuckets.map((wk) => (
                      <th key={wk.label} colSpan={3} className="occ-th-group">{wk.label}</th>
                    ))}
                    <th colSpan={3} className="occ-th-group rd-total-edge rd-total-header-blank">
                      TOTAL
                    </th>
                    <th className="occ-th-group" aria-hidden="true" />
                  </tr>
                  <tr>
                    {weekBuckets.map((wk, wIdx) => {
                      const targetKey = `weekdate:${wIdx}:target`;
                      const revenueKey = `weekdate:${wIdx}:revenue`;
                      const pctKey = `weekdate:${wIdx}:pct`;
                      return (
                        <React.Fragment key={`${wk.label}-sub`}>
                          <th onClick={() => handleHeaderClick(targetKey)} className="occ-th-metric">
                            TARGET
                            {sortKey === targetKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          <th onClick={() => handleHeaderClick(revenueKey)} className="occ-th-metric">
                            REVENUE
                            {sortKey === revenueKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          <th onClick={() => handleHeaderClick(pctKey)} className="occ-th-metric">
                            %
                            {sortKey === pctKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                        </React.Fragment>
                      );
                    })}
                    <th onClick={() => handleHeaderClick("weekTotalTarget")} className="occ-th-metric rd-total-edge rd-total-grid-dark">
                      TARGET (SUM)
                      {sortKey === "weekTotalTarget" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("weekTotalRevenue")} className="occ-th-metric rd-total-grid-dark">
                      REVENUE (SUM)
                      {sortKey === "weekTotalRevenue" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("weekTotalPct")} className="occ-th-metric rd-total-grid-dark">
                      %
                      {sortKey === "weekTotalPct" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th className="occ-th-metric" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {sortedWeeklyRows.map((row, rIdx) => {
                    const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                    return (
                      <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`}>
                        <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                          <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(row.location) }} />
                          {row.property}
                        </td>
                        {row.perWeek.map((cell, wIdx) => (
                          <React.Fragment key={wIdx}>
                            <td className="occ-td-t">{formatRevCurrency(cell.target)}</td>
                            <td style={{ backgroundColor: getRevenueColor(cell.pct) }}>{formatRevCurrency(cell.revenue)}</td>
                            <td className="rev-pct-plain">{cell.pct === null ? "—" : `${cell.pct.toFixed(0)}%`}</td>
                          </React.Fragment>
                        ))}
                        {(() => {
                          const dT = row.total.targetSum;
                          const dR = row.total.revenueSum;
                          const dP = dT > 0 ? (dR / dT) * 100 : null;
                          return (
                            <>
                              <td className="occ-td-t rd-total-edge" style={{ fontWeight: 700 }}>{formatRevCurrency(dT)}</td>
                              <td style={{ fontWeight: 700, backgroundColor: getRevenueColor(dP) }}>{formatRevCurrency(dR)}</td>
                              <td className="rev-pct-plain" style={{ fontWeight: 700 }}>{dP === null ? "—" : `${dP.toFixed(0)}%`}</td>
                            </>
                          );
                        })()}
                        <td className={rowBgClass} aria-hidden="true" />
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="booking-total-row">
                    <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className="rd-td-fixed">TOTAL</td>
                    {weeklyTotals.map((t, wIdx) => (
                      <React.Fragment key={wIdx}>
                        <td className="occ-td-t">{formatRevCurrency(t.target)}</td>
                        <td className="occ-td-t">{formatRevCurrency(t.revenue)}</td>
                        <td className="occ-td-t">{t.pct === null ? "—" : `${t.pct.toFixed(0)}%`}</td>
                      </React.Fragment>
                    ))}
                    {(() => {
                      const dT = weeklyGrandTotal.targetSum;
                      const dR = weeklyGrandTotal.revenueSum;
                      const dP = dT > 0 ? (dR / dT) * 100 : null;
                      return (
                        <>
                          <td className="occ-td-t rd-total-edge">{formatRevCurrency(dT)}</td>
                          <td className="occ-td-t">{formatRevCurrency(dR)}</td>
                          <td className="occ-td-t">{dP === null ? "—" : `${dP.toFixed(0)}%`}</td>
                        </>
                      );
                    })()}
                    <td aria-hidden="true" />
                  </tr>
                </tfoot>
              </table>
            ) : activePreset === "mtd" ? (
              <table className="rd-table occ-table">
                <colgroup>
                  <col style={{ width: REV_PROPERTY_COL_WIDTH }} />
                  {[...Array(6)].map((_, i) => <col key={`mtd-col-${i}`} style={{ width: REV_TOTAL_COL_WIDTH }} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th onClick={() => handleHeaderClick("mtdProperty")} style={{ left: 0, width: REV_PROPERTY_COL_WIDTH, cursor: "pointer" }} className="rd-th-fixed">
                      <span className="rd-count-badge">{mtdRows.length}</span>
                      PROPERTY NAME
                      {sortKey === "mtdProperty" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdFullTarget")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      TARGET
                      {sortKey === "mtdFullTarget" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdFullRevenue")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      REVENUE
                      {sortKey === "mtdFullRevenue" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdFullPct")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      % ACHIEVED
                      {sortKey === "mtdFullPct" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdMtdTarget")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      MTD TARGET
                      {sortKey === "mtdMtdTarget" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdMtdRevenue")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      MTD REVENUE
                      {sortKey === "mtdMtdRevenue" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("mtdMtdPct")} className="occ-th-metric booking-th-single" style={{ cursor: "pointer" }}>
                      MTD % ACHIEVED
                      {sortKey === "mtdMtdPct" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedMtdRows.map((row, rIdx) => {
                    const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                    return (
                      <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`}>
                        <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                          <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(row.location) }} />
                          {row.property}
                        </td>
                        <td className="occ-td-t">{formatRevCurrency(row.target)}</td>
                        <td style={{ backgroundColor: getRevenueColor(row.pct) }}>{formatRevCurrency(row.revenue)}</td>
                        <td className="rev-pct-plain">{row.pct === null ? "—" : `${row.pct.toFixed(0)}%`}</td>
                        <td className="occ-td-t">{formatRevCurrency(row.mtdTarget)}</td>
                        <td style={{ backgroundColor: getRevenueColor(row.mtdPct) }}>{formatRevCurrency(row.mtdRevenue)}</td>
                        <td className="rev-pct-plain">{row.mtdPct === null ? "—" : `${row.mtdPct.toFixed(0)}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="booking-total-row">
                    <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className="rd-td-fixed">TOTAL</td>
                    <td className="occ-td-t">{formatRevCurrency(mtdTotals.target)}</td>
                    <td className="occ-td-t">{formatRevCurrency(mtdTotals.revenue)}</td>
                    <td className="occ-td-t">{mtdTotals.pct === null ? "—" : `${mtdTotals.pct.toFixed(0)}%`}</td>
                    <td className="occ-td-t">{formatRevCurrency(mtdTotals.mtdTarget)}</td>
                    <td className="occ-td-t">{formatRevCurrency(mtdTotals.mtdRevenue)}</td>
                    <td className="occ-td-t">{mtdTotals.mtdPct === null ? "—" : `${mtdTotals.mtdPct.toFixed(0)}%`}</td>
                  </tr>
                </tfoot>
              </table>
            ) : (
              <table className="rd-table occ-table">
                <colgroup>
                  <col style={{ width: REV_PROPERTY_COL_WIDTH }} />
                  {dateColumns.map((d) => [...Array(3)].map((_, i) => <col key={`${d}-${i}-col`} style={{ width: REV_METRIC_COL_WIDTH }} />))}
                  {[...Array(3)].map((_, i) => <col key={`total-${i}-col`} style={{ width: REV_TOTAL_COL_WIDTH }} />)}
                  <col style={{ width: REV_TRAILING_SPACER_WIDTH }} />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan={2} onClick={() => handleHeaderClick("property")} style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className="rd-th-fixed">
                      <span className="rd-count-badge">{sortedRows.length}</span>
                      PROPERTY NAME
                      {sortKey === "property" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    {dateColumns.map((d) => (
                      <th key={d} colSpan={3} className="occ-th-group">
                        {new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "2-digit" })}
                      </th>
                    ))}
                    <th colSpan={3} className="occ-th-group rd-total-edge rd-total-header-blank">
                      TOTAL
                    </th>
                    <th className="occ-th-group" aria-hidden="true" />
                  </tr>
                  <tr>
                    {dateColumns.map((d, dIdx) => {
                      const targetKey = `revdate:${dIdx}:target`;
                      const revenueKey = `revdate:${dIdx}:revenue`;
                      const pctKey = `revdate:${dIdx}:pct`;
                      return (
                        <React.Fragment key={`${d}-sub`}>
                          <th onClick={() => handleHeaderClick(targetKey)} className="occ-th-metric">
                            TARGET
                            {sortKey === targetKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          <th onClick={() => handleHeaderClick(revenueKey)} className="occ-th-metric">
                            REVENUE
                            {sortKey === revenueKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          <th onClick={() => handleHeaderClick(pctKey)} className="occ-th-metric">
                            % REV
                            {sortKey === pctKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                        </React.Fragment>
                      );
                    })}
                    <th onClick={() => handleHeaderClick("totalTarget")} className="occ-th-metric rd-total-edge rd-total-grid-dark">
                      TARGET (SUM)
                      {sortKey === "totalTarget" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("totalRevenue")} className="occ-th-metric rd-total-grid-dark">
                      REVENUE (SUM)
                      {sortKey === "totalRevenue" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th onClick={() => handleHeaderClick("totalPct")} className="occ-th-metric rd-total-grid-dark">
                      % REV
                      {sortKey === "totalPct" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                    <th className="occ-th-metric" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, rIdx) => {
                    const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                    return (
                      <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`}>
                        <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                          <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(row.location) }} />
                          {row.property}
                        </td>
                        {row.perDate.map((cell, dIdx) => (
                          <React.Fragment key={dIdx}>
                            <td className="occ-td-t">{formatRevCurrency(cell.target)}</td>
                            <td style={{ backgroundColor: getRevenueColor(cell.pct) }}>{formatRevCurrency(cell.revenue)}</td>
                            <td className="rev-pct-plain">{cell.pct === null ? "—" : `${cell.pct.toFixed(2)}%`}</td>
                          </React.Fragment>
                        ))}
                        {(() => {
                          const dT = row.total.targetSum;
                          const dR = row.total.revenueSum;
                          const dP = dT > 0 ? (dR / dT) * 100 : null;
                          return (
                            <>
                              <td className="occ-td-t rd-total-edge" style={{ fontWeight: 700 }}>{formatRevCurrency(dT)}</td>
                              <td style={{ fontWeight: 700, backgroundColor: getRevenueColor(dP) }}>{formatRevCurrency(dR)}</td>
                              <td className="rev-pct-plain" style={{ fontWeight: 700 }}>{dP === null ? "—" : `${dP.toFixed(2)}%`}</td>
                            </>
                          );
                        })()}
                        <td className={rowBgClass} aria-hidden="true" />
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="booking-total-row">
                    <td style={{ left: 0, width: REV_PROPERTY_COL_WIDTH }} className="rd-td-fixed">TOTAL</td>
                    {totals.map((t, dIdx) => (
                      <React.Fragment key={dIdx}>
                        <td className="occ-td-t">{formatRevCurrency(t.target)}</td>
                        <td className="occ-td-t">{formatRevCurrency(t.revenue)}</td>
                        <td className="occ-td-t">{t.pct === null ? "—" : `${t.pct.toFixed(2)}%`}</td>
                      </React.Fragment>
                    ))}
                    {(() => {
                      const dT = grandTotal.targetSum;
                      const dR = grandTotal.revenueSum;
                      const dP = dT > 0 ? (dR / dT) * 100 : null;
                      return (
                        <>
                          <td className="occ-td-t rd-total-edge">{formatRevCurrency(dT)}</td>
                          <td className="occ-td-t">{formatRevCurrency(dR)}</td>
                          <td className="occ-td-t">{dP === null ? "—" : `${dP.toFixed(2)}%`}</td>
                        </>
                      );
                    })()}
                    <td aria-hidden="true" />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
// One TOTAL cell at the end of the Rankly table (one per platform).
// Same colours as the daily cells (getRatingColor) so green = good.
function RanklyTotalCell({ total, platform, viewMode, isFirst }) {
  const cls = `rd-td-rating rd-total-td${isFirst ? " rd-total-edge" : ""}`;
  if (!total) {
    return <td className={cls} title="No data in the selected range">—</td>;
  }
  const days = total.days || 1;
  const since = total.baseDate ? formatDateLabel(total.baseDate) : null;
  // Reviews line(s): added and removed are each SUMMED over the range and
  // shown separately, so a drop in reviews is never hidden by additions.
  const addedWord = viewMode === "new" ? "new" : "reviews";
  const removedLine = total.removed > 0 ? (
    <span className="rd-rating-reviews" style={{ color: "#b91c1c", fontWeight: 600 }}>
      −{total.removed} removed
    </span>
  ) : null;
  const reviewLines = total.netNew === null ? null : (
    <>
      {(total.added > 0 || total.removed === 0) && (
        <span className="rd-rating-reviews">+{total.added} {addedWord}</span>
      )}
      {removedLine}
    </>
  );
  const reviewsTitle = total.netNew === null
    ? ""
    : ` Reviews over the range: +${total.added} added, −${total.removed} removed (net ${total.netNew >= 0 ? "+" : ""}${total.netNew}).`;

  if (viewMode !== "new") {
    return (
      <td
        className={cls}
        style={{ backgroundColor: getRatingColor(platform, total.avgRating) }}
        title={`Average rating across the selected range: ${total.avgRating.toFixed(2)}.` +
          reviewsTitle + (since ? ` Counted since ${since}.` : "")}
      >
        <span className="rd-rating-value">{total.avgRating.toFixed(1)}</span>
        {reviewLines || (
          <span className="rd-rating-reviews">avg of {days} {days === 1 ? "day" : "days"}</span>
        )}
      </td>
    );
  }

  // NEW REVIEWS ONLY view
  const v = total.newStats || {};
  if (v.noPrev) {
    return <td className={cls} title="No earlier snapshot to compare with">—</td>;
  }
  // More reviews removed than added (net drop), or no net change: there's
  // no new-review score to show, but both counts still are.
  if (v.removed || v.noNew) {
    const anyChange = total.added > 0 || total.removed > 0;
    return (
      <td
        className={cls}
        style={total.removed > 0 ? { backgroundColor: "#fef3c7" } : undefined}
        title={(anyChange ? "No new-review score (reviews went down or didn't change overall)." : "No new reviews in the selected range.") + reviewsTitle}
      >
        {anyChange ? reviewLines : <span className="rd-rating-reviews">No new</span>}
      </td>
    );
  }
  // Star score of the new reviews over the whole range — exact, or
  // calculated from only a few new reviews (explained in the hover text).
  if (typeof v.rating === "number") {
    return (
      <td
        className={cls}
        style={{ backgroundColor: getRatingColor(platform, v.rating) }}
        title={newReviewScoreTitle(v, reviewsTitle)}
      >
        <span className="rd-rating-value">★ {v.rating.toFixed(1)}</span>
        {reviewLines}
      </td>
    );
  }
  return <td className={cls}>—</td>;
}
// Shared "new reviews only" math — used by the per-day cells AND the
// range TOTAL column, so both always agree. Compares two snapshots of the
// same property/platform and works out the STAR SCORE the guests gave in
// the new reviews:
//   new-review avg = (today score × today reviews − yesterday score × yesterday reviews) ÷ new reviews
// Rankly publishes ratings rounded to 1 decimal (±0.05), so with only a
// few new reviews on a big total the result can be off. We always SHOW a
// score now (no "= 0.0" / "▼ −0.1" rating changes), but flag it:
//   estimated: false -> exact enough, coloured like any rating
//   estimated: true  -> calculated from only a few reviews; still shown
//                       as "★ 7.9" (hover text explains it)
// The score is kept inside the range that's actually possible (a guest
// can't give below 1 or above the platform's maximum).
const NEW_REVIEW_MIN_SCORE = 1;
function ratingScaleFor(platform, cur, prev) {
  const plat = String(platform || "").trim().toLowerCase();
  if (RATING_OUT_OF_10_PLATFORMS.has(plat)) return 10;
  if (RATING_OUT_OF_5_PLATFORMS.has(plat)) return 5;
  return Math.max(cur.rating, prev.rating) > 5 ? 10 : 5;
}
function computeNewReviewStats(prev, cur, sinceDate = null, platform = null) {
  const newCount = cur.reviews - prev.reviews;
  if (newCount < 0) return { removed: -newCount, sinceDate };
  if (newCount === 0) return { noNew: true, sinceDate };
  const H = 0.05;
  const scale = ratingScaleFor(platform, cur, prev);
  const clamp = (x) => Math.min(scale, Math.max(NEW_REVIEW_MIN_SCORE, x));
  const raw = (cur.rating * cur.reviews - prev.rating * prev.reviews) / newCount;
  const lo = clamp(((cur.rating - H) * cur.reviews - (prev.rating + H) * prev.reviews) / newCount);
  const hi = clamp(((cur.rating + H) * cur.reviews - (prev.rating - H) * prev.reviews) / newCount);
  const maxWidth = scale === 10 ? 1.0 : 0.5;
  return {
    reviews: newCount,
    total: cur.reviews,
    lo,
    hi,
    sinceDate,
    scale,
    rating: Math.min(hi, Math.max(lo, raw)),
    estimated: hi - lo > maxWidth,
  };
}
function newReviewScoreTitle(v, extra = "") {
  const scoreText = `${v.rating.toFixed(1)}/${v.scale}`;
  return (v.estimated
    ? `Calculated star rating of the ${v.reviews} new review(s): ${scoreText}. Calculated from only ${v.reviews} new on ${v.total} total, and Rankly rounds ratings, so the guests' real score could be anywhere ${v.lo.toFixed(1)}–${v.hi.toFixed(1)}.`
    : `Average star score given by the ${v.reviews} new review(s): ${scoreText} (possible range ${v.lo.toFixed(1)}–${v.hi.toFixed(1)}).`) + extra;
}
function RanklyTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [platformFilter, setPlatformFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPopup, setSyncPopup] = useState(null);
  const [lastSyncedAt, recordSync] = useLastSyncedTracking("revx_lastsync_rankly");
  // "all" = the rating exactly as Rankly reports it (all-time average, as
  // before). "new" = the average of ONLY the reviews added since that
  // property/platform's previous snapshot, using:
  //   new-review avg = (today score × today reviews − prev score × prev reviews) ÷ new reviews
  const [ranklyViewMode, setRanklyViewMode] = useState("all");
  useEffect(() => {
    fetch(apiUrl("/api/rankly/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setRawRows(json.data); recordSync(json.data);
        } else {
          setLoadError(json.error || "Failed to load data");
        }
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  const availableDates = useMemo(() => {
    return Array.from(new Set(rawRows.map((r) => r.record_date).filter(Boolean))).sort();
  }, [rawRows]);
  useEffect(() => {
    if (availableDates.length > 0 && !fromDate && !toDate) {
      const latest = availableDates[availableDates.length - 1];
      setFromDate(latest);
      setToDate(latest);
    }
  }, [availableDates]);
  function resetDateRange() {
    if (availableDates.length > 0) {
      const latest = availableDates[availableDates.length - 1];
      setFromDate(latest);
      setToDate(latest);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) {
      setToDate(newFrom);
    }
  }
  function goToToday() {
    if (availableDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0
      ? pastOrToday[pastOrToday.length - 1]
      : availableDates[availableDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  async function handleSyncClick() {
    if (syncing) return;
    setSyncing(true);
    setSyncPopup(null);
    try {
      const res = await fetch(apiUrl("/api/rankly/sync"), { method: "POST" });
      const json = await res.json();
      if (json.success) {
        setRawRows(json.data); recordSync(json.data);
        setSyncPopup({ type: "success", message: "Data has been refreshed." });
      } else {
        setSyncPopup({ type: "error", message: json.error || "Sync failed. Please try again." });
      }
    } catch (err) {
      setSyncPopup({ type: "error", message: err.message });
    } finally {
      setSyncing(false);
    }
  }
  useEffect(() => {
    if (!syncPopup) return;
    const t = setTimeout(() => setSyncPopup(null), 6000);
    return () => clearTimeout(t);
  }, [syncPopup]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate).slice(0, MAX_DATE_COLUMNS);
  }, [availableDates, fromDate, toDate]);
  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((row) => {
      if (!map.has(row.location_id)) {
        map.set(row.location_id, {
          locationId: row.location_id,
          name: row.location_name,
          location: row.location,
          agent: row.agent,
          masterId: row.master_id,
        });
      }
    });
    return Array.from(map.values());
  }, [rawRows]);
  const allPlatforms = useMemo(() => {
    return sortPlatformsByPreferredOrder(Array.from(new Set(rawRows.map((r) => r.platform).filter(Boolean))));
  }, [rawRows]);
  const ratingsByDateLocation = useMemo(() => {
    const map = {};
    rawRows.forEach((row) => {
      const d = row.record_date;
      if (!d) return;
      if (!map[d]) map[d] = {};
      if (!map[d][row.location_id]) map[d][row.location_id] = {};
      const hasRating = row.avg_rating !== null && row.avg_rating !== "";
      map[d][row.location_id][row.platform] = hasRating
        ? { rating: Number(row.avg_rating), reviews: Number(row.total_reviews) || 0 }
        : null;
    });
    return map;
  }, [rawRows]);
  // NEW-REVIEWS VIEW. For every date, compares each property/platform with
  // its own most recent EARLIER snapshot (which can be before the selected
  // From date — all loaded history is used), so the first column of a range
  // still has something to compare against. Cell value:
  //   { rating: star score of the new reviews, reviews, estimated }
  //   { noNew: true }                      same review count
  //   { removed: N }                       review count went DOWN by N
  //   { noPrev: true }                     no earlier snapshot to compare with
  // Every entry also carries `prevDate` (the snapshot it was compared to)
  // and `gap` = true when that's older than the previous available date,
  // i.e. this property/platform had no data in between, so "+N new"
  // covers more than one day.
  // Ratings are only published to 1 decimal, so each new-review score is
  // shown as a star rating like "★ 8.8"; when there are only a few
  // new reviews the hover text says so — see computeNewReviewStats.
  const newReviewsByDateLocation = useMemo(() => {
    const map = {};
    const last = {}; // `${loc}|${plat}` -> { rating, reviews, date }
    availableDates.forEach((d, dIdx) => {
      const day = ratingsByDateLocation[d] || {};
      const prevAvailableDate = dIdx > 0 ? availableDates[dIdx - 1] : null;
      map[d] = {};
      for (const [loc, byPlat] of Object.entries(day)) {
        map[d][loc] = {};
        for (const [plat, cur] of Object.entries(byPlat)) {
          if (!cur) { map[d][loc][plat] = null; continue; }
          const key = `${loc}|${plat}`;
          const prev = last[key];
          if (!prev) {
            map[d][loc][plat] = { noPrev: true };
          } else {
            // gap = this platform had no snapshot on the previous available
            // date, so this comparison covers more than one day.
            const sinceDate = prev.date !== prevAvailableDate ? prev.date : null;
            map[d][loc][plat] = computeNewReviewStats(prev, cur, sinceDate, plat);
          }
          last[key] = { ...cur, date: d };
        }
      }
    });
    return map;
  }, [availableDates, ratingsByDateLocation]);
  // Every snapshot per `${location_id}|${platform}`, oldest first — used by
  // the TOTAL column to find the last snapshot in the range and the one
  // just before the range starts.
  const snapshotsByLocPlat = useMemo(() => {
    const m = {};
    availableDates.forEach((d) => {
      const day = ratingsByDateLocation[d] || {};
      for (const [loc, byPlat] of Object.entries(day)) {
        for (const [plat, cur] of Object.entries(byPlat)) {
          if (!cur) continue;
          const key = `${loc}|${plat}`;
          (m[key] || (m[key] = [])).push({ ...cur, date: d });
        }
      }
    });
    return m;
  }, [availableDates, ratingsByDateLocation]);
  // Range TOTAL for one property/platform over the selected dateColumns.
  //   avgRating — plain average of the all-time rating across the range
  //   netNew    — reviews added over the range (= sum of the daily "+N new")
  //   newStats  — average score of ONLY those new reviews over the range,
  //               same formula as the daily cells (computeNewReviewStats)
  function computeRanklyRangeTotal(locationId, plat) {
    if (dateColumns.length === 0) return null;
    const first = dateColumns[0];
    const lastCol = dateColumns[dateColumns.length - 1];
    const snaps = snapshotsByLocPlat[`${locationId}|${plat}`] || [];
    const inRange = snaps.filter((x) => x.date >= first && x.date <= lastCol);
    if (inRange.length === 0) return null;
    const latest = inRange[inRange.length - 1];
    const before = snaps.filter((x) => x.date < first);
    // Prefer the snapshot just before the range (so the first day's new
    // reviews are counted, same as the daily cells); fall back to the
    // first snapshot inside the range when there's no earlier history.
    const base = before.length > 0 ? before[before.length - 1] : inRange[0];
    // ALL TIME view: plain average of the daily ratings shown in the row.
    const avgRating = inRange.reduce((acc, x) => acc + x.rating, 0) / inRange.length;
    // Walk day by day from the base snapshot — the SAME day-to-day steps
    // the daily cells use — so that:
    //   • reviews ADDED and REMOVED are each summed on their own
    //     (+5 one day and −3 another shows "+5" AND "−3 removed");
    //   • the NEW REVIEWS ★ total is the AVERAGE of the daily ★ scores in
    //     the row, weighted by how many new reviews each day had
    //     (★9.0 from 1 review + ★7.0 from 3 reviews = (9×1 + 7×3) ÷ 4 = 7.5).
    //     So the Total always sits within the daily scores it summarises.
    let added = 0;
    let removed = 0;
    let scoreSum = 0, loSum = 0, hiSum = 0, scale = null, anyEstimated = false;
    const chain = base === inRange[0] ? inRange : [base, ...inRange];
    for (let i = 1; i < chain.length; i++) {
      const diff = chain[i].reviews - chain[i - 1].reviews;
      if (diff > 0) {
        added += diff;
        const day = computeNewReviewStats(chain[i - 1], chain[i], null, plat);
        scoreSum += day.rating * diff;
        loSum += day.lo * diff;
        hiSum += day.hi * diff;
        scale = day.scale;
        if (day.estimated) anyEstimated = true;
      } else if (diff < 0) {
        removed += -diff;
      }
    }
    let newStats;
    if (base === latest) newStats = { noPrev: true };
    else if (added > 0) {
      newStats = {
        reviews: added,
        total: latest.reviews,
        rating: scoreSum / added,
        lo: loSum / added,
        hi: hiSum / added,
        scale,
        estimated: anyEstimated,
      };
    } else if (removed > 0) newStats = { removed };
    else newStats = { noNew: true };
    return {
      avgRating,
      added,
      removed,
      netNew: base === latest ? null : latest.reviews - base.reviews,
      newStats,
      baseDate: base === latest ? null : base.date,
      latestDate: latest.date,
      days: dateColumns.length,
    };
  }
  function handleRowClick(propertyName) {
    setSelectedRow((prev) => (prev === propertyName ? null : propertyName));
  }
  function handleHeaderClick(key) {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }
  const filters = {
    properties: selectedProperties,
    locations: selectedLocations,
    agents: selectedAgents,
  };
  const propertyOptions = useMemo(
    () => getAvailableOptions("name", filters, propertyMeta),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  const locationOptions = useMemo(
    () => getAvailableOptions("location", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedAgents]
  );
  const agentOptions = useMemo(
    () => getAvailableOptions("agent", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedLocations]
  );
  const visiblePlatforms =
    platformFilter.length === 0 ? allPlatforms : allPlatforms.filter((p) => platformFilter.includes(p));
  const visibleMeta = useMemo(() => {
    return propertyMeta.filter(
      (m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)
    );
  }, [propertyMeta, selectedProperties, selectedLocations, selectedAgents, searchQuery]);
  const matrix = useMemo(() => {
    return visibleMeta.map((meta) => {
      const source = ranklyViewMode === "new" ? newReviewsByDateLocation : ratingsByDateLocation;
      const perDate = dateColumns.map((d) =>
        visiblePlatforms.map((plat) => source[d]?.[meta.locationId]?.[plat] ?? null)
      );
      const totals = visiblePlatforms.map((plat) => computeRanklyRangeTotal(meta.locationId, plat));
      return { locationId: meta.locationId, property: meta.name, location: meta.location, agent: meta.agent, perDate, totals };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleMeta, dateColumns, visiblePlatforms, ratingsByDateLocation, newReviewsByDateLocation, ranklyViewMode, snapshotsByLocPlat]);
  const sortedMatrix = useMemo(() => {
    const arr = [...matrix];
    const ratingMatch = sortKey.match(/^rating:(\d+):(\d+)$/);
    const totalMatch = sortKey.match(/^ranklytotal:(\d+)$/);
    // Value the TOTAL column is sorted by — the rating it shows.
    const totalSortValue = (t) => {
      if (!t) return null;
      if (ranklyViewMode !== "new") return t.avgRating;
      if (typeof t.newStats?.rating === "number") return t.newStats.rating;
      return null;
    };
    arr.sort((a, b) => {
      let cmp = 0;
      if (totalMatch) {
        const pIdx = Number(totalMatch[1]);
        const ra = totalSortValue(a.totals?.[pIdx]);
        const rb = totalSortValue(b.totals?.[pIdx]);
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) return 1; // blanks always last
        else if (rb === null) return -1;
        else cmp = ra - rb;
        return sortDirection === "desc" ? -cmp : cmp;
      }
      if (sortKey === "property") {
        cmp = a.property.localeCompare(b.property);
      } else if (sortKey === "location") {
        cmp = a.location.localeCompare(b.location);
      } else if (sortKey === "agent") {
        cmp = a.agent.localeCompare(b.agent);
      } else if (ratingMatch) {
        const dIdx = Number(ratingMatch[1]);
        const pIdx = Number(ratingMatch[2]);
        const ra = a.perDate[dIdx]?.[pIdx]?.rating ?? null;
        const rb = b.perDate[dIdx]?.[pIdx]?.rating ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = ra - rb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [matrix, sortKey, sortDirection, ranklyViewMode]);
  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedAgents([]);
    setPlatformFilter([]);
    setSearchQuery("");
    setRanklyViewMode("all");
    resetDateRange();
  }
  if (loading) {
    return <div className="rd-blank-tab">Loading Rankly data...</div>;
  }
  if (loadError) {
    return <div className="rd-blank-tab">Couldn't load Rankly data: {loadError}</div>;
  }
  return (
    <>
      {syncPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${syncPopup.type}`}>
          <span>{syncPopup.message}</span>
          <button onClick={() => setSyncPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter
              label="Property Name"
              options={propertyOptions}
              selected={selectedProperties}
              onChange={setSelectedProperties}
              onClear={() => setSelectedProperties([])}
            />
            <MultiSelectFilter
              label="Location"
              options={locationOptions}
              selected={selectedLocations}
              onChange={setSelectedLocations}
              onClear={() => setSelectedLocations([])}
            />
            <MultiSelectFilter
              label="Agent"
              options={agentOptions}
              selected={selectedAgents}
              onChange={setSelectedAgents}
              onClear={() => setSelectedAgents([])}
            />
            <MultiSelectFilter
              label="Platform"
              options={allPlatforms}
              selected={platformFilter}
              onChange={setPlatformFilter}
              onClear={() => setPlatformFilter([])}
            />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <div className="rd-filter-group">
              <FilterLabel label="View" onClear={() => setRanklyViewMode("all")} />
              <select
                value={ranklyViewMode}
                onChange={(e) => setRanklyViewMode(e.target.value)}
                className="rd-dropdown-btn"
                title="All time = rating as reported. New reviews only = average of just the reviews added since the previous snapshot."
              >
                <option value="all">All time rating</option>
                <option value="new">New reviews only</option>
              </select>
            </div>
            <AvailableDateRangeFilter
              availableDates={availableDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={resetDateRange}
              onToday={goToToday}
            />
            <div className="rd-actions">
              {canUseSync() && (
                <button
                  onClick={handleSyncClick}
                  disabled={syncing}
                  className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`}
                  title="Fetch the latest ratings now"
                >
                  <RefreshCw size={14} className={syncing ? "rd-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync"}
                </button>
              )}
              <LastSyncedLabel lastSyncedAt={lastSyncedAt} />
              <button onClick={clearAllFilters} className="rd-btn-clear">
                <X size={14} />
                Clear all filters
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table">
              <colgroup>
                <col style={{ width: PROPERTY_COL_WIDTH }} />
                {dateColumns.map((d) =>
                  visiblePlatforms.map((plat) => (
                    <col key={`${d}-${plat}-col`} style={{ width: RATING_COL_WIDTH }} />
                  ))
                )}
                {visiblePlatforms.map((plat) => (
                  <col key={`total-${plat}-col`} style={{ width: RATING_COL_WIDTH + 20 }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    onClick={() => handleHeaderClick("property")}
                    style={{ left: 0, width: PROPERTY_COL_WIDTH }}
                    className="rd-th-fixed"
                  >
                    <span className="rd-count-badge">{matrix.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={visiblePlatforms.length} className="rd-th-date-group">
                      {formatDateLabel(d)}
                    </th>
                  ))}
                  {dateColumns.length > 0 && visiblePlatforms.map((plat, pIdx) => {
                    const key = `ranklytotal:${pIdx}`;
                    return (
                      <th
                        key={`total-${plat}`}
                        rowSpan={2}
                        onClick={() => handleHeaderClick(key)}
                        className={`rd-th-date-group rd-total-grid-dark rd-th-rankly-total${pIdx === 0 ? " rd-total-edge" : ""}`}
                        title={
                          ranklyViewMode === "new"
                            ? `Total for ${dateColumns.length} day(s). Rating = average score of only the new reviews in the range; count = total new reviews.`
                            : `Total for ${dateColumns.length} day(s). Rating = average of the daily ratings; count = reviews added in the range.`
                        }
                      >
                        Total
                        {visiblePlatforms.length > 1 && (
                          <span className="rd-th-rankly-total-sub">{plat}</span>
                        )}
                        {sortKey === key && (
                          <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                        )}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {dateColumns.map((d, dIdx) =>
                    visiblePlatforms.map((plat, pIdx) => {
                      const key = `rating:${dIdx}:${pIdx}`;
                      return (
                        <th key={`${d}-${plat}`} onClick={() => handleHeaderClick(key)} className="rd-th-platform">
                          {plat}
                          {sortKey === key && (
                            <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                          )}
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedMatrix.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  const isSelected = selectedRow === row.property;
                  const isDimmed = selectedRow !== null && !isSelected;
                  return (
                    <tr
                      // FIXED: keyed by Rankly location_id, not the property name.
                      // Two Rankly locations can share a name (e.g. WILDLINE GOLD
                      // RESORT appears twice); duplicate keys made React reuse the
                      // wrong row's cells — stale "No new" in All time view and
                      // garbled extra cells to the right of the table.
                      key={row.locationId ?? row.property}
                      onClick={() => handleRowClick(row.property)}
                      className={`rd-matrix-row ${rowBgClass}${isDimmed ? " rd-dimmed" : ""}`}
                    >
                      <td style={{ left: 0, width: PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                        {row.property}
                      </td>
                      {row.perDate.map((dateCells, dIdx) =>
                        dateCells.map((value, cIdx) => {
                          const hasRating = value && typeof value.rating === "number";
                          const plat = visiblePlatforms[cIdx];
                          const cellKey = `${dIdx}-${cIdx}`;
                          // ALL TIME view — exactly as before.
                          if (ranklyViewMode !== "new") {
                            return (
                              <td
                                key={cellKey}
                                className="rd-td-rating"
                                style={{ backgroundColor: getRatingColor(plat, hasRating ? value.rating : null) }}
                              >
                                {hasRating ? (
                                  <>
                                    <span className="rd-rating-value">{value.rating.toFixed(1)}</span>
                                    <span className="rd-rating-reviews">{value.reviews} reviews</span>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          }
                          // NEW REVIEWS ONLY view.
                          const AMBER_BG = "#fef3c7";
                          const AMBER_TEXT = "#92400e";
                          const since = value && value.sinceDate ? formatDateLabel(value.sinceDate) : null;
                          let title;
                          let bg = getRatingColor(plat, hasRating ? value.rating : null);
                          let content = "—";
                          if (!value) {
                            title = "No data for this day — the next day with data includes these reviews";
                          } else if (value.noPrev) {
                            title = "No earlier snapshot to compare with";
                          } else if (value.removed) {
                            bg = AMBER_BG;
                            title = `${value.removed} review(s) removed since ${since || "the previous snapshot"}`;
                            content = (
                              <span className="rd-rating-reviews" style={{ color: AMBER_TEXT, fontWeight: 600 }}>
                                −{value.removed} removed
                              </span>
                            );
                          } else if (value.noNew) {
                            // Only "No new" here — no rating up/down shown.
                            title = "No new reviews since the previous snapshot";
                            content = <span className="rd-rating-reviews">No new</span>;
                          } else if (typeof value.rating === "number") {
                            const sinceText = since ? ` Includes all new reviews since ${since} (no data in between).` : "";
                            title = newReviewScoreTitle(value, sinceText);
                            // Every calculated score shows as a star rating,
                            // coloured like any rating (per request — easier to
                            // read). Whether it's exact or calculated from only a
                            // few reviews is explained in the hover text.
                            bg = getRatingColor(plat, value.rating);
                            content = (
                              <>
                                <span className="rd-rating-value">★ {value.rating.toFixed(1)}</span>
                                <span className="rd-rating-reviews">+{value.reviews} new{since ? ` since ${since}` : ""}</span>
                              </>
                            );
                          }
                          return (
                            <td key={cellKey} className="rd-td-rating" title={title} style={{ backgroundColor: bg }}>
                              {content}
                            </td>
                          );
                        })
                      )}
                      {dateColumns.length > 0 && visiblePlatforms.map((plat, pIdx) => (
                        <RanklyTotalCell
                          key={`total-${plat}`}
                          total={row.totals?.[pIdx]}
                          platform={plat}
                          viewMode={ranklyViewMode}
                          isFirst={pIdx === 0}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
const OCC_PROPERTY_COL_WIDTH = 210;
const OCC_SIDE_COL_WIDTH = 120;
const OCC_METRIC_COL_WIDTH = 78;
const OCC_LOCATION_LEFT = OCC_PROPERTY_COL_WIDTH;
const OCC_AGENT_LEFT = OCC_PROPERTY_COL_WIDTH + OCC_SIDE_COL_WIDTH;
const OCC_PMS_LEFT = OCC_PROPERTY_COL_WIDTH + OCC_SIDE_COL_WIDTH * 2;
function occRatingClass(pct) {
  if (pct === null || pct === undefined) return "occ-cell-empty";
  if (pct >= 70) return "occ-cell-high";
  if (pct >= 35) return "occ-cell-mid";
  return "occ-cell-low";
}
function OccupancySummaryBadges({ rows }) {
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        const first = r.perDate[0];
        acc.t += first?.t || 0;
        acc.a += first?.a || 0;
        acc.s += first?.u || 0;
        return acc;
      },
      { t: 0, a: 0, s: 0 }
    );
  }, [rows]);
  return (
    <div className="occ-summary-badges">
      <div className="occ-summary-badge">
        <span className="occ-summary-value">{totals.t}</span>
        <span className="occ-summary-label">C T ROOMS</span>
      </div>
      <div className="occ-summary-badge">
        <span className="occ-summary-value">{totals.a}</span>
        <span className="occ-summary-label">C A ROOMS</span>
      </div>
      <div className="occ-summary-badge">
        <span className="occ-summary-value">{totals.s}</span>
        <span className="occ-summary-label">S ROOMS</span>
      </div>
    </div>
  );
}
function DateRangeControl({ availableDates, fromDate, toDate, onFromChange, onToChange, onClear, onToday }) {
  const toDateOptions = availableDates.filter((d) => d >= fromDate);
  return (
    <div className="occ-date-group">
      <div className="rd-filter-label-row">
        <label className="rd-filter-label">
          <CalendarDays size={12} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          DATE RANGE
        </label>
        <button onClick={onClear} title="Clear date range" className="rd-clear-btn">
          <Eraser size={12} />
        </button>
      </div>
      <div className="occ-date-inputs">
        <select value={fromDate} onChange={(e) => onFromChange(e.target.value)} className="rd-dropdown-btn">
          {availableDates.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <span className="rd-date-sep">to</span>
        <select value={toDate} onChange={(e) => onToChange(e.target.value)} className="rd-dropdown-btn">
          {toDateOptions.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today">
          <RotateCcw size={12} />
          TODAY
        </button>
      </div>
    </div>
  );
}
const OCC_MAX_DATE_COLUMNS = 31;
function OccupancyTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    fetch(apiUrl("/api/occupancy/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setRawRows(json.data); recordSync(json.data);
        } else {
          setLoadError(json.error || "Failed to load data");
        }
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  const realToday = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(realToday);
  const [toDate, setToDate] = useState(realToday);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [pmsFilter, setPmsFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPopup, setSyncPopup] = useState(null);
  const [lastSyncedAt, recordSync] = useLastSyncedTracking("revx_lastsync_occupancy");
  const syncPollRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const lastSyncSnapshotRef = useRef(null);
  const availableDates = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.report_date).filter(Boolean))).sort(),
    [rawRows]
  );
  const didInitDate = useRef(false);
  useEffect(() => {
    if (!didInitDate.current && availableDates.length > 0) {
      didInitDate.current = true;
      if (!availableDates.includes(realToday)) {
        goToToday();
      }
    }
  }, [availableDates]);
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) {
      setToDate(newFrom);
    }
  }
  function goToToday() {
    if (availableDates.length === 0) {
      setFromDate(realToday);
      setToDate(realToday);
      return;
    }
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0
      ? pastOrToday[pastOrToday.length - 1]
      : availableDates[availableDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  function clearDates() {
    setFromDate("");
    setToDate("");
  }
  const OCC_SYNC_POLL_INTERVAL_MS = 30000;
  const OCC_SYNC_WAIT_MS = 10.5 * 60 * 1000;
  function stopSyncPolling() {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
  }
  async function refreshOccupancyOnce() {
    const res = await fetch(apiUrl("/api/occupancy/summary/refresh"));
    const json = await res.json();
    if (!json.success) throw new Error(json.error || "Refresh failed");
    return json.data;
  }
  async function handleSyncClick() {
    if (syncing) return;
    setSyncing(true);
    setSyncPopup(null);
    try {
      const res = await fetch("/api/occupancy/sync", { method: "POST" });
      const json = await res.json();
      if (!json.success) {
        setSyncing(false);
        setSyncPopup({ type: "error", message: json.error || "Couldn't start sync." });
        return;
      }
    } catch (err) {
      setSyncing(false);
      setSyncPopup({ type: "error", message: err.message });
      return;
    }
    syncPollRef.current = setInterval(async () => {
      try {
        const data = await refreshOccupancyOnce();
        setRawRows(data); recordSync(data);
      } catch {
      }
    }, OCC_SYNC_POLL_INTERVAL_MS);
    syncTimeoutRef.current = setTimeout(async () => {
      stopSyncPolling();
      try {
        const data = await refreshOccupancyOnce();
        setRawRows(data); recordSync(data);
        setSyncPopup({ type: "success", message: "Data has been refreshed." });
      } catch (err) {
        setSyncPopup({ type: "error", message: err.message });
      } finally {
        setSyncing(false);
      }
    }, OCC_SYNC_WAIT_MS);
  }
  useEffect(() => {
    return () => stopSyncPolling();
  }, []);
  useEffect(() => {
    if (!syncPopup) return;
    const t = setTimeout(() => setSyncPopup(null), 8000);
    return () => clearTimeout(t);
  }, [syncPopup]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate).slice(0, OCC_MAX_DATE_COLUMNS);
  }, [availableDates, fromDate, toDate]);
  const propertySnapshotsByDate = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      const key = `${r.property_code}|${r.pms_source}`;
      if (!map.has(key)) map.set(key, new Map());
      const byDate = map.get(key);
      if (!byDate.has(r.report_date)) {
        byDate.set(r.report_date, {
          t: Number(r.overall_total_rooms) || 0,
          a: Number(r.overall_available_rooms) || 0,
          u: Number(r.overall_used_rooms) || 0,
          oOcc: Number(r.overall_occupancy_pct) || 0,
          rateSum: Number(r.rate) || 0,
          rateCount: r.rate != null ? 1 : 0,
          cOccSum: Number(r.occupancy_pct) || 0,
          cOccCount: r.occupancy_pct != null ? 1 : 0,
        });
      } else {
        const existing = byDate.get(r.report_date);
        if (r.rate != null) {
          existing.rateSum += Number(r.rate);
          existing.rateCount += 1;
        }
        if (r.occupancy_pct != null) {
          existing.cOccSum += Number(r.occupancy_pct);
          existing.cOccCount += 1;
        }
      }
    });
    return map;
  }, [rawRows]);
  const categorySnapshotsByProperty = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      const key = `${r.property_code}|${r.pms_source}`;
      if (!map.has(key)) map.set(key, new Map());
      const byCategory = map.get(key);
      const cat = r.room_category_name || "Unspecified";
      if (!byCategory.has(cat)) byCategory.set(cat, new Map());
      byCategory.get(cat).set(r.report_date, {
        t: Number(r.total_rooms) || 0,
        a: Number(r.available_rooms) || 0,
        u: Number(r.sold_rooms) || 0,
        occ: Number(r.occupancy_pct) || 0,
        adr: r.rate != null ? Number(r.rate) : null,
      });
    });
    return map;
  }, [rawRows]);
  function pickSnap(snap) {
    return {
      t: snap ? snap.t : 0,
      a: snap ? snap.a : 0,
      u: snap ? snap.u : 0,
      oOcc: snap ? snap.oOcc : 0,
      cOcc: snap && snap.cOccCount > 0 ? snap.cOccSum / snap.cOccCount : 0,
      adr: snap && snap.rateCount > 0 ? Math.round(snap.rateSum / snap.rateCount) : 0,
    };
  }
  function getCategoryRows(propKey) {
    const byCategory = categorySnapshotsByProperty.get(propKey);
    if (!byCategory) return [];
    return Array.from(byCategory.entries())
      .map(([category, byDate]) => ({
        category,
        perDate: dateColumns.map((d) => {
          const snap = byDate.get(d);
          return {
            t: snap ? snap.t : 0,
            a: snap ? snap.a : 0,
            u: snap ? snap.u : 0,
            occ: snap ? snap.occ : 0,
            adr: snap ? snap.adr : null,
          };
        }),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }
  const propertyMeta = useMemo(() => {
    const seen = new Map();
    rawRows.forEach((r) => {
      const key = `${r.property_code}|${r.pms_source}`;
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          propertyCode: r.property_code,
          masterId: r.master_id,
          name: r.property_name,
          location: r.location,
          agent: r.agent,
          pms: r.pms_source,
        });
      }
    });
    return Array.from(seen.values());
  }, [rawRows]);
  const filters = {
    properties: selectedProperties,
    locations: selectedLocations,
    agents: selectedAgents,
  };
  const propertyOptions = useMemo(
    () => getAvailableOptions("name", filters, propertyMeta),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  const locationOptions = useMemo(
    () => getAvailableOptions("location", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedAgents]
  );
  const agentOptions = useMemo(
    () => getAvailableOptions("agent", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedLocations]
  );
  const pmsOptions = useMemo(() => Array.from(new Set(propertyMeta.map((m) => m.pms))).sort(), [propertyMeta]);
  const displayRows = useMemo(() => {
    return propertyMeta
      .filter((meta) => {
        if (!propertyMatchesFilters(meta, filters, null)) return false;
        if (pmsFilter.length > 0 && !pmsFilter.includes(meta.pms)) return false;
        if (!matchesSearch(meta, searchQuery)) return false;
        return true;
      })
      .map((meta) => {
        const byDate = propertySnapshotsByDate.get(meta.key) || new Map();
        const perDate = dateColumns.map((d) => pickSnap(byDate.get(d)));
        return {
          masterId: meta.masterId,
          propertyCode: meta.propertyCode,
          property: meta.name,
          location: meta.location,
          agent: meta.agent,
          pms: meta.pms,
          perDate,
        };
      });
  }, [propertyMeta, propertySnapshotsByDate, dateColumns, selectedProperties, selectedLocations, selectedAgents, pmsFilter, searchQuery]);
  const sortedRows = useMemo(() => {
    const arr = [...displayRows];
    const metricMatch = sortKey.match(/^occmetric:(\d+):(t|a|u|oOcc|adr)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (sortKey === "pms") cmp = a.pms.localeCompare(b.pms);
      else if (metricMatch) {
        const dIdx = Number(metricMatch[1]);
        const field = metricMatch[2];
        const va = a.perDate[dIdx]?.[field] ?? 0;
        const vb = b.perDate[dIdx]?.[field] ?? 0;
        cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [displayRows, sortKey, sortDirection]);
  function handleHeaderClick(key) {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }
  function handleRowClick(key) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  const allExpanded = sortedRows.length > 0 && sortedRows.every((r) => expandedKeys.has(`${r.propertyCode}|${r.pms}`));
  function toggleExpandAll() {
    if (allExpanded) {
      setExpandedKeys(new Set());
    } else {
      setExpandedKeys(new Set(sortedRows.map((r) => `${r.propertyCode}|${r.pms}`)));
    }
  }
  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedAgents([]);
    setPmsFilter([]);
    setSearchQuery("");
    goToToday();
  }
  if (loading) {
    return <div className="rd-blank-tab">Loading occupancy data...</div>;
  }
  if (loadError) {
    return <div className="rd-blank-tab">Couldn't load occupancy data: {loadError}</div>;
  }
  return (
    <>
      {syncPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${syncPopup.type}`}>
          <span>{syncPopup.message}</span>
          <button onClick={() => setSyncPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <span className="occ-mobile-filter-mini-badges">
            <span>{sortedRows.reduce((s, r) => s + (r.perDate[0]?.t || 0), 0)} total</span>
            <span>{sortedRows.reduce((s, r) => s + (r.perDate[0]?.u || 0), 0)} sold</span>
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter
              label="Property Name"
              options={propertyOptions}
              selected={selectedProperties}
              onChange={setSelectedProperties}
              onClear={() => setSelectedProperties([])}
            />
            <MultiSelectFilter
              label="Location"
              options={locationOptions}
              selected={selectedLocations}
              onChange={setSelectedLocations}
              onClear={() => setSelectedLocations([])}
            />
            <MultiSelectFilter
              label="Agent"
              options={agentOptions}
              selected={selectedAgents}
              onChange={setSelectedAgents}
              onClear={() => setSelectedAgents([])}
            />
            <MultiSelectFilter
              label="PMS"
              options={pmsOptions}
              selected={pmsFilter}
              onChange={setPmsFilter}
              onClear={() => setPmsFilter([])}
            />
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={() => setSearchQuery("")}
            />
            <div className="occ-date-and-clear">
              <DateRangeControl
                availableDates={availableDates}
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={handleFromDateChange}
                onToChange={setToDate}
                onClear={clearDates}
                onToday={goToToday}
              />
              <div className="rd-actions occ-actions-row">
                {canUseSync() && (
                  <button
                    onClick={handleSyncClick}
                    disabled={syncing}
                    className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`}
                    title="Fetch the latest occupancy data now (can take up to 10 minutes)"
                  >
                    <RefreshCw size={14} className={syncing ? "rd-spin" : ""} />
                    {syncing ? "Syncing..." : "Sync"}
                  </button>
                )}
                <LastSyncedLabel lastSyncedAt={lastSyncedAt} />
                <button onClick={clearAllFilters} className="rd-btn-clear">
                  <X size={14} />
                  Clear all filters
                </button>
              </div>
            </div>
            <OccupancySummaryBadges rows={sortedRows} />
          </div>
        </div>
      </div>
     <div className="rd-matrix-section">
        <div className="occ-table-toolbar">
          <button
            onClick={toggleExpandAll}
            className="occ-expand-all-btn"
            title={allExpanded ? "Collapse all properties" : "Expand all properties"}
          >
            <ChevronsUpDown size={13} />
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: OCC_PROPERTY_COL_WIDTH }} />
                {dateColumns.map((d) =>
                  [...Array(5)].map((_, i) => (
                    <col key={`${d}-${i}-col`} style={{ width: OCC_METRIC_COL_WIDTH }} />
                  ))
                )}
              </colgroup>
              <thead>
                <tr className="occ-group-row">
                  <th
                    rowSpan={2}
                    onClick={() => handleHeaderClick("property")}
                    style={{ left: 0, width: OCC_PROPERTY_COL_WIDTH }}
                    className="rd-th-fixed"
                  >
                    <span className="rd-count-badge">{sortedRows.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={5} className="occ-th-group">
                      {formatDateLabel(d)}
                    </th>
                  ))}
                </tr>
                <tr>
                  {dateColumns.map((d, dIdx) =>
                    [
                      { label: "TOTAL INVENTORY", field: "t" },
                      { label: "AVAILABLE INVENTORY", field: "a" },
                      { label: "SOLD INVENTORY", field: "u" },
                      { label: "OCCUPANCY %", field: "oOcc" },
                      { label: "ADR", field: "adr" },
                    ].map(({ label, field }) => {
                      const key = `occmetric:${dIdx}:${field}`;
                      return (
                        <th key={`${d}-${label}`} onClick={() => handleHeaderClick(key)} className="occ-th-metric">
                          {label}
                          {sortKey === key && (
                            <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                          )}
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  const rowKey = `${row.propertyCode}|${row.pms}`;
                  const isExpanded = expandedKeys.has(rowKey);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        onClick={() => handleRowClick(rowKey)}
                        className={`rd-matrix-row ${rowBgClass}`}
                      >
                        <td style={{ left: 0, width: OCC_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                          {row.property}
                          <ChevronDown size={12} className={`occ-expand-icon ${isExpanded ? "occ-expanded" : ""}`} />
                        </td>
                        {row.perDate.map((snap, dIdx) => (
                          <React.Fragment key={dIdx}>
                            <td className="occ-td-t">{snap.t}</td>
                            <td className="occ-td-a">{snap.a}</td>
                            <td className="occ-td-u" style={{ backgroundColor: getSoldInventoryColor(snap.u, snap.t) }}>{snap.u}</td>
                            <td className={`occ-td-pct ${occRatingClass(snap.oOcc)}`}>{snap.oOcc.toFixed(2)}%</td>
                            <td className="occ-td-adr">₹{snap.adr.toLocaleString("en-IN")}</td>
                          </React.Fragment>
                        ))}
                      </tr>
                      {isExpanded && getCategoryRows(rowKey).map((cat) => (
                        <tr key={`${rowKey}-${cat.category}`} className="occ-subrow">
                          <td style={{ left: 0, width: OCC_PROPERTY_COL_WIDTH }} className="occ-subrow-label rd-td-fixed">
                            {cat.category}
                          </td>
                          {cat.perDate.map((snap, dIdx) => (
                            <React.Fragment key={dIdx}>
                              <td className="occ-td-t">{snap.t}</td>
                              <td className="occ-td-a">{snap.a}</td>
                              <td className="occ-td-u" style={{ backgroundColor: getSoldInventoryColor(snap.u, snap.t) }}>{snap.u}</td>
                              <td className={`occ-td-pct ${occRatingClass(snap.occ)}`}>{snap.occ.toFixed(2)}%</td>
                              <td className="occ-td-adr">{snap.adr != null ? `₹${snap.adr.toLocaleString("en-IN")}` : "—"}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
const BOOKING_SUBPAGES = [
  {
    id: "overview",
    title: "STAY DATA",
    description: "ADR, revenue, and room nights by hotel vs. market value.",
  },
  {
    id: "visit",
    title: "BOOK DATA",
    description: "Daily bookings, property page conversion %, and page views.",
  },
];
const BOOKING_PROPERTY_COL_WIDTH = 210;
const BOOKING_SIDE_COL_WIDTH = 120;
const BOOKING_METRIC_COL_WIDTH = 78;
const BOOKING_RANK_LEFT = BOOKING_PROPERTY_COL_WIDTH;
const BOOKING_RANKLY_LEFT = BOOKING_RANK_LEFT + BOOKING_SIDE_COL_WIDTH;
const BOOKING_MAX_DATE_COLUMNS = 15;
const BOOKING_OVERVIEW_METRICS = [
  { key: "room_nights", abbr: "RN", revxField: "room_nights_revx", marketField: "room_nights_market", format: "int", aggregation: "sum" },
  { key: "adr", abbr: "ADR", revxField: "adr_revx", marketField: "adr_market", format: "currency", aggregation: "avg" },
  { key: "revenue", abbr: "REV", revxField: "revenue_revx", marketField: "revenue_market", format: "currency", aggregation: "sum" },
];
function formatBookingMetricValue(value, format) {
  const num = Number(value) || 0;
  if (format === "currency") return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${num.toFixed(2)}%`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
function useHotelRankLookup() {
  const [rankRows, setRankRows] = useState([]);
  useEffect(() => {
    fetch(apiUrl("/api/booking/rank-data"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRankRows(json.data);
      })
      .catch(() => {});
  }, []);
  return useMemo(() => {
    const map = {};
    rankRows.forEach((r) => {
      map[r.hotel_name] = {
        rank: r.rank_in_city,
        totalRank: r.total_rank_in_city,
      };
    });
    return map;
  }, [rankRows]);
}
function RankCell({ rank, rowBgClass }) {
  return (
    <td
      style={{ left: BOOKING_RANK_LEFT, width: BOOKING_SIDE_COL_WIDTH }}
      className={`rd-td-fixed rd-side rd-rank-cell ${rowBgClass || ""}`}
    >
      {rank ? (
        <>
          <span className="rd-rating-value">{Number(rank.rank).toFixed(0)}</span>
          <span className="rd-rating-reviews">Total: {Number(rank.totalRank).toFixed(0)}</span>
        </>
      ) : (
        <span className="rd-rating-empty">—</span>
      )}
    </td>
  );
}
function BookingOverviewTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [metricDisplayModes, setMetricDisplayModes] = useState(() => {
    const initial = {};
    BOOKING_OVERVIEW_METRICS.forEach((m) => {
      initial[m.key] = m.aggregation === "avg" ? "average" : "sum";
    });
    return initial;
  });
  const [selectedPropertyName, setSelectedPropertyName] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showMkt, setShowMkt] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshPopup, setRefreshPopup] = useState(null);
  const rankByHotel = useHotelRankLookup();
  const ranklyRatings = useRanklyRatingsLookup();
  useEffect(() => {
    fetch(apiUrl("/api/booking/overview"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRawRows(json.data);
        else setLoadError(json.error || "Failed to load data");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshPopup(null);
    try {
      const res = await fetch(apiUrl("/api/booking/overview?forceRefresh=true"));
      const json = await res.json();
      if (json.success) {
        setRawRows(json.data);
        setRefreshPopup({ type: "success", message: "Showing the latest data from Athena." });
      } else {
        setRefreshPopup({ type: "error", message: json.error || "Couldn't refresh data." });
      }
    } catch (err) {
      setRefreshPopup({ type: "error", message: err.message });
    } finally {
      setRefreshing(false);
    }
  }
  useEffect(() => {
    if (!refreshPopup) return;
    const t = setTimeout(() => setRefreshPopup(null), 6000);
    return () => clearTimeout(t);
  }, [refreshPopup]);
  const availableDates = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.record_date).filter(Boolean))).sort(),
    [rawRows]
  );
  useEffect(() => {
    if (availableDates.length > 0 && !fromDate && !toDate) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - 7);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }, [availableDates]);
  function resetDateRange() {
    if (availableDates.length > 0) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - 7);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (availableDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : availableDates[availableDates.length - 1];
    setFromDate(exact || fallback);
    setToDate(exact || fallback);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent });
    });
    return Array.from(map.values());
  }, [rawRows]);
  const filters = { properties: selectedHotels, locations: selectedLocations, agents: selectedAgents };
  const hotelOptions = useMemo(() => getAvailableOptions("name", filters, propertyMeta), [propertyMeta, selectedLocations, selectedAgents]);
  const locationOptions = useMemo(() => getAvailableOptions("location", filters, propertyMeta), [propertyMeta, selectedHotels, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", filters, propertyMeta), [propertyMeta, selectedHotels, selectedLocations]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate);
  }, [availableDates, fromDate, toDate]);
  const valuesByDateHotel = useMemo(() => {
    const map = {};
    rawRows.forEach((r) => {
      const d = r.record_date;
      if (!map[d]) map[d] = {};
      map[d][r.hotel_name] = r;
    });
    return map;
  }, [rawRows]);
  const visibleMeta = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)),
    [propertyMeta, selectedHotels, selectedLocations, selectedAgents, searchQuery]
  );
  const rows = useMemo(() => {
    return visibleMeta.map((meta) => {
      const perDate = dateColumns.map((d) => {
        const rec = valuesByDateHotel[d]?.[meta.name];
        return BOOKING_OVERVIEW_METRICS.map((m) => ({
          revx: rec ? Number(rec[m.revxField]) || 0 : 0,
          market: rec ? Number(rec[m.marketField]) || 0 : 0,
        }));
      });
      const metricTotals = BOOKING_OVERVIEW_METRICS.map((m, mIdx) => {
        const sum = perDate.reduce((s, dateGroup) => s + (dateGroup[mIdx]?.revx || 0), 0);
        return { sum };
      });
      return { property: meta.name, location: meta.location, agent: meta.agent, perDate, metricTotals };
    });
  }, [visibleMeta, dateColumns, valuesByDateHotel]);
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const metricMatch = sortKey.match(/^bkmetric:(\d+):(\d+):(revx|market)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (sortKey === "rank") {
        const ra = rankByHotel[a.property]?.rank ?? null;
        const rb = rankByHotel[b.property]?.rank ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = Number(ra) - Number(rb);
      } else if (sortKey === "rankly") {
        const ra = ranklyRatings.getRating(a.property, "booking")?.rating ?? null;
        const rb = ranklyRatings.getRating(b.property, "booking")?.rating ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = ra - rb;
      } else if (metricMatch) {
        const dIdx = Number(metricMatch[1]);
        const mIdx = Number(metricMatch[2]);
        const field = metricMatch[3];
        const va = a.perDate[dIdx]?.[mIdx]?.[field] ?? 0;
        const vb = b.perDate[dIdx]?.[mIdx]?.[field] ?? 0;
        cmp = va - vb;
      } else if (sortKey.startsWith("bktotal:")) {
        const mIdx = Number(sortKey.split(":")[1]);
        const va = a.metricTotals[mIdx]?.sum ?? 0;
        const vb = b.metricTotals[mIdx]?.sum ?? 0;
        cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortKey, sortDirection, rankByHotel, ranklyRatings]);
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }
  function clearAllFilters() {
    setSelectedHotels([]); setSelectedLocations([]); setSelectedAgents([]);
    setSearchQuery(""); resetDateRange();
  }
  if (loading) return <div className="rd-blank-tab">Loading booking overview...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load booking overview: {loadError}</div>;
  if (selectedPropertyName) {
    return (
      <BookingOverviewDetailView
        allRows={rawRows}
        initialPropertyName={selectedPropertyName}
        onBack={() => setSelectedPropertyName(null)}
      />
    );
  }
  return (
    <>
      {refreshPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${refreshPopup.type}`}>
          <span>{refreshPopup.message}</span>
          <button onClick={() => setRefreshPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter label="Hotel Name" options={hotelOptions} selected={selectedHotels} onChange={setSelectedHotels} onClear={() => setSelectedHotels([])} />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={availableDates} fromDate={fromDate} toDate={toDate}
              onFromChange={handleFromDateChange} onToChange={setToDate} onClear={resetDateRange} onToday={goToToday}
            />
            <button
              type="button"
              onClick={() => setShowMkt((v) => !v)}
              className={`rd-btn-toggle${showMkt ? " rd-btn-toggle-active" : ""}`}
              title={showMkt ? "Hide market data" : "Show market data"}
            >
              MKT
            </button>
            <div className="rd-actions">
              {canUseSync() && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`rd-btn-sync${refreshing ? " rd-btn-sync-active" : ""}`}
                  title="Show the latest data currently in Athena"
                >
                  <RefreshCw size={14} className={refreshing ? "rd-spin" : ""} />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              )}
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: BOOKING_PROPERTY_COL_WIDTH }} />
                <col style={{ width: BOOKING_SIDE_COL_WIDTH }} />
                <col style={{ width: BOOKING_SIDE_COL_WIDTH }} />
                {dateColumns.map((d) =>
                  [...Array(BOOKING_OVERVIEW_METRICS.length * (showMkt ? 2 : 1))].map((_, i) => (
                    <col key={`${d}-${i}-col`} style={{ width: BOOKING_METRIC_COL_WIDTH }} />
                  ))
                )}
                {BOOKING_OVERVIEW_METRICS.map((m) => (
                  <col key={`total-${m.key}-col`} style={{ width: BOOKING_METRIC_COL_WIDTH + 20 }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan={3} onClick={() => handleHeaderClick("property")} style={{ left: 0, width: BOOKING_PROPERTY_COL_WIDTH }} className="rd-th-fixed">
                    <span className="rd-count-badge">{sortedRows.length}</span>HOTEL NAME
                    {sortKey === "property" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th rowSpan={3} onClick={() => handleHeaderClick("rank")} style={{ left: BOOKING_RANK_LEFT, width: BOOKING_SIDE_COL_WIDTH }} className="rd-th-fixed rd-side">
                    RANK
                    {sortKey === "rank" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th rowSpan={3} onClick={() => handleHeaderClick("rankly")} style={{ left: BOOKING_RANKLY_LEFT, width: BOOKING_SIDE_COL_WIDTH }} className="rd-th-fixed rd-side">
                    RATING
                    {sortKey === "rankly" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={BOOKING_OVERVIEW_METRICS.length * (showMkt ? 2 : 1)} className="occ-th-group">
                      {formatDateLabel(d)}
                    </th>
                  ))}
                  {BOOKING_OVERVIEW_METRICS.map((m, mIdx) => (
                    <th key={`total-filler1-${m.key}`} className={`occ-th-group rd-total-header-blank${mIdx === 0 ? " rd-total-edge" : ""}`} />
                  ))}
                </tr>
                <tr>
                  {dateColumns.map((d) =>
                    BOOKING_OVERVIEW_METRICS.map((m) => (
                      <th key={`${d}-${m.key}`} colSpan={showMkt ? 2 : 1} className="mmt-th-metric-group">{m.abbr}</th>
                    ))
                  )}
                  {BOOKING_OVERVIEW_METRICS.map((m, mIdx) => {
                    const sortKeyForMetric = `bktotal:${mIdx}`;
                    return (
                      <th
                        key={`total-${m.key}`}
                        onClick={() => handleHeaderClick(sortKeyForMetric)}
                        className={`mmt-th-metric-group rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}
                        style={{ cursor: "pointer" }}
                      >
                        {m.abbr} TOTAL
                        {sortKey === sortKeyForMetric && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {dateColumns.map((d, dIdx) =>
                    BOOKING_OVERVIEW_METRICS.map((m, mIdx) => {
                      const revxKey = `bkmetric:${dIdx}:${mIdx}:revx`;
                      const mktKey = `bkmetric:${dIdx}:${mIdx}:market`;
                      return (
                        <React.Fragment key={`${d}-${m.key}-sub`}>
                          <th onClick={() => handleHeaderClick(revxKey)} className="mmt-th-subcol">
                            REVX
                            {sortKey === revxKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          {showMkt && (
                            <th onClick={() => handleHeaderClick(mktKey)} className="mmt-th-subcol">
                              MKT
                              {sortKey === mktKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                            </th>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                  {BOOKING_OVERVIEW_METRICS.map((m, mIdx) => (
                    <th key={`total-filler3-${m.key}`} className={`mmt-th-subcol rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                      <select
                        value={metricDisplayModes[m.key]}
                        onChange={(e) => setMetricDisplayModes((prev) => ({ ...prev, [m.key]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        className="rd-total-select"
                      >
                        <option value="sum">Sum</option>
                        <option value="average">Average</option>
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  return (
                    <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`} onClick={() => setSelectedPropertyName(row.property)} title="Click to view this property's booking performance">
                      <td style={{ left: 0, width: BOOKING_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>{row.property}</td>
                      <RankCell rank={rankByHotel[row.property]} rowBgClass={rowBgClass} />
                      <RanklyRatingCell
                        entry={ranklyRatings.getRating(row.property, "booking")}
                        platform="booking"
                        style={{ left: BOOKING_RANKLY_LEFT, width: BOOKING_SIDE_COL_WIDTH }}
                        className="rd-td-fixed rd-side rd-rank-cell"
                      />
                      {row.perDate.map((dateGroup, dIdx) =>
                        dateGroup.map((cell, mIdx) => (
                          <React.Fragment key={`${dIdx}-${mIdx}`}>
                            <td className="occ-td-adr">{formatBookingMetricValue(cell.revx, BOOKING_OVERVIEW_METRICS[mIdx].format)}</td>
                            {showMkt && (
                              <td className="occ-td-adr">{formatBookingMetricValue(cell.market, BOOKING_OVERVIEW_METRICS[mIdx].format)}</td>
                            )}
                          </React.Fragment>
                        ))
                      )}
                      {BOOKING_OVERVIEW_METRICS.map((m, mIdx) => {
                        const mode = metricDisplayModes[m.key];
                        const divisor = dateColumns.length || 1;
                        const raw = row.metricTotals[mIdx].sum;
                        const displayed = mode === "sum" ? raw : raw / divisor;
                        return (
                          <td key={`total-${m.key}`} className={`rd-total-td ${rowBgClass}${mIdx === 0 ? " rd-total-edge" : ""}`}>
                            {formatBookingMetricValue(displayed, m.format)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
function BookingOverviewDetailView({ allRows, initialPropertyName, onBack }) {
  const [activeMetricKey, setActiveMetricKey] = useState("revenue");
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const allDates = useMemo(() => Array.from(new Set(allRows.map((r) => r.record_date).filter(Boolean))).sort(), [allRows]);
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => { if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent }); });
    return Array.from(map.values());
  }, [allRows]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const currentMeta = propertyMeta.find((m) => m.name === currentPropertyName);
  const switchFilters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(() => getAvailableOptions("location", switchFilters, propertyMeta), [propertyMeta, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", switchFilters, propertyMeta), [propertyMeta, selectedLocations]);
  const propertyOptions = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, switchFilters, null)).map((m) => m.name).sort(),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  useEffect(() => {
    if (propertyOptions.length > 0 && !propertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(propertyOptions[0]);
    }
  }, [propertyOptions]);
  const rangeRows = useMemo(
    () => allRows.filter((r) => r.hotel_name === currentPropertyName && r.record_date >= fromDate && r.record_date <= toDate),
    [allRows, currentPropertyName, fromDate, toDate]
  );
  const totals = useMemo(() => {
    const out = {};
    BOOKING_OVERVIEW_METRICS.forEach((m) => {
      const sums = rangeRows.reduce((acc, r) => ({
        revx: acc.revx + (Number(r[m.revxField]) || 0),
        market: acc.market + (Number(r[m.marketField]) || 0),
        count: acc.count + 1,
      }), { revx: 0, market: 0, count: 0 });
      if (m.aggregation === "avg" && sums.count > 0) {
        out[m.key] = { revx: sums.revx / sums.count, market: sums.market / sums.count };
      } else {
        out[m.key] = { revx: sums.revx, market: sums.market };
      }
    });
    return out;
  }, [rangeRows]);
  const activeMetric = BOOKING_OVERVIEW_METRICS.find((m) => m.key === activeMetricKey);
  const chartData = useMemo(() => {
    return [...rangeRows]
      .sort((a, b) => a.record_date.localeCompare(b.record_date))
      .map((r) => ({
        label: new Date(r.record_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        revx: Number(r[activeMetric.revxField]) || 0,
        comp: Number(r[activeMetric.marketField]) || 0,
      }));
  }, [rangeRows, activeMetric]);
  if (!currentMeta) {
    return (
      <div>
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">← Back</button>
        <div className="rd-blank-tab">No data for this property.</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 24px", overflow: "auto", height: "100%", boxSizing: "border-box" }}>
      <button onClick={onBack} className="rd-btn-clear booking-back-btn" style={{ marginBottom: 12 }}>← Back to Booking Overview</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 className="booking-subpage-title" style={{ margin: 0 }}>{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>{currentMeta.location} · Agent: {currentMeta.agent}</span>
      </div>
      <div className="rd-filter-row" style={{ marginBottom: 16 }}>
        <PropertySearchSelect label="Switch Property" value={currentPropertyName} options={propertyOptions} onChange={setCurrentPropertyName} />
        <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
        <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
        <AvailableDateRangeFilter
          availableDates={allDates} fromDate={fromDate} toDate={toDate}
          onFromChange={(v) => { setFromDate(v); if (toDate && toDate < v) setToDate(v); }}
          onToChange={setToDate}
          onClear={() => { setFromDate(allDates[0] || ""); setToDate(allDates[allDates.length - 1] || ""); }}
          onToday={goToToday}
        />
      </div>
      <div className="mmt-detail-kpi-grid booking-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 210px))" }}>
        {BOOKING_OVERVIEW_METRICS.map((m) => {
          const isActive = activeMetricKey === m.key;
          return (
            <button key={m.key} onClick={() => setActiveMetricKey(m.key)} className={`mmt-detail-card${isActive ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
              <div className="mmt-detail-card-label">{m.abbr} — REVX</div>
              <div className="mmt-detail-card-value">{formatBookingMetricValue(totals[m.key]?.revx, m.format)}</div>
            </button>
          );
        })}
      </div>
      <div className="mmt-detail-chart-card">
        <div className="mmt-detail-title">{currentMeta.name}'s {activeMetric.abbr} — REVX vs Market</div>
        <div className="mmt-detail-subtext">{formatDateLabel(fromDate)} – {formatDateLabel(toDate)}</div>
        {chartData.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this date range.</div>
        ) : (
          <div className="booking-chart-scroll">
            <MMTBarChart data={chartData} revxLabel="REVX" compLabel="MARKET" />
          </div>
        )}
      </div>
      <div className="mmt-asp-table-card" style={{ marginTop: 20 }}>
        <div className="mmt-asp-table-scroll">
          <table className="rd-table occ-table">
            <colgroup>
              <col style={{ width: 200 }} />
              {rangeRows.map((_, i) => <col key={i} style={{ width: 100 }} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="rd-th-fixed" style={{ left: 0, width: 200 }}>METRIC</th>
                {rangeRows.map((r) => (
                  <th key={r.record_date} className="occ-th-metric booking-th-single">{formatDateLabel(r.record_date)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BOOKING_OVERVIEW_METRICS.map((m) => (
                <React.Fragment key={m.key}>
                  <tr className="rd-matrix-row rd-row-even">
                    <td className="rd-td-fixed" style={{ left: 0, width: 200, backgroundColor: "#fff" }}>{m.abbr} (REVX)</td>
                    {rangeRows.map((r) => <td key={r.record_date} className="occ-td-adr">{formatBookingMetricValue(r[m.revxField], m.format)}</td>)}
                  </tr>
                  <tr className="rd-matrix-row rd-row-odd">
                    <td className="rd-td-fixed" style={{ left: 0, width: 200, backgroundColor: "#f8fafc" }}>{m.abbr} (Market)</td>
                    {rangeRows.map((r) => <td key={r.record_date} className="occ-td-a">{formatBookingMetricValue(r[m.marketField], m.format)}</td>)}
                  </tr>
                  <tr className="rd-matrix-row occ-subrow">
                    <td className="occ-subrow-label rd-td-fixed" style={{ left: 0, width: 200 }}>% Difference</td>
                    {rangeRows.map((r) => {
                      const revx = Number(r[m.revxField]) || 0;
                      const mkt = Number(r[m.marketField]) || 0;
                      const pct = mkt !== 0 ? ((revx - mkt) / mkt) * 100 : 0;
                      return <td key={r.record_date} className="occ-td-t" style={{ color: pct >= 0 ? "#166534" : "#7f1d1d" }}>{pct >= 0 ? "+" : ""}{pct.toFixed(1)}%</td>;
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
const VC_METRICS = [
  { key: "bookings", label: "CONVERSION", hasMarket: true, marketField: "bookingsMarket", varField: "bookingsVar" },
  { key: "views", label: "PROPERTY PAGE VISITS", hasMarket: true, marketField: "viewsMarket", varField: "viewsVar" },
  { key: "searchViews", label: "SEARCH RESULT VIEWS", hasMarket: true, marketField: "searchViewsMarket", varField: "searchViewsVar" },
];

function VisitConversionTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [metricDisplayModes, setMetricDisplayModes] = useState({
    bookings: "sum",
    views: "sum",
    searchViews: "sum",
  });
  const [selectedPropertyName, setSelectedPropertyName] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showMkt, setShowMkt] = useState(false);
  const [showVar, setShowVar] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshPopup, setRefreshPopup] = useState(null);
  const rankByHotel = useHotelRankLookup();
  const ranklyRatings = useRanklyRatingsLookup();
  useEffect(() => {
    fetch(apiUrl("/api/booking/visit-conversion"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRawRows(json.data);
        else setLoadError(json.error || "Failed to load data");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshPopup(null);
    try {
      const res = await fetch(apiUrl("/api/booking/visit-conversion?forceRefresh=true"));
      const json = await res.json();
      if (json.success) {
        setRawRows(json.data);
        setRefreshPopup({ type: "success", message: "Showing the latest data from Athena." });
      } else {
        setRefreshPopup({ type: "error", message: json.error || "Couldn't refresh data." });
      }
    } catch (err) {
      setRefreshPopup({ type: "error", message: err.message });
    } finally {
      setRefreshing(false);
    }
  }
  useEffect(() => {
    if (!refreshPopup) return;
    const t = setTimeout(() => setRefreshPopup(null), 6000);
    return () => clearTimeout(t);
  }, [refreshPopup]);
  const availableDates = useMemo(() => Array.from(new Set(rawRows.map((r) => r.record_date).filter(Boolean))).sort(), [rawRows]);
  function currentMonthStart(lastDateStr) {
    const d = new Date(`${lastDateStr}T00:00:00`);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  }
  useEffect(() => {
    if (availableDates.length > 0 && !fromDate && !toDate) {
      const last = availableDates[availableDates.length - 1];
      setFromDate(currentMonthStart(last));
      setToDate(last);
    }
  }, [availableDates]);
  function resetDateRange() {
    if (availableDates.length > 0) {
      const last = availableDates[availableDates.length - 1];
      setFromDate(currentMonthStart(last));
      setToDate(last);
    }
  }
  const [lastNDaysInput, setLastNDaysInput] = useState("");
  function applyLastNDays(value) {
    setLastNDaysInput(value);
    const n = parseInt(value, 10);
    if (!Number.isFinite(n) || n <= 0 || availableDates.length === 0) return;
    const startIdx = Math.max(0, availableDates.length - n);
    setFromDate(availableDates[startIdx]);
    setToDate(availableDates[availableDates.length - 1]);
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (availableDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : availableDates[availableDates.length - 1];
    setFromDate(exact || fallback);
    setToDate(exact || fallback);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => { if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent }); });
    return Array.from(map.values());
  }, [rawRows]);
  const filters = { properties: selectedHotels, locations: selectedLocations, agents: selectedAgents };
  const hotelOptions = useMemo(() => getAvailableOptions("name", filters, propertyMeta), [propertyMeta, selectedLocations, selectedAgents]);
  const locationOptions = useMemo(() => getAvailableOptions("location", filters, propertyMeta), [propertyMeta, selectedHotels, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", filters, propertyMeta), [propertyMeta, selectedHotels, selectedLocations]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate);
  }, [availableDates, fromDate, toDate]);
  const valuesByDateHotel = useMemo(() => {
    const map = {};
    rawRows.forEach((r) => {
      const d = r.record_date;
      if (!map[d]) map[d] = {};
      map[d][r.hotel_name] = r;
    });
    return map;
  }, [rawRows]);
  const visibleMeta = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)),
    [propertyMeta, selectedHotels, selectedLocations, selectedAgents, searchQuery]
  );
  const rows = useMemo(() => {
    return visibleMeta.map((meta) => {
      const perDate = dateColumns.map((d) => {
        const rec = valuesByDateHotel[d]?.[meta.name];
        const bookings = rec ? Number(rec.daily_bookings) || 0 : 0;
        const views = rec ? Number(rec.page_views) || 0 : 0;
        const bookingsMarket = rec ? Number(rec.daily_bookings_market) || 0 : 0;
        const viewsMarket = rec ? Number(rec.page_views_market) || 0 : 0;
        const searchViews = rec ? Number(rec.search_views) || 0 : 0;
        const searchViewsMarket = rec ? Number(rec.search_views_market) || 0 : 0;
        return {
          bookings,
          views,
          bookingsMarket,
          viewsMarket,
          searchViews,
          searchViewsMarket,
          bookingsVar: bookings - bookingsMarket,
          viewsVar: views - viewsMarket,
          searchViewsVar: searchViews - searchViewsMarket,
        };
      });
      const totals = {
        bookings: perDate.reduce((s, d) => s + d.bookings, 0),
        views: perDate.reduce((s, d) => s + d.views, 0),
        searchViews: perDate.reduce((s, d) => s + d.searchViews, 0),
        bookingsMarket: perDate.reduce((s, d) => s + d.bookingsMarket, 0),
        viewsMarket: perDate.reduce((s, d) => s + d.viewsMarket, 0),
        searchViewsMarket: perDate.reduce((s, d) => s + d.searchViewsMarket, 0),
        bookingsVar: perDate.reduce((s, d) => s + d.bookingsVar, 0),
        viewsVar: perDate.reduce((s, d) => s + d.viewsVar, 0),
        searchViewsVar: perDate.reduce((s, d) => s + d.searchViewsVar, 0),
      };
      return { property: meta.name, location: meta.location, agent: meta.agent, perDate, totals };
    });
  }, [visibleMeta, dateColumns, valuesByDateHotel]);
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const metricMatch = sortKey.match(/^vcmetric:(\d+):(bookings|views|bookingsMarket|viewsMarket|searchViews|searchViewsMarket|bookingsVar|viewsVar|searchViewsVar)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (sortKey === "rank") {
        const ra = rankByHotel[a.property]?.rank ?? null;
        const rb = rankByHotel[b.property]?.rank ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = Number(ra) - Number(rb);
      } else if (sortKey === "rankly") {
        const ra = ranklyRatings.getRating(a.property, "booking")?.rating ?? null;
        const rb = ranklyRatings.getRating(b.property, "booking")?.rating ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = ra - rb;
      } else if (metricMatch) {
        const dIdx = Number(metricMatch[1]);
        const field = metricMatch[2];
        const va = a.perDate[dIdx]?.[field] ?? 0;
        const vb = b.perDate[dIdx]?.[field] ?? 0;
        cmp = va - vb;
      } else if (sortKey.startsWith("vctotal:")) {
        const field = sortKey.split(":")[1];
        const va = a.totals[field] ?? 0;
        const vb = b.totals[field] ?? 0;
        cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortKey, sortDirection, rankByHotel, ranklyRatings]);
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }
  function clearAllFilters() {
    setSelectedHotels([]); setSelectedLocations([]); setSelectedAgents([]);
    setSearchQuery(""); resetDateRange();
  }
  if (loading) return <div className="rd-blank-tab">Loading visit & conversion data...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load visit & conversion data: {loadError}</div>;
  if (selectedPropertyName) {
    return (
      <VisitConversionDetailView
        allRows={rawRows}
        initialPropertyName={selectedPropertyName}
        onBack={() => setSelectedPropertyName(null)}
      />
    );
  }
  return (
    <>
      {refreshPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${refreshPopup.type}`}>
          <span>{refreshPopup.message}</span>
          <button onClick={() => setRefreshPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter label="Hotel Name" options={hotelOptions} selected={selectedHotels} onChange={setSelectedHotels} onClear={() => setSelectedHotels([])} />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={availableDates} fromDate={fromDate} toDate={toDate}
              onFromChange={handleFromDateChange} onToChange={setToDate} onClear={resetDateRange} onToday={goToToday}
            />
            <div className="rd-filter-group">
              <div className="rd-filter-label-row">
                <label className="rd-filter-label">Last</label>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  min="1"
                  value={lastNDaysInput}
                  onChange={(e) => applyLastNDays(e.target.value)}
                  placeholder="days"
                  className="rd-dropdown-btn"
                  style={{ width: 70 }}
                  title="Show the last N dates that actually have data, ending at the most recent available date"
                />
                <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>days</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMkt((v) => !v)}
              className={`rd-btn-toggle${showMkt ? " rd-btn-toggle-active" : ""}`}
              title={showMkt ? "Hide market data" : "Show market data"}
            >
              MKT
            </button>
            <button
              type="button"
              onClick={() => setShowVar((v) => !v)}
              className={`rd-btn-toggle${showVar ? " rd-btn-toggle-active" : ""}`}
              title={showVar ? "Hide variance vs market" : "Show variance vs market (property minus market)"}
            >
              VAR
            </button>
            <div className="rd-actions">
              {canUseSync() && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className={`rd-btn-sync${refreshing ? " rd-btn-sync-active" : ""}`}
                  title="Show the latest data currently in Athena"
                >
                  <RefreshCw size={14} className={refreshing ? "rd-spin" : ""} />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </button>
              )}
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: BOOKING_PROPERTY_COL_WIDTH }} />
                <col style={{ width: BOOKING_SIDE_COL_WIDTH }} />
                <col style={{ width: BOOKING_SIDE_COL_WIDTH }} />
                {dateColumns.map((d) =>
                  VC_METRICS.flatMap((m) => {
                    const cols = [<col key={`${d}-${m.key}-col`} style={{ width: BOOKING_METRIC_COL_WIDTH }} />];
                    if (m.hasMarket && showMkt) {
                      cols.push(<col key={`${d}-${m.key}-mkt-col`} style={{ width: BOOKING_METRIC_COL_WIDTH }} />);
                    }
                    if (m.hasMarket && showVar) {
                      cols.push(<col key={`${d}-${m.key}-var-col`} style={{ width: BOOKING_METRIC_COL_WIDTH }} />);
                    }
                    return cols;
                  })
                )}
                {["bookings", "views", "searchViews"].flatMap((key) => {
                  const cols = [<col key={`total-${key}-col`} style={{ width: BOOKING_METRIC_COL_WIDTH + 20 }} />];
                  if (showMkt) cols.push(<col key={`total-${key}-mkt-col`} style={{ width: BOOKING_METRIC_COL_WIDTH + 20 }} />);
                  if (showVar) cols.push(<col key={`total-${key}-var-col`} style={{ width: BOOKING_METRIC_COL_WIDTH + 20 }} />);
                  return cols;
                })}
              </colgroup>
              <thead>
                <tr className="occ-group-row">
                  <th rowSpan={2} onClick={() => handleHeaderClick("property")} style={{ left: 0, width: BOOKING_PROPERTY_COL_WIDTH }} className="rd-th-fixed">
                    <span className="rd-count-badge">{sortedRows.length}</span>HOTEL NAME
                    {sortKey === "property" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th rowSpan={2} onClick={() => handleHeaderClick("rank")} style={{ left: BOOKING_RANK_LEFT, width: BOOKING_SIDE_COL_WIDTH }} className="rd-th-fixed rd-side">
                    RANK
                    {sortKey === "rank" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th rowSpan={2} onClick={() => handleHeaderClick("rankly")} style={{ left: BOOKING_RANKLY_LEFT, width: BOOKING_SIDE_COL_WIDTH }} className="rd-th-fixed rd-side">
                    RATING
                    {sortKey === "rankly" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  {dateColumns.map((d) => {
                    const colSpanForDate = VC_METRICS.reduce(
                      (sum, m) => sum + 1 + (m.hasMarket && showMkt ? 1 : 0) + (m.hasMarket && showVar ? 1 : 0),
                      0
                    );
                    return <th key={d} colSpan={colSpanForDate} className="occ-th-group">{formatDateLabel(d)}</th>;
                  })}
                  {[
                    { key: "bookings", label: "CONVERSION" },
                    { key: "views", label: "PROPERTY PAGE VISITS" },
                    { key: "searchViews", label: "SEARCH RESULT VIEWS" },
                  ].map(({ key, label }, mIdx) => {
                    const sortKeyForMetric = `vctotal:${key}`;
                    const totalColSpan = 1 + (showMkt ? 1 : 0) + (showVar ? 1 : 0);
                    return (
                      <th
                        key={`total-${key}`}
                        colSpan={totalColSpan}
                        onClick={() => handleHeaderClick(sortKeyForMetric)}
                        className={`occ-th-group rd-total-header-blank${mIdx === 0 ? " rd-total-edge" : ""}`}
                        style={{ cursor: "pointer" }}
                      >
                        {label} TOTAL
                        {sortKey === sortKeyForMetric && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {dateColumns.map((d, dIdx) =>
                    VC_METRICS.flatMap((m) => {
                      const key = `vcmetric:${dIdx}:${m.key}`;
                      const cells = [
                        <th key={`${d}-${m.key}`} onClick={() => handleHeaderClick(key)} className="occ-th-metric">
                          {m.label}
                          {sortKey === key && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                        </th>,
                      ];
                      if (m.hasMarket && showMkt) {
                        const mktKey = `vcmetric:${dIdx}:${m.marketField}`;
                        cells.push(
                          <th key={`${d}-${m.key}-mkt`} onClick={() => handleHeaderClick(mktKey)} className="occ-th-metric">
                            {m.label} MKT
                            {sortKey === mktKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                        );
                      }
                      if (m.hasMarket && showVar) {
                        const varKey = `vcmetric:${dIdx}:${m.varField}`;
                        cells.push(
                          <th key={`${d}-${m.key}-var`} onClick={() => handleHeaderClick(varKey)} className="occ-th-metric">
                            {m.label} VAR
                            {sortKey === varKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                        );
                      }
                      return cells;
                    })
                  )}
                  {["bookings", "views", "searchViews"].flatMap((key, mIdx) => {
                    const ths = [
                      <th key={`total-filler-${key}`} className={`occ-th-metric rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                        <select
                          value={metricDisplayModes[key]}
                          onChange={(e) => setMetricDisplayModes((prev) => ({ ...prev, [key]: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          className="rd-total-select"
                        >
                          <option value="sum">Sum</option>
                          <option value="average">Average</option>
                        </select>
                      </th>,
                    ];
                    if (showMkt) {
                      ths.push(
                        <th key={`total-mkt-${key}`} className="occ-th-metric rd-total-grid-dark">MKT TOTAL</th>
                      );
                    }
                    if (showVar) {
                      ths.push(
                        <th key={`total-var-${key}`} className="occ-th-metric rd-total-grid-dark">VAR TOTAL</th>
                      );
                    }
                    return ths;
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  return (
                    <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`} onClick={() => setSelectedPropertyName(row.property)} title="Click to view this property's visit & conversion performance">
                      <td style={{ left: 0, width: BOOKING_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>{row.property}</td>
                      <RankCell rank={rankByHotel[row.property]} rowBgClass={rowBgClass} />
                      <RanklyRatingCell
                        entry={ranklyRatings.getRating(row.property, "booking")}
                        platform="booking"
                        style={{ left: BOOKING_RANKLY_LEFT, width: BOOKING_SIDE_COL_WIDTH }}
                        className="rd-td-fixed rd-side rd-rank-cell"
                      />
                      {row.perDate.map((snap, dIdx) => (
                        <React.Fragment key={dIdx}>
                          <td className="occ-td-t">{snap.bookings.toLocaleString("en-IN")}</td>
                          {showMkt && <td className="occ-td-t">{snap.bookingsMarket.toLocaleString("en-IN")}</td>}
                          {showVar && (
                            <td className={snap.bookingsVar >= 0 ? "mmt-growth-up" : "mmt-growth-down"}>
                              {snap.bookingsVar >= 0 ? "▲" : "▼"} {Math.abs(snap.bookingsVar).toLocaleString("en-IN")}
                            </td>
                          )}
                          <td className="occ-td-u">{snap.views.toLocaleString("en-IN")}</td>
                          {showMkt && <td className="occ-td-u">{snap.viewsMarket.toLocaleString("en-IN")}</td>}
                          {showVar && (
                            <td className={snap.viewsVar >= 0 ? "mmt-growth-up" : "mmt-growth-down"}>
                              {snap.viewsVar >= 0 ? "▲" : "▼"} {Math.abs(snap.viewsVar).toLocaleString("en-IN")}
                            </td>
                          )}
                          <td className="occ-td-u">{snap.searchViews.toLocaleString("en-IN")}</td>
                          {showMkt && <td className="occ-td-u">{snap.searchViewsMarket.toLocaleString("en-IN")}</td>}
                          {showVar && (
                            <td className={snap.searchViewsVar >= 0 ? "mmt-growth-up" : "mmt-growth-down"}>
                              {snap.searchViewsVar >= 0 ? "▲" : "▼"} {Math.abs(snap.searchViewsVar).toLocaleString("en-IN")}
                            </td>
                          )}
                        </React.Fragment>
                      ))}
                      {(() => {
                        const divisor = dateColumns.length || 1;
                        const metricsForTotals = [
                          { key: "bookings", total: row.totals.bookings, market: row.totals.bookingsMarket, varTotal: row.totals.bookingsVar },
                          { key: "views", total: row.totals.views, market: row.totals.viewsMarket, varTotal: row.totals.viewsVar },
                          { key: "searchViews", total: row.totals.searchViews, market: row.totals.searchViewsMarket, varTotal: row.totals.searchViewsVar },
                        ];
                        return metricsForTotals.map((m, mIdx) => {
                          const mode = metricDisplayModes[m.key];
                          const display = mode === "sum" ? m.total : m.total / divisor;
                          const marketDisplay = mode === "sum" ? m.market : m.market / divisor;
                          const varDisplay = mode === "sum" ? m.varTotal : m.varTotal / divisor;
                          return (
                            <React.Fragment key={m.key}>
                              <td className={`rd-total-td${mIdx === 0 ? " rd-total-edge" : ""}`}>
                                {display.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                              </td>
                              {showMkt && (
                                <td className="rd-total-td">
                                  {marketDisplay.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                                </td>
                              )}
                              {showVar && (
                                <td className={`rd-total-td ${varDisplay >= 0 ? "mmt-growth-up" : "mmt-growth-down"}`}>
                                  {varDisplay >= 0 ? "▲" : "▼"} {Math.abs(varDisplay).toLocaleString("en-IN", { maximumFractionDigits: 1 })}
                                </td>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
function VisitConversionDetailView({ allRows, initialPropertyName, onBack }) {
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const [activeMetricKey, setActiveMetricKey] = useState("bookings");
  const allDates = useMemo(() => Array.from(new Set(allRows.map((r) => r.record_date).filter(Boolean))).sort(), [allRows]);
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => { if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent }); });
    return Array.from(map.values());
  }, [allRows]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const currentMeta = propertyMeta.find((m) => m.name === currentPropertyName);
  const switchFilters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(() => getAvailableOptions("location", switchFilters, propertyMeta), [propertyMeta, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", switchFilters, propertyMeta), [propertyMeta, selectedLocations]);
  const propertyOptions = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, switchFilters, null)).map((m) => m.name).sort(),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  useEffect(() => {
    if (propertyOptions.length > 0 && !propertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(propertyOptions[0]);
    }
  }, [propertyOptions]);
  const rangeRows = useMemo(
    () => [...allRows.filter((r) => r.hotel_name === currentPropertyName && r.record_date >= fromDate && r.record_date <= toDate)].sort((a, b) => a.record_date.localeCompare(b.record_date)),
    [allRows, currentPropertyName, fromDate, toDate]
  );
  const totals = useMemo(() => {
    const views = rangeRows.reduce((s, r) => s + (Number(r.page_views) || 0), 0);
    const bookings = rangeRows.reduce((s, r) => s + (Number(r.daily_bookings) || 0), 0);
    const conv = views > 0 ? (bookings / views) * 100 : 0;
    const avgViews = rangeRows.length > 0 ? views / rangeRows.length : 0;
    return { views, bookings, conv, avgViews };
  }, [rangeRows]);
  const chartData = useMemo(() => {
    return rangeRows.map((r) => ({
      label: new Date(r.record_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
      revx:
        activeMetricKey === "bookings"
          ? Number(r.daily_bookings) || 0
          : activeMetricKey === "views"
          ? Number(r.page_views) || 0
          : Number(r.conversion_pct) || 0,
      comp: 0,
    }));
  }, [rangeRows, activeMetricKey]);
  if (!currentMeta) {
    return (
      <div>
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">← Back</button>
        <div className="rd-blank-tab">No data for this property.</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 24px", overflow: "auto", height: "100%", boxSizing: "border-box" }}>
      <button onClick={onBack} className="rd-btn-clear booking-back-btn" style={{ marginBottom: 12 }}>← Back to Visit & Conversion</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 className="booking-subpage-title" style={{ margin: 0 }}>{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>{currentMeta.location} · Agent: {currentMeta.agent}</span>
      </div>
      <div className="rd-filter-row" style={{ marginBottom: 16 }}>
        <PropertySearchSelect label="Switch Property" value={currentPropertyName} options={propertyOptions} onChange={setCurrentPropertyName} />
        <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
        <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
        <AvailableDateRangeFilter
          availableDates={allDates} fromDate={fromDate} toDate={toDate}
          onFromChange={(v) => { setFromDate(v); if (toDate && toDate < v) setToDate(v); }}
          onToChange={setToDate}
          onClear={() => { setFromDate(allDates[0] || ""); setToDate(allDates[allDates.length - 1] || ""); }}
          onToday={goToToday}
        />
      </div>
      <div className="booking-funnel-card">
        <div className="booking-funnel-row">
          <div className="booking-funnel-step">
            <div className="booking-funnel-step-value">{totals.views.toLocaleString("en-IN")}</div>
            <div className="booking-funnel-step-label">Property Page Views</div>
          </div>
          <div className="booking-funnel-arrow">
            →
            <span>{totals.conv.toFixed(2)}% converted</span>
          </div>
          <div className="booking-funnel-step">
            <div className="booking-funnel-step-value">{totals.bookings.toLocaleString("en-IN")}</div>
            <div className="booking-funnel-step-label">Bookings</div>
          </div>
        </div>
      </div>
      <div className="mmt-detail-kpi-grid booking-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 210px))" }}>
        <button onClick={() => setActiveMetricKey("bookings")} className={`mmt-detail-card${activeMetricKey === "bookings" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Daily Bookings</div>
          <div className="mmt-detail-card-value">{totals.bookings.toLocaleString("en-IN")}</div>
        </button>
        <button onClick={() => setActiveMetricKey("views")} className={`mmt-detail-card${activeMetricKey === "views" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Property Page Views</div>
          <div className="mmt-detail-card-value">{totals.views.toLocaleString("en-IN")}</div>
          <span className="rd-rating-reviews">Avg: {totals.avgViews.toLocaleString("en-IN", { maximumFractionDigits: 1 })}/day</span>
        </button>
        <button onClick={() => setActiveMetricKey("conversion")} className={`mmt-detail-card${activeMetricKey === "conversion" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Conversion</div>
          <div className="mmt-detail-card-value">{totals.conv.toFixed(2)}%</div>
        </button>
      </div>
      <div className="mmt-detail-chart-card">
        <div className="mmt-detail-title">
          {currentMeta.name}'s {activeMetricKey === "bookings" ? "Bookings" : activeMetricKey === "views" ? "Page Views" : "Conversion"} trend
        </div>
        <div className="mmt-detail-subtext">{formatDateLabel(fromDate)} – {formatDateLabel(toDate)}</div>
        {chartData.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this date range.</div>
        ) : (
          <div className="booking-chart-scroll">
            <MMTBarChart
              data={chartData}
              showComp={false}
              revxLabel={activeMetricKey === "bookings" ? "BOOKINGS" : activeMetricKey === "views" ? "PAGE VIEWS" : "CONV %"}
            />
          </div>
        )}
      </div>
    </div>
  );
}
function BookingTabNav({ active, onSelect }) {
  return (
    <div className="booking-tab-nav">
      {BOOKING_SUBPAGES.map((p) => (
        <button
          key={p.id}
          onClick={() => onSelect(p.id)}
          className={`booking-tab-btn${active === p.id ? " booking-tab-btn-active" : ""}`}
        >
          {p.title}
        </button>
      ))}
    </div>
  );
}
function BookingSubPage({ page, onSwitch }) {
  return (
    <div className="booking-subpage">
      <div className="booking-subpage-header">
        <h2 className="booking-subpage-title">{page.title}</h2>
        <BookingTabNav active={page.id} onSelect={onSwitch} />
      </div>
      <div className="booking-subpage-body">
        {page.id === "overview" && <BookingOverviewTab />}
        {page.id === "visit" && <VisitConversionTab />}
      </div>
    </div>
  );
}
function BookingDashboardTab() {
  const [activeSubpage, setActiveSubpage] = useState("overview");
  const page = BOOKING_SUBPAGES.find((p) => p.id === activeSubpage);
  return (
    <BookingSubPage
      page={page}
      onSwitch={(id) => setActiveSubpage(id)}
    />
  );
}
const MMT_METRICS = [
  { key: "Room Nights", label: "Room Nights", abbr: "RN" },
  { key: "Revenue", label: "Revenue", abbr: "REV" },
  { key: "ASP", label: "ASP", abbr: "ASP" },
  { key: "Property Visits", label: "Property Visits", abbr: "VISITS" },
  { key: "Conversion", label: "Conversion", abbr: "CONV%" },
];
const MMT_METRIC_FORMAT = {
  "Room Nights": "int",
  Revenue: "currency",
  ASP: "currency",
  "Property Visits": "int",
  Conversion: "percent",
};
function formatMmtMetricValue(value, metric) {
  const format = MMT_METRIC_FORMAT[metric] || "int";
  const num = Number(value) || 0;
  if (format === "currency") return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${num.toFixed(2)}%`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
function PropertySearchSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const filteredOptions = options.filter((o) =>
    o.toLowerCase().includes(query.trim().toLowerCase())
  );
  function selectOption(opt) {
    onChange(opt);
    setOpen(false);
    setQuery("");
  }
  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <div className="rd-filter-label-row">
        <label className="rd-filter-label">{label}</label>
      </div>
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{value || "Select property"}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search property..."
            className="mmt-property-search-input"
          />
          {filteredOptions.length === 0 ? (
            <div className="mmt-property-search-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => selectOption(opt)}
                className={`rd-option${opt === value ? " rd-selected" : ""}`}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
function computeGrowthBadge(rawRows, hotelName, metric, fromDate, toDate) {
  if (!fromDate || !toDate) return null;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  const rangeDays = Math.round((to - from) / 86400000) + 1;
  const priorTo = new Date(from);
  priorTo.setDate(priorTo.getDate() - 1);
  const priorFrom = new Date(priorTo);
  priorFrom.setDate(priorFrom.getDate() - (rangeDays - 1));
  const priorFromStr = priorFrom.toISOString().slice(0, 10);
  const priorToStr = priorTo.toISOString().slice(0, 10);
  function periodValue(m, f, t) {
    const vals = rawRows
      .filter((r) => r.hotel_name === hotelName && r.metric === m && r.date >= f && r.date <= t)
      .map((r) => Number(r.value) || 0);
    if (m === "ASP" || m === "Conversion") {
      const nonZero = vals.filter((v) => v > 0);
      if (nonZero.length === 0) return 0;
      return nonZero.reduce((s, v) => s + v, 0) / nonZero.length;
    }
    return vals.reduce((s, v) => s + v, 0);
  }
  const currentTotal = periodValue(metric, fromDate, toDate);
  const priorTotal = periodValue(metric, priorFromStr, priorToStr);
  if (priorTotal === 0) return null;
  const pct = ((currentTotal - priorTotal) / priorTotal) * 100;
  return { pct, up: pct >= 0 };
}
function buildAspByHotel(rowsByDate, hotelName) {
  const lowerHotelName = (hotelName || "").toLowerCase();
  const dates = Object.keys(rowsByDate);
  const nameCounts = {};
  dates.forEach((d) => {
    (rowsByDate[d] || [])
      .filter((r) => r.hotel_name === hotelName)
      .forEach((r) => {
        const n = r.asp_hotel_name || "";
        const nl = n.toLowerCase();
        if (nl && (nl.includes(lowerHotelName) || lowerHotelName.includes(nl))) {
          nameCounts[n] = (nameCounts[n] || 0) + 1;
        }
      });
  });
  const canonicalSelfName =
    Object.keys(nameCounts).sort((a, b) => nameCounts[b] - nameCounts[a])[0] || null;
  const byDate = {};
  dates.forEach((d) => {
    const forThisHotel = (rowsByDate[d] || []).filter((r) => r.hotel_name === hotelName);
    if (forThisHotel.length === 0) return;
    byDate[d] = {
      allRows: forThisHotel.map((r) => ({
        name: r.asp_hotel_name || "(unnamed)",
        price: Number(r.price),
        rank: r.rank != null ? Number(r.rank) : null,
        isSelf: canonicalSelfName != null && r.asp_hotel_name === canonicalSelfName,
      })),
    };
  });
  return byDate;
}
function useAspCompetitorData(hotelName, fromDate, toDate) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!hotelName || !fromDate || !toDate) return;
    setData(null);
    setError(null);
    // PERFORMANCE FIX: was one /api/mmt/asp?date=X fetch per CALENDAR DAY
    // in the range (200+ requests for a multi-month range) — now one
    // /api/mmt/asp/range call for the whole span, backed by a 5-minute
    // server-side cache (see mmtRoutes.js).
    fetch(apiUrl(`/api/mmt/asp/range?from=${fromDate}&to=${toDate}`))
      .then((r) => r.json())
      .then((json) => {
        const rows = json.success ? json.data : [];
        const rowsByDate = {};
        for (const r of rows) {
          if (!rowsByDate[r.date]) rowsByDate[r.date] = [];
          rowsByDate[r.date].push(r);
        }
        setData(buildAspByHotel(rowsByDate, hotelName));
      })
      .catch((err) => setError(err.message));
  }, [hotelName, fromDate, toDate]);
  return { data, error };
}
function useAspCompetitorDataAllProperties(fromDate, toDate) {
  const [rowsByDate, setRowsByDate] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!fromDate || !toDate) return;
    setRowsByDate(null);
    setError(null);
    // Same PERFORMANCE FIX as useAspCompetitorData above — one call for
    // the whole range instead of one per calendar day.
    fetch(apiUrl(`/api/mmt/asp/range?from=${fromDate}&to=${toDate}`))
      .then((r) => r.json())
      .then((json) => {
        const rows = json.success ? json.data : [];
        const map = {};
        for (const r of rows) {
          if (!map[r.date]) map[r.date] = [];
          map[r.date].push(r);
        }
        setRowsByDate(map);
      })
      .catch((err) => setError(err.message));
  }, [fromDate, toDate]);
  return { rowsByDate, error };
}
function MmtAspCompetitorTableView({ hotelName, byDate }) {
  const dates = Object.keys(byDate).sort();
  if (dates.length === 0) {
    return <div className="mmt-empty-note">No competitor rate data for this date range.</div>;
  }
  const competitorNames = Array.from(
    new Set(dates.flatMap((d) => byDate[d].allRows.filter((r) => !r.isSelf).map((r) => r.name)))
  ).sort();
  function selfRowFor(date) {
    return byDate[date].allRows.find((r) => r.isSelf) || null;
  }
  function medianFor(date) {
    const others = byDate[date].allRows.filter((r) => !r.isSelf).map((r) => r.price).filter((p) => !Number.isNaN(p));
    if (others.length === 0) return null;
    const sorted = [...others].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  }
  return (
    <div className="mmt-asp-table-card" style={{ marginTop: 20, width: "100%", boxSizing: "border-box" }}>
      <div className="mmt-asp-table-scroll">
        <table className="rd-table occ-table">
          <colgroup>
            <col style={{ width: 240 }} />
            {dates.map((d) => (
              <col key={`${d}-col`} style={{ width: 110 }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="rd-th-fixed" style={{ left: 0, width: 240 }}>PROPERTY</th>
              {dates.map((d) => (
                <th key={d} className="occ-th-metric booking-th-single">{formatDateLabel(d)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="rd-matrix-row rd-row-even">
              <td className="rd-td-fixed" style={{ left: 0, width: 240, backgroundColor: "#fff" }}>{hotelName} (Rank)</td>
              {dates.map((d) => {
                const self = selfRowFor(d);
                return (
                  <td key={d} className="occ-td-t">{self && self.rank != null ? self.rank : "—"}</td>
                );
              })}
            </tr>
            <tr className="rd-matrix-row rd-row-odd">
              <td className="rd-td-fixed" style={{ left: 0, width: 240, backgroundColor: "#f8fafc" }}>{hotelName} (Rate)</td>
              {dates.map((d) => {
                const self = selfRowFor(d);
                return (
                  <td key={d} className="occ-td-adr">
                    {self && !Number.isNaN(self.price) ? `₹${self.price.toLocaleString("en-IN")}` : "—"}
                  </td>
                );
              })}
            </tr>
            <tr className="rd-matrix-row rd-row-even">
              <td className="rd-td-fixed" style={{ left: 0, width: 240, backgroundColor: "#fff" }}>Competitors (Median)</td>
              {dates.map((d) => {
                const median = medianFor(d);
                return (
                  <td key={d} className="occ-td-adr">
                    {median != null ? `₹${median.toLocaleString("en-IN")}` : "—"}
                  </td>
                );
              })}
            </tr>
            {competitorNames.map((name) => (
              <tr key={name} className="rd-matrix-row occ-subrow">
                <td className="occ-subrow-label rd-td-fixed" style={{ left: 0, width: 240 }}>{name}</td>
                {dates.map((d) => {
                  const match = byDate[d].allRows.find((r) => r.name === name);
                  return (
                    <td key={d} className="occ-td-a">
                      {match ? `₹${match.price.toLocaleString("en-IN")}` : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function MmtAspCompetitorTable({ hotelName, fromDate, toDate }) {
  const { data, error } = useAspCompetitorData(hotelName, fromDate, toDate);
  if (error) return <div className="rd-blank-tab">Couldn't load competitor rates: {error}</div>;
  if (!data) return <div className="rd-blank-tab">Loading competitor rates...</div>;
  return <MmtAspCompetitorTableView hotelName={hotelName} byDate={data} />;
}
function MMTCompAllPropertiesView({ propertyMeta, allDates, onBack }) {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      const last = allDates[allDates.length - 1];
      setFromDate(last);
      setToDate(last);
    }
  }, [allDates]);
  function resetDateRange() {
    if (allDates.length > 0) {
      const last = allDates[allDates.length - 1];
      setFromDate(last);
      setToDate(last);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  function clearAllFilters() {
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSearchQuery("");
    resetDateRange();
  }
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }
  const filters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(() => getAvailableOptions("location", filters, propertyMeta), [propertyMeta, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", filters, propertyMeta), [propertyMeta, selectedLocations]);
  const visibleMeta = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)),
    [propertyMeta, selectedLocations, selectedAgents, searchQuery]
  );
  const { rowsByDate, error } = useAspCompetitorDataAllProperties(fromDate, toDate);
  const dates = useMemo(() => Object.keys(rowsByDate || {}).sort(), [rowsByDate]);
  const propertyRows = useMemo(() => {
    if (!rowsByDate) return [];
    return visibleMeta.map((meta) => {
      const byDate = buildAspByHotel(rowsByDate, meta.name);
      const competitorNames = Array.from(
        new Set(dates.flatMap((d) => (byDate[d]?.allRows || []).filter((r) => !r.isSelf).map((r) => r.name)))
      ).sort();
      function selfRowFor(d) {
        return byDate[d]?.allRows.find((r) => r.isSelf) || null;
      }
      function medianFor(d) {
        const others = (byDate[d]?.allRows || []).filter((r) => !r.isSelf).map((r) => r.price).filter((p) => !Number.isNaN(p));
        if (others.length === 0) return null;
        const sorted = [...others].sort((a, b) => a - b);
        return sorted[Math.floor(sorted.length / 2)];
      }
      function priceFor(d, name) {
        const match = byDate[d]?.allRows.find((r) => r.name === name);
        return match ? match.price : null;
      }
      return { meta, competitorNames, selfRowFor, medianFor, priceFor };
    });
  }, [visibleMeta, rowsByDate, dates]);
  const sortedPropertyRows = useMemo(() => {
    const arr = [...propertyRows];
    const dateMatch = sortKey.match(/^date:(.+)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") {
        cmp = a.meta.name.localeCompare(b.meta.name);
      } else if (dateMatch) {
        const d = dateMatch[1];
        const ra = a.selfRowFor(d)?.price;
        const rb = b.selfRowFor(d)?.price;
        const va = ra != null && !Number.isNaN(ra) ? ra : null;
        const vb = rb != null && !Number.isNaN(rb) ? rb : null;
        if (va === null && vb === null) cmp = 0;
        else if (va === null) cmp = 1;
        else if (vb === null) cmp = -1;
        else cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [propertyRows, sortKey, sortDirection]);
  function toggleRow(name) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }
  const allExpanded = sortedPropertyRows.length > 0 && sortedPropertyRows.every((r) => expandedKeys.has(r.meta.name));
  function toggleExpandAll() {
    if (allExpanded) setExpandedKeys(new Set());
    else setExpandedKeys(new Set(sortedPropertyRows.map((r) => r.meta.name)));
  }
  const PROPERTY_COL_W = 260;
  return (
    <div className="booking-subpage">
      <div className="booking-subpage-header">
        {onBack && (
          <button onClick={onBack} className="rd-btn-clear booking-back-btn">
            ← Back to MMT Dashboard
          </button>
        )}
        <h2 className="booking-subpage-title">MMT Comp Data — Competitor Rates, All Properties</h2>
      </div>
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={allDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={resetDateRange}
              onToday={goToToday}
            />
            <div className="rd-actions">
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        {error && <div className="rd-blank-tab">Couldn't load competitor rates: {error}</div>}
        {!error && !rowsByDate && <div className="rd-blank-tab">Loading competitor rates...</div>}
        {!error && rowsByDate && sortedPropertyRows.length === 0 && (
          <div className="rd-blank-tab">No properties match the current filters.</div>
        )}
        {!error && rowsByDate && sortedPropertyRows.length > 0 && (
          <>
            <div className="occ-table-toolbar">
              <button
                onClick={toggleExpandAll}
                className="occ-expand-all-btn"
                title={allExpanded ? "Collapse all properties" : "Expand all properties"}
              >
                <ChevronsUpDown size={13} />
                {allExpanded ? "Collapse all" : "Expand all"}
              </button>
            </div>
            <div className="rd-matrix-card">
              <div className="rd-matrix-scroll">
                <table className="rd-table occ-table">
                  <colgroup>
                    <col style={{ width: PROPERTY_COL_W }} />
                    {dates.map((d) => (
                      <col key={`${d}-col`} style={{ width: 110 }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleHeaderClick("property")}
                        className="rd-th-fixed"
                        style={{ left: 0, width: PROPERTY_COL_W }}
                      >
                        <span className="rd-count-badge">{sortedPropertyRows.length}</span>
                        PROPERTY
                        {sortKey === "property" && (
                          <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                        )}
                      </th>
                      {dates.map((d) => {
                        const key = `date:${d}`;
                        return (
                          <th key={d} onClick={() => handleHeaderClick(key)} className="occ-th-metric booking-th-single">
                            {formatDateLabel(d)}
                            {sortKey === key && (
                              <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPropertyRows.map(({ meta, competitorNames, selfRowFor, medianFor, priceFor }, rIdx) => {
                      const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                      const isExpanded = expandedKeys.has(meta.name);
                      return (
                        <React.Fragment key={meta.name}>
                          <tr className={rowBgClass}>
                            <td style={{ left: 0, width: PROPERTY_COL_W }} className={`rd-td-fixed ${rowBgClass}`}>
                              {meta.name}
                              <span style={{ display: "block", fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>
                                {meta.location} · {meta.agent}
                              </span>
                            </td>
                            {dates.map((d) => {
                              const self = selfRowFor(d);
                              return (
                                <td key={d} className="occ-td-adr rd-rank-cell">
                                  {self ? (
                                    <>
                                      <span className="rd-rating-value">
                                        {!Number.isNaN(self.price) ? `₹${self.price.toLocaleString("en-IN")}` : "—"}
                                      </span>
                                      <span className="rd-rating-reviews">{self.rank != null ? `Rank: ${self.rank}` : ""}</span>
                                    </>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          <tr
                            className="rd-matrix-row occ-subrow"
                            onClick={() => toggleRow(meta.name)}
                          >
                            <td className="occ-subrow-label rd-td-fixed" style={{ left: 0, width: PROPERTY_COL_W }}>
                              Competitors (Median)
                              <ChevronDown size={12} className={`occ-expand-icon ${isExpanded ? "occ-expanded" : ""}`} />
                            </td>
                            {dates.map((d) => {
                              const median = medianFor(d);
                              return (
                                <td key={d} className="occ-td-adr">
                                  {median != null ? `₹${median.toLocaleString("en-IN")}` : "—"}
                                </td>
                              );
                            })}
                          </tr>
                          {isExpanded && competitorNames.map((name) => (
                            <tr key={`${meta.name}-${name}`} className="occ-subrow">
                              <td
                                className="occ-subrow-label rd-td-fixed"
                                style={{ left: 0, width: PROPERTY_COL_W, paddingLeft: 52 }}
                              >
                                {name}
                              </td>
                              {dates.map((d) => {
                                const price = priceFor(d, name);
                                return (
                                  <td key={d} className="occ-td-a">
                                    {price != null ? `₹${price.toLocaleString("en-IN")}` : "—"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
function MMTBarChart({ data, revxLabel = "REVX VALUE", compLabel = "COMP VALUE", showComp = true }) {
  const maxVal = Math.max(1, ...data.map((d) => (showComp ? Math.round(d.revx) + Math.round(d.comp) : Math.round(d.revx))));

  return (
    <div className="mmt-chart-card">
      <div className="mmt-chart-legend">
        <span className="mmt-legend-item"><span className="mmt-legend-dot mmt-dot-revx" />{revxLabel}</span>
        {showComp && <span className="mmt-legend-item"><span className="mmt-legend-dot mmt-dot-comp" />{compLabel}</span>}
      </div>
      <div className="mmt-chart-bars">
        {data.map((d) => {
          const revx = Math.round(d.revx);
          const comp = Math.round(d.comp);
          const total = showComp ? revx + comp : revx;
          const totalHeightPct = total > 0 ? (total / maxVal) * 100 : 2;
          const revxSharePct = total > 0 ? (revx / total) * 100 : 0;
          const compSharePct = total > 0 ? (comp / total) * 100 : 0;
          const isEmpty = total === 0;
          return (
            <div key={d.label} className="mmt-chart-group">
              <div
                className={`mmt-bar-stack${isEmpty ? " mmt-bar-stack-empty" : ""}`}
                style={{ height: `${totalHeightPct}%` }}
                title={
                  showComp
                    ? `${revxLabel}: ${revx.toLocaleString("en-IN")} · ${compLabel}: ${comp.toLocaleString("en-IN")}`
                    : `${revxLabel}: ${revx.toLocaleString("en-IN")}`
                }
              >
              {(() => {
                const barPixelHeight = (totalHeightPct / 100) * 200;
                const revxPixelHeight = barPixelHeight * (revxSharePct / 100);
                const compPixelHeight = barPixelHeight * (compSharePct / 100);
                const MIN_LABEL_HEIGHT = 14;
                return (
                  <>
                    {revx > 0 && (
                      <div className="mmt-bar-seg mmt-bar-seg-revx" style={{ height: `${revxSharePct}%` }}>
                        {revxPixelHeight >= MIN_LABEL_HEIGHT && (
                          <span className="mmt-bar-seg-value">{revx.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    )}
                    {showComp && comp > 0 && (
                      <div className="mmt-bar-seg mmt-bar-seg-comp" style={{ height: `${compSharePct}%` }}>
                        {compPixelHeight >= MIN_LABEL_HEIGHT && (
                          <span className="mmt-bar-seg-value">{comp.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
              </div>
              <div className="mmt-bar-label">{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
function MMTPropertyDetailView({ allRows, initialPropertyName, onBack }) {
  const [activeMetric, setActiveMetric] = useState("Room Nights");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const allDates = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.date).filter(Boolean))).sort(),
    [allRows]
  );
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function resetDateRange() {
    if (allDates.length > 0) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback =
      pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!map.has(r.hotel_name)) {
        map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent });
      }
    });
    return Array.from(map.values());
  }, [allRows]);
  const filters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locAgentFilters = { properties: [currentPropertyName], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(
    () => getAvailableOptions("location", locAgentFilters, propertyMeta),
    [propertyMeta, currentPropertyName, selectedAgents]
  );
  const agentOptions = useMemo(
    () => getAvailableOptions("agent", locAgentFilters, propertyMeta),
    [propertyMeta, currentPropertyName, selectedLocations]
  );
  const filteredPropertyOptions = useMemo(() => {
    return propertyMeta
      .filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery))
      .map((m) => m.name)
      .sort();
  }, [propertyMeta, selectedLocations, selectedAgents, searchQuery]);
  useEffect(() => {
    if (filteredPropertyOptions.length > 0 && !filteredPropertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(filteredPropertyOptions[0]);
    }
  }, [filteredPropertyOptions]);
  const currentMeta =
    propertyMeta.find((m) => m.name === currentPropertyName) ||
    propertyMeta.find(
      (m) => m.name?.trim().toLowerCase() === (currentPropertyName || "").trim().toLowerCase()
    );
  function switchProperty(name) {
    setCurrentPropertyName(name);
    setSearchQuery("");
  }
  function clearAllFilters() {
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSearchQuery("");
    resetDateRange();
  }
  const metricTotals = useMemo(() => {
    function sumMetric(key) {
      return allRows
        .filter((r) => r.hotel_name === currentPropertyName && r.metric === key && r.date >= fromDate && r.date <= toDate)
        .reduce((s, r) => s + (Number(r.value) || 0), 0);
    }
    function avgOfNonZeroDays(key) {
      const vals = allRows
        .filter((r) => r.hotel_name === currentPropertyName && r.metric === key && r.date >= fromDate && r.date <= toDate)
        .map((r) => Number(r.value) || 0)
        .filter((v) => v > 0);
      if (vals.length === 0) return 0;
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    }
    return {
      "Room Nights": sumMetric("Room Nights"),
      Revenue: sumMetric("Revenue"),
      "Property Visits": sumMetric("Property Visits"),
      ASP: avgOfNonZeroDays("ASP"),
      Conversion: avgOfNonZeroDays("Conversion"),
    };
  }, [allRows, currentPropertyName, fromDate, toDate]);
  const chartData = useMemo(() => {
    const byDate = new Map();
    allRows.forEach((r) => {
      if (r.hotel_name !== currentPropertyName) return;
      if (r.metric !== activeMetric) return;
      if (fromDate && r.date < fromDate) return;
      if (toDate && r.date > toDate) return;
      byDate.set(r.date, { revx: Number(r.value) || 0, comp: Number(r.competitor_value) || 0 });
    });
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({
        label: new Date(date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        revx: Math.round(vals.revx),
        comp: Math.round(vals.comp),
      }));
  }, [allRows, currentPropertyName, activeMetric, fromDate, toDate]);
  const growth = computeGrowthBadge(allRows, currentPropertyName, activeMetric, fromDate, toDate);
  if (!currentMeta) {
    return (
      <div className="booking-subpage">
        <div className="booking-subpage-header">
          <button onClick={onBack} className="rd-btn-clear booking-back-btn">← Back to MMT Dashboard</button>
        </div>
        <div className="rd-blank-tab">No properties match the current filters.</div>
      </div>
    );
  }
  return (
    <div
      className="booking-subpage"
      style={{ display: "block", height: "100%", overflow: "auto", boxSizing: "border-box" }}
    >
      <div className="booking-subpage-header">
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">
          ← Back to MMT Dashboard
        </button>
        <h2 className="booking-subpage-title">{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>
          {currentMeta.location} · Agent: {currentMeta.agent}
        </span>
      </div>
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <PropertySearchSelect
              label="Property Name"
              value={currentPropertyName}
              options={filteredPropertyOptions}
              onChange={switchProperty}
            />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={allDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={resetDateRange}
              onToday={goToToday}
            />
            <div className="rd-actions">
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="booking-subpage-body"
        style={{ display: "block", padding: "20px 24px", overflow: "visible", width: "100%", boxSizing: "border-box" }}
      >
        <div className="mmt-detail-kpi-grid">
          {MMT_METRICS.map((m) => {
            const isActive = activeMetric === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setActiveMetric(m.key)}
                className={`mmt-detail-card${isActive ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}
              >
                <div className="mmt-detail-card-label">{m.label}</div>
                <div className="mmt-detail-card-value">{formatMmtMetricValue(metricTotals[m.key], m.key)}</div>
              </button>
            );
          })}
        </div>
        <div className="mmt-detail-chart-card">
          <div className="mmt-detail-title-row">
            <div className="mmt-detail-title">{currentMeta.name}'s {activeMetric}</div>
            {growth && (
              <span className={`mmt-growth-badge ${growth.up ? "mmt-growth-up" : "mmt-growth-down"}`}>
                {growth.up ? "↑" : "↓"} {Math.abs(growth.pct).toFixed(0)}%
              </span>
            )}
          </div>
          <div className="mmt-detail-subtext">
            ({formatDateLabel(fromDate)} – {formatDateLabel(toDate)})
            {growth && " · vs. same length period immediately before"}
          </div>
          <div className="mmt-detail-total">
            {formatMmtMetricValue(metricTotals[activeMetric], activeMetric)}
          </div>
          {chartData.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this metric/date range.</div>
          ) : (
            <MMTBarChart data={chartData} />
          )}
        </div>
      </div>
    </div>
  );
}
const MMT_PROPERTY_COL_WIDTH = 210;
const MMT_SIDE_COL_WIDTH = 120;
const MMT_METRIC_COL_WIDTH = 78;
const MMT_RANKLY_LEFT = MMT_PROPERTY_COL_WIDTH;
const MMT_MAX_DATE_COLUMNS = 15;
function MMTDashboardTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedPropertyName, setSelectedPropertyName] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showComp, setShowComp] = useState(false);
  const [showVar, setShowVar] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPopup, setSyncPopup] = useState(null);
  const [lastSyncedAt, recordSync] = useLastSyncedTracking("revx_lastsync_mmt");
  const syncPollRef = useRef(null);
  const syncTimeoutRef = useRef(null);
  const [metricDisplayModes, setMetricDisplayModes] = useState({
    "Room Nights": "sum",
    Revenue: "sum",
    ASP: "revrn",
    "Property Visits": "sum",
    Conversion: "average",
  });
  const ranklyRatings = useRanklyRatingsLookup();
    useEffect(() => {
    fetch(apiUrl("/api/mmt/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) { setRawRows(json.data); recordSync(json.data); }
        else setLoadError(json.error || "Failed to load data");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);
  const availableDates = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.date).filter(Boolean))).sort(),
    [rawRows]
  );
  useEffect(() => {
    if (availableDates.length > 0 && !fromDate && !toDate) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - 7);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }, [availableDates]);
  function resetDateRange() {
    if (availableDates.length > 0) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - 7);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (availableDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback =
      pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : availableDates[availableDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      if (!map.has(r.hotel_name)) {
        map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent });
      }
    });
    return Array.from(map.values());
  }, [rawRows]);
  const filters = { properties: selectedProperties, locations: selectedLocations, agents: selectedAgents };
  const propertyOptions = useMemo(
    () => getAvailableOptions("name", filters, propertyMeta),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  const locationOptions = useMemo(
    () => getAvailableOptions("location", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedAgents]
  );
  const agentOptions = useMemo(
    () => getAvailableOptions("agent", filters, propertyMeta),
    [propertyMeta, selectedProperties, selectedLocations]
  );
  const effectiveMetrics = useMemo(() => {
    if (selectedMetrics.length === 0) return MMT_METRICS;
    return MMT_METRICS.filter((m) => selectedMetrics.includes(m.label));
  }, [selectedMetrics]);
  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate).slice(0, MMT_MAX_DATE_COLUMNS);
  }, [availableDates, fromDate, toDate]);
  const valuesByDateHotelMetric = useMemo(() => {
    const map = {};
    rawRows.forEach((row) => {
      const d = row.date;
      if (!map[d]) map[d] = {};
      if (!map[d][row.hotel_name]) map[d][row.hotel_name] = {};
      map[d][row.hotel_name][row.metric] = {
        value: Number(row.value) || 0,
        competitor_value: Number(row.competitor_value) || 0,
      };
    });
    return map;
  }, [rawRows]);
  const visibleMeta = useMemo(() => {
    return propertyMeta.filter(
      (m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)
    );
  }, [propertyMeta, selectedProperties, selectedLocations, selectedAgents, searchQuery]);
  const rows = useMemo(() => {
    return visibleMeta.map((meta) => {
      const perDate = dateColumns.map((d) => {
        return effectiveMetrics.map((m) => {
          const cell = valuesByDateHotelMetric[d]?.[meta.name]?.[m.key];
          const value = cell?.value || 0;
          const competitor_value = cell?.competitor_value || 0;
          return { value, competitor_value, variance: value - competitor_value };
        });
      });
      const metricTotals = effectiveMetrics.map((m, mIdx) => {
        const sum = perDate.reduce((s, dateGroup) => s + (dateGroup[mIdx]?.value || 0), 0);
        const compSum = perDate.reduce((s, dateGroup) => s + (dateGroup[mIdx]?.competitor_value || 0), 0);
        const varSum = sum - compSum;
        return { sum, compSum, varSum };
      });
      return { property: meta.name, location: meta.location, agent: meta.agent, perDate, metricTotals };
    });
  }, [visibleMeta, dateColumns, valuesByDateHotelMetric, effectiveMetrics]);
  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const metricMatch = sortKey.match(/^mmtmetric:(\d+):(\d+):(value|competitor_value|variance)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (sortKey === "ranklyMmt") {
        const ra = ranklyRatings.getRating(a.property, "makemytrip")?.rating ?? null;
        const rb = ranklyRatings.getRating(b.property, "makemytrip")?.rating ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = ra - rb;
      } else if (sortKey === "ranklyGoibibo") {
        const ra = ranklyRatings.getRating(a.property, "goibibo")?.rating ?? null;
        const rb = ranklyRatings.getRating(b.property, "goibibo")?.rating ?? null;
        if (ra === null && rb === null) cmp = 0;
        else if (ra === null) cmp = 1;
        else if (rb === null) cmp = -1;
        else cmp = ra - rb;
      } else if (metricMatch) {
        const dIdx = Number(metricMatch[1]);
        const mIdx = Number(metricMatch[2]);
        const field = metricMatch[3];
        const va = a.perDate[dIdx]?.[mIdx]?.[field] ?? 0;
        const vb = b.perDate[dIdx]?.[mIdx]?.[field] ?? 0;
        cmp = va - vb;
      } else if (sortKey.startsWith("mmttotal:")) {
        const mIdx = Number(sortKey.split(":")[1]);
        const metric = effectiveMetrics[mIdx];
        if (metric && metric.key === "ASP" && metricDisplayModes.ASP === "revrn") {
          function aspRevRn(row) {
            const rnSum = dateColumns.reduce(
              (s, d) => s + (valuesByDateHotelMetric[d]?.[row.property]?.["Room Nights"]?.value || 0),
              0
            );
            const revSum = dateColumns.reduce(
              (s, d) => s + (valuesByDateHotelMetric[d]?.[row.property]?.["Revenue"]?.value || 0),
              0
            );
            return rnSum > 0 ? revSum / rnSum : 0;
          }
          cmp = aspRevRn(a) - aspRevRn(b);
        } else {
          const va = a.metricTotals[mIdx]?.sum ?? 0;
          const vb = b.metricTotals[mIdx]?.sum ?? 0;
          cmp = va - vb;
        }
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [rows, sortKey, sortDirection, ranklyRatings, effectiveMetrics, metricDisplayModes, dateColumns, valuesByDateHotelMetric]);
  function handleHeaderClick(key) {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }
  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSelectedMetrics([]);
    setSearchQuery("");
    resetDateRange();
  }
  const MMT_SYNC_POLL_INTERVAL_MS = 30000;
  const MMT_SYNC_WAIT_MS = 15.5 * 60 * 1000;
  function stopSyncPolling() {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
  }
  async function refreshMmtOnce() {
    const res = await fetch(apiUrl("/api/mmt/summary"));
    const json = await res.json();
    if (!json.success) throw new Error(json.error || "Refresh failed");
    return json.data;
  }
  async function handleSyncClick() {
    if (syncing) return;
    setSyncing(true);
    setSyncPopup(null);
    try {
      const res = await fetch("/api/mmt/sync", { method: "POST" });
      const json = await res.json();
      if (!json.success) {
        setSyncing(false);
        setSyncPopup({ type: "error", message: json.error || "Couldn't start sync." });
        return;
      }
    } catch (err) {
      setSyncing(false);
      setSyncPopup({ type: "error", message: err.message });
      return;
    }
    syncPollRef.current = setInterval(async () => {
      try {
        const data = await refreshMmtOnce();
        setRawRows(data); recordSync(data);
      } catch {
      }
    }, MMT_SYNC_POLL_INTERVAL_MS);
    syncTimeoutRef.current = setTimeout(async () => {
      stopSyncPolling();
      try {
        const data = await refreshMmtOnce();
        setRawRows(data); recordSync(data);
        setSyncPopup({ type: "success", message: "Data has been refreshed." });
      } catch (err) {
        setSyncPopup({ type: "error", message: err.message });
      } finally {
        setSyncing(false);
      }
    }, MMT_SYNC_WAIT_MS);
  }
  useEffect(() => {
    return () => stopSyncPolling();
  }, []);
  useEffect(() => {
    if (!syncPopup) return;
    const t = setTimeout(() => setSyncPopup(null), 8000);
    return () => clearTimeout(t);
  }, [syncPopup]);
  // Computes the REV/RN-mode ASP for a given property from either the
  // "value" field (REVX) or "competitor_value" field (COMP) — reused for
  // the base, COMP and VAR ASP totals so all three stay on the same
  // sum(Revenue)/sum(Room Nights) basis rather than averaging per-date
  // ASP figures.
  function aspRevRnFor(propertyName, field) {
    const rnSum = dateColumns.reduce(
      (s, d) => s + (valuesByDateHotelMetric[d]?.[propertyName]?.["Room Nights"]?.[field] || 0),
      0
    );
    const revSum = dateColumns.reduce(
      (s, d) => s + (valuesByDateHotelMetric[d]?.[propertyName]?.["Revenue"]?.[field] || 0),
      0
    );
    return rnSum > 0 ? revSum / rnSum : 0;
  }
  if (loading) return <div className="rd-blank-tab">Loading MMT data...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load MMT data: {loadError}</div>;
  if (selectedPropertyName) {
    return (
      <MMTPropertyDetailView
        allRows={rawRows}
        initialPropertyName={selectedPropertyName}
        onBack={() => setSelectedPropertyName(null)}
      />
    );
  }
  const colsPerMetric = 1 + (showComp ? 1 : 0) + (showVar ? 1 : 0);
  const colsPerDate = effectiveMetrics.length * colsPerMetric;
  const totalColSpan = colsPerMetric;
  return (
    <>
      {syncPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${syncPopup.type}`}>
          <span>{syncPopup.message}</span>
          <button onClick={() => setSyncPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter
              label="Property Name"
              options={propertyOptions}
              selected={selectedProperties}
              onChange={setSelectedProperties}
              onClear={() => setSelectedProperties([])}
            />
            <MultiSelectFilter
              label="Location"
              options={locationOptions}
              selected={selectedLocations}
              onChange={setSelectedLocations}
              onClear={() => setSelectedLocations([])}
            />
            <MultiSelectFilter
              label="Agent"
              options={agentOptions}
              selected={selectedAgents}
              onChange={setSelectedAgents}
              onClear={() => setSelectedAgents([])}
            />
            <MultiSelectFilter
              label="Metric"
              options={MMT_METRICS.map((m) => m.label)}
              selected={selectedMetrics}
              onChange={setSelectedMetrics}
              onClear={() => setSelectedMetrics([])}
            />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={availableDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={resetDateRange}
              onToday={goToToday}
            />
            <button
              type="button"
              onClick={() => setShowComp((v) => !v)}
              className={`rd-btn-toggle${showComp ? " rd-btn-toggle-active" : ""}`}
              title={showComp ? "Hide competitor data" : "Show competitor data"}
            >
              COMP
            </button>
            <button
              type="button"
              onClick={() => setShowVar((v) => !v)}
              className={`rd-btn-toggle${showVar ? " rd-btn-toggle-active" : ""}`}
              title={showVar ? "Hide variance vs comp" : "Show variance vs comp (REVX minus COMP)"}
            >
              VAR
            </button>
            <div className="rd-actions">
              {canUseSync() && (
                <button
                  onClick={handleSyncClick}
                  disabled={syncing}
                  className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`}
                  title="Fetch the latest MMT data now (can take up to 15 minutes)"
                >
                  <RefreshCw size={14} className={syncing ? "rd-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync"}
                </button>
              )}
              <LastSyncedLabel lastSyncedAt={lastSyncedAt} />
              <button onClick={clearAllFilters} className="rd-btn-clear">
                <X size={14} />
                Clear all filters
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table mmt-table">
              <colgroup>
                <col style={{ width: MMT_PROPERTY_COL_WIDTH }} />
                <col style={{ width: MMT_METRIC_COL_WIDTH }} />
                <col style={{ width: MMT_METRIC_COL_WIDTH }} />
                {dateColumns.map((d) =>
                  [...Array(colsPerDate)].map((_, i) => (
                    <col key={`${d}-${i}-col`} style={{ width: MMT_METRIC_COL_WIDTH }} />
                  ))
                )}
                {effectiveMetrics.flatMap((m) =>
                  [...Array(totalColSpan)].map((_, i) => (
                    <col key={`total-${m.key}-${i}-col`} style={{ width: MMT_METRIC_COL_WIDTH + 20 }} />
                  ))
                )}
              </colgroup>
              <thead>
                <tr>
                  <th
                    rowSpan={3}
                    onClick={() => handleHeaderClick("property")}
                    style={{ left: 0, width: MMT_PROPERTY_COL_WIDTH }}
                    className="rd-th-fixed"
                  >
                    <span className="rd-count-badge">{sortedRows.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                  <th
                    colSpan={2}
                    rowSpan={2}
                    style={{ left: MMT_RANKLY_LEFT, width: MMT_METRIC_COL_WIDTH * 2 }}
                    className="rd-th-fixed rd-side"
                  >
                    RATING
                  </th>
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={colsPerDate} className="occ-th-group">
                      {formatDateLabel(d)}
                    </th>
                  ))}
                  {effectiveMetrics.map((m, mIdx) => (
                    <th
                      key={`total-filler1-${m.key}`}
                      colSpan={totalColSpan}
                      className={`occ-th-group rd-total-header-blank${mIdx === 0 ? " rd-total-edge" : ""}`}
                    />
                  ))}
                </tr>
                <tr>
                  {dateColumns.map((d) =>
                    effectiveMetrics.map((m) => (
                      <th key={`${d}-${m.key}`} colSpan={colsPerMetric} className="mmt-th-metric-group">
                        {m.abbr}
                      </th>
                    ))
                  )}
                  {effectiveMetrics.map((m, mIdx) => {
                    const sortKeyForMetric = `mmttotal:${mIdx}`;
                    return (
                      <th
                        key={`total-${m.key}`}
                        colSpan={totalColSpan}
                        onClick={() => handleHeaderClick(sortKeyForMetric)}
                        className={`mmt-th-metric-group rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}
                        style={{ cursor: "pointer" }}
                      >
                        {m.abbr} TOTAL
                        {sortKey === sortKeyForMetric && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  <th
                    onClick={() => handleHeaderClick("ranklyMmt")}
                    className="mmt-th-subcol mmt-th-rating-sub"
                    style={{ left: MMT_RANKLY_LEFT, zIndex: 25 }}
                  >
                    MMT
                    {sortKey === "ranklyMmt" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th
                    onClick={() => handleHeaderClick("ranklyGoibibo")}
                    className="mmt-th-subcol mmt-th-rating-sub"
                    style={{ left: MMT_RANKLY_LEFT + MMT_METRIC_COL_WIDTH, zIndex: 25 }}
                  >
                    GOIBIBO
                    {sortKey === "ranklyGoibibo" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  {dateColumns.map((d, dIdx) =>
                    effectiveMetrics.map((m, mIdx) => {
                      const revxKey = `mmtmetric:${dIdx}:${mIdx}:value`;
                      const compKey = `mmtmetric:${dIdx}:${mIdx}:competitor_value`;
                      const varKey = `mmtmetric:${dIdx}:${mIdx}:variance`;
                      return (
                        <React.Fragment key={`${d}-${m.key}-sub`}>
                          <th onClick={() => handleHeaderClick(revxKey)} className="mmt-th-subcol">
                            REVX
                            {sortKey === revxKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                          {showComp && (
                            <th onClick={() => handleHeaderClick(compKey)} className="mmt-th-subcol">
                              COMP
                              {sortKey === compKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                            </th>
                          )}
                          {showVar && (
                            <th onClick={() => handleHeaderClick(varKey)} className="mmt-th-subcol">
                              VAR
                              {sortKey === varKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                            </th>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                  {effectiveMetrics.map((m, mIdx) => (
                    <React.Fragment key={`total-filler3-${m.key}`}>
                      <th className={`mmt-th-subcol rd-total-grid-dark${mIdx === 0 ? " rd-total-edge" : ""}`}>
                        <select
                          value={metricDisplayModes[m.key]}
                          onChange={(e) => setMetricDisplayModes((prev) => ({ ...prev, [m.key]: e.target.value }))}
                          onClick={(e) => e.stopPropagation()}
                          className="rd-total-select"
                        >
                          <option value="sum">Sum</option>
                          {m.key === "ASP" ? (
                            <option value="revrn">REV/RN</option>
                          ) : (
                            <option value="average">Average</option>
                          )}
                        </select>
                      </th>
                      {showComp && <th className="mmt-th-subcol rd-total-grid-dark">COMP TOTAL</th>}
                      {showVar && <th className="mmt-th-subcol rd-total-grid-dark">VAR TOTAL</th>}
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  return (
                    <tr
                      key={row.property}
                      className={`rd-matrix-row ${rowBgClass}`}
                      onClick={() => setSelectedPropertyName(row.property)}
                      title="Click to view this property's MMT performance page"
                    >
                      <td style={{ left: 0, width: MMT_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                        {row.property}
                      </td>
                      <RanklyRatingCell
                        entry={ranklyRatings.getRating(row.property, "makemytrip")}
                        platform="makemytrip"
                        style={{ left: MMT_RANKLY_LEFT, width: MMT_METRIC_COL_WIDTH }}
                        className="rd-td-fixed rd-side rd-rank-cell"
                      />
                      <RanklyRatingCell
                        entry={ranklyRatings.getRating(row.property, "goibibo")}
                        platform="goibibo"
                        style={{ left: MMT_RANKLY_LEFT + MMT_METRIC_COL_WIDTH, width: MMT_METRIC_COL_WIDTH }}
                        className="rd-td-fixed rd-side rd-rank-cell"
                      />
                      {row.perDate.map((dateGroup, dIdx) =>
                        dateGroup.map((cell, mIdx) => (
                          <React.Fragment key={`${dIdx}-${mIdx}`}>
                            <td className="occ-td-t">{formatMmtMetricValue(cell.value, effectiveMetrics[mIdx].key)}</td>
                            {showComp && (
                              <td className="occ-td-a">{formatMmtMetricValue(cell.competitor_value, effectiveMetrics[mIdx].key)}</td>
                            )}
                            {showVar && (
                              <td className={cell.variance >= 0 ? "mmt-growth-up" : "mmt-growth-down"}>
                                {cell.variance >= 0 ? "▲" : "▼"} {formatMmtMetricValue(Math.abs(cell.variance), effectiveMetrics[mIdx].key)}
                              </td>
                            )}
                          </React.Fragment>
                        ))
                      )}
                      {effectiveMetrics.map((m, mIdx) => {
                        const mode = metricDisplayModes[m.key];
                        const divisor = dateColumns.length || 1;
                        const raw = row.metricTotals[mIdx].sum;
                        const compRaw = row.metricTotals[mIdx].compSum;
                        const varRaw = row.metricTotals[mIdx].varSum;
                        let displayed, compDisplayed, varDisplayed;
                        if (m.key === "ASP" && mode === "revrn") {
                          const revxAsp = aspRevRnFor(row.property, "value");
                          const compAsp = aspRevRnFor(row.property, "competitor_value");
                          displayed = revxAsp;
                          compDisplayed = compAsp;
                          varDisplayed = revxAsp - compAsp;
                        } else {
                          displayed = mode === "sum" ? raw : raw / divisor;
                          compDisplayed = mode === "sum" ? compRaw : compRaw / divisor;
                          varDisplayed = mode === "sum" ? varRaw : varRaw / divisor;
                        }
                        return (
                          <React.Fragment key={`total-${m.key}`}>
                            <td className={`rd-total-td ${rowBgClass}${mIdx === 0 ? " rd-total-edge" : ""}`}>
                              {formatMmtMetricValue(displayed, m.key)}
                            </td>
                            {showComp && (
                              <td className={`rd-total-td ${rowBgClass}`}>
                                {formatMmtMetricValue(compDisplayed, m.key)}
                              </td>
                            )}
                            {showVar && (
                              <td className={`rd-total-td ${rowBgClass} ${varDisplayed >= 0 ? "mmt-growth-up" : "mmt-growth-down"}`}>
                                {varDisplayed >= 0 ? "▲" : "▼"} {formatMmtMetricValue(Math.abs(varDisplayed), m.key)}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
function MMTCompDashboardTab() {
  const [rawRows, setRawRows] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(apiUrl("/api/mmt/summary")).then((res) => res.json()),
      fetch(apiUrl("/api/mmt/asp/dates")).then((res) => res.json()),
    ])
      .then(([summaryJson, datesJson]) => {
        if (summaryJson.success) setRawRows(summaryJson.data);
        else setLoadError(summaryJson.error || "Failed to load data");

        if (datesJson.success) setAvailableDates(datesJson.data);
        else setLoadError((prev) => prev || datesJson.error || "Failed to load available dates");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      if (!map.has(r.hotel_name)) {
        map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent });
      }
    });
    return Array.from(map.values());
  }, [rawRows]);

  if (loading) return <div className="rd-blank-tab">Loading MMT Comp data...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load MMT Comp data: {loadError}</div>;

  return <MMTCompAllPropertiesView propertyMeta={propertyMeta} allDates={availableDates} />;
}
const RP_PROPERTY_COL_WIDTH = 210;

function rpParityClass(status) {
  if (status === "MAJOR_BREACH") return "rp-cell-major";
  if (status === "MINOR_BREACH") return "rp-cell-minor";
  if (status === "OK") return "rp-cell-ok";
  return "occ-cell-empty";
}

function formatRpPrice(value) {
  if (value === null || value === undefined) return "—";
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

const KNOWN_OTA_KEYWORDS = [
  "agoda", "booking", "makemytrip", "mmt", "goibibo", "cleartrip",
  "expedia", "google", "hostelworld", "hotels.com", "ixigo",
  "skyscanner", "traveloka", "trip.com", "yatra", "bookmybooking",
  "easemytrip", "bluepillow", "dealbase",
];
function isKnownOtaName(name) {
  const lower = String(name || "").toLowerCase();
  return KNOWN_OTA_KEYWORDS.some((kw) => lower.includes(kw));
}

const RP_OTA_ORDER_GROUPS = [
  { rank: 0, keywords: ["agoda"] },
  { rank: 1, keywords: ["booking"] },
  { rank: 2, keywords: ["makemytrip", "mmt"] },
  { rank: 3, keywords: ["goibibo"] },
  { rank: 4, keywords: ["cleartrip"] },
  { rank: 5, keywords: ["expedia"] },
  { rank: 6, keywords: ["easemytrip"] },
];
function getRpOtaRank(otaName) {
  const lower = String(otaName || "").toLowerCase().trim();
  for (const group of RP_OTA_ORDER_GROUPS) {
    if (group.keywords.some((kw) => lower.startsWith(kw))) return group.rank;
  }
  return RP_OTA_ORDER_GROUPS.length;
}
function sortOtaNamesFixedOrder(names) {
  return [...names].sort((a, b) => {
    const ra = getRpOtaRank(a);
    const rb = getRpOtaRank(b);
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
}

function usePmsLowestRateLookup() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetch(apiUrl("/api/occupancy/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRows(json.data);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);
  function _normalizeKey(name) {
    return String(name || "").trim().toLowerCase();
  }
  const map = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const propKey = _normalizeKey(r.property_name);
      const date = r.report_date;
      const rate = r.rate !== null && r.rate !== undefined ? Number(r.rate) : null;
      if (!propKey || !date || rate === null || Number.isNaN(rate)) return;
      if (!m[propKey]) m[propKey] = {};
      if (m[propKey][date] === undefined || rate < m[propKey][date]) {
        m[propKey][date] = rate;
      }
    });
    return m;
  }, [rows]);
  return useMemo(
    () => ({
      loaded,
      getLowestRate: (propertyName, date) => {
        const propKey = _normalizeKey(propertyName);
        return map[propKey] ? map[propKey][date] ?? null : null;
      },
    }),
    [map, loaded]
  );
}

function RateParityTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedOtas, setSelectedOtas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [otaSortByProperty, setOtaSortByProperty] = useState({});
  const [highlightedOtaByProperty, setHighlightedOtaByProperty] = useState({});

  const realToday = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(realToday);
  const [toDate, setToDate] = useState(realToday);

  const pmsLowest = usePmsLowestRateLookup();

  useEffect(() => {
    fetch(apiUrl("/api/rateparity/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) setRawRows(json.data);
        else setLoadError(json.error || "Failed to load data");
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    function fetchHighlights() {
      fetch(apiUrl("/api/rateparity/highlight"))
        .then((res) => res.json())
        .then((json) => {
          if (!cancelled && json.success) setHighlightedOtaByProperty(json.highlights || {});
        })
        .catch(() => {});
    }
    fetchHighlights();
    const interval = setInterval(fetchHighlights, 45000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const availableDates = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.check_in).filter(Boolean))).sort(),
    [rawRows]
  );

  const didInitDate = useRef(false);
  useEffect(() => {
    if (!didInitDate.current && availableDates.length > 0) {
      didInitDate.current = true;
      if (!availableDates.includes(realToday)) goToToday();
    }
  }, [availableDates]);

  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }

  function goToToday() {
    if (availableDates.length === 0) {
      setFromDate(realToday);
      setToDate(realToday);
      return;
    }
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0
      ? pastOrToday[pastOrToday.length - 1]
      : availableDates[availableDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }

  function clearDates() {
    setFromDate("");
    setToDate("");
  }

  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate).slice(0, MAX_DATE_COLUMNS);
  }, [availableDates, fromDate, toDate]);

  const propertyMeta = useMemo(() => {
    const map = new Map();
    rawRows.forEach((r) => {
      if (!map.has(r.property_name)) {
        map.set(r.property_name, { name: r.property_name, location: r.location, agent: r.agent });
      }
    });
    return Array.from(map.values());
  }, [rawRows]);

  const otaOptions = useMemo(() => {
    return Array.from(
      new Set(rawRows.map((r) => r.ota_name).filter(Boolean).filter(isKnownOtaName))
    ).sort();
  }, [rawRows]);

  const filters = { properties: selectedProperties, locations: selectedLocations, agents: selectedAgents };
  const propertyOptions = useMemo(() => getAvailableOptions("name", filters, propertyMeta), [propertyMeta, selectedLocations, selectedAgents]);
  const locationOptions = useMemo(() => getAvailableOptions("location", filters, propertyMeta), [propertyMeta, selectedProperties, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", filters, propertyMeta), [propertyMeta, selectedProperties, selectedLocations]);

  const rowsByPropertyDate = useMemo(() => {
    const map = {};
    rawRows.forEach((r) => {
      if (!isKnownOtaName(r.ota_name)) return;
      if (selectedOtas.length > 0 && !selectedOtas.includes(r.ota_name)) return;
      if (!map[r.property_name]) map[r.property_name] = {};
      if (!map[r.property_name][r.check_in]) {
        map[r.property_name][r.check_in] = { baseline: r.baseline_price, worstStatus: "OK", otas: [] };
      }
      const cell = map[r.property_name][r.check_in];
      cell.otas.push({
        ota_name: r.ota_name,
        price: r.price,
        parity_status: r.parity_status,
        is_official_site: r.is_official_site,
      });
      if (r.parity_status === "MAJOR_BREACH") cell.worstStatus = "MAJOR_BREACH";
      else if (r.parity_status === "MINOR_BREACH" && cell.worstStatus !== "MAJOR_BREACH") cell.worstStatus = "MINOR_BREACH";
    });
    return map;
  }, [rawRows, selectedOtas]);

  const visibleMeta = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery)),
    [propertyMeta, selectedProperties, selectedLocations, selectedAgents, searchQuery]
  );

  const displayRows = useMemo(() => {
    return visibleMeta.map((meta) => {
      const byDate = rowsByPropertyDate[meta.name] || {};
      const perDate = dateColumns.map((d) => {
        const cell = byDate[d] || { baseline: null, worstStatus: null, otas: [] };
        const pmsRate = pmsLowest.getLowestRate(meta.name, d);
        const displayValue = !pmsLowest.loaded
          ? null
          : pmsRate !== null && pmsRate !== undefined
          ? pmsRate
          : cell.baseline;
        return { ...cell, displayValue };
      });
      return { property: meta.name, location: meta.location, agent: meta.agent, perDate };
    });
  }, [visibleMeta, rowsByPropertyDate, dateColumns, pmsLowest]);

  const sortedRows = useMemo(() => {
    const arr = [...displayRows];
    const dateMatch = sortKey.match(/^rpdate:(\d+)$/);
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") cmp = a.property.localeCompare(b.property);
      else if (sortKey === "location") cmp = a.location.localeCompare(b.location);
      else if (sortKey === "agent") cmp = a.agent.localeCompare(b.agent);
      else if (dateMatch) {
        const dIdx = Number(dateMatch[1]);
        const va = a.perDate[dIdx]?.displayValue ?? -Infinity;
        const vb = b.perDate[dIdx]?.displayValue ?? -Infinity;
        cmp = va - vb;
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [displayRows, sortKey, sortDirection]);

  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }

  function handleRowClick(propertyName) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(propertyName)) next.delete(propertyName);
      else next.add(propertyName);
      return next;
    });
  }

  const allExpanded = sortedRows.length > 0 && sortedRows.every((r) => expandedKeys.has(r.property));
  function toggleExpandAll() {
    if (allExpanded) setExpandedKeys(new Set());
    else setExpandedKeys(new Set(sortedRows.map((r) => r.property)));
  }

  function handlePropertyOtaChange(propertyName, value) {
    setHighlightedOtaByProperty((prev) => ({ ...prev, [propertyName]: value }));
    if (value) {
      setExpandedKeys((prev) => new Set([...prev, propertyName]));
    }
    fetch(apiUrl("/api/rateparity/highlight"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ property: propertyName, ota: value }),
    }).catch(() => {});
  }

  const orderedOtaNames = useMemo(() => {
    const base = selectedOtas.length > 0 ? selectedOtas : otaOptions;
    return sortOtaNamesFixedOrder(base);
  }, [otaOptions, selectedOtas]);

  function getOtaSubrows(row) {
    let subrows = orderedOtaNames.map((otaName) => ({
      ota_name: otaName,
      perDate: row.perDate.map((cell) => cell.otas.find((o) => o.ota_name === otaName) || null),
    }));

    const thisSort = otaSortByProperty[row.property];
    if (thisSort) {
      const { dateIdx, direction } = thisSort;
      subrows = [...subrows].sort((a, b) => {
        const va = a.perDate[dateIdx]?.price;
        const vb = b.perDate[dateIdx]?.price;
        const missA = va === null || va === undefined;
        const missB = vb === null || vb === undefined;
        if (missA && missB) return 0;
        if (missA) return 1;
        if (missB) return -1;
        const cmp = va - vb;
        return direction === "desc" ? -cmp : cmp;
      });
    }

    return subrows;
  }

  function handleOtaSortClick(propertyName, dateIdx, e) {
    e.stopPropagation();
    setOtaSortByProperty((prev) => {
      const current = prev[propertyName];
      const nextDirection = current && current.dateIdx === dateIdx && current.direction === "asc" ? "desc" : "asc";
      return { ...prev, [propertyName]: { dateIdx, direction: nextDirection } };
    });
  }

  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSelectedOtas([]);
    setSearchQuery("");
    goToToday();
  }

  if (loading) return <div className="rd-blank-tab">Loading rate parity data...</div>;
  if (loadError) return <div className="rd-blank-tab">Couldn't load rate parity data: {loadError}</div>;

  return (
    <>
      <div className="rd-filter-bar occ-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <MultiSelectFilter label="Property Name" options={propertyOptions} selected={selectedProperties} onChange={setSelectedProperties} onClear={() => setSelectedProperties([])} />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <MultiSelectFilter label="OTA" options={otaOptions} selected={selectedOtas} onChange={setSelectedOtas} onClear={() => setSelectedOtas([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <div className="occ-date-and-clear">
              <DateRangeControl
                availableDates={availableDates}
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={handleFromDateChange}
                onToChange={setToDate}
                onClear={clearDates}
                onToday={goToToday}
              />
              <div className="rd-actions occ-actions-row">
                <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rd-matrix-section">
        <div className="occ-table-toolbar">
          <button onClick={toggleExpandAll} className="occ-expand-all-btn" title={allExpanded ? "Collapse all properties" : "Expand all properties"}>
            <ChevronsUpDown size={13} />
            {allExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: RP_PROPERTY_COL_WIDTH }} />
                <col style={{ width: 150 }} />
                {dateColumns.map((d) => <col key={`${d}-col`} style={{ width: OCC_METRIC_COL_WIDTH }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th onClick={() => handleHeaderClick("property")} style={{ left: 0, width: RP_PROPERTY_COL_WIDTH }} className="rd-th-fixed">
                    <span className="rd-count-badge">{sortedRows.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th className="rd-th-fixed rp-th-platform" style={{ width: 150 }}>PLATFORM</th>
                  {dateColumns.map((d, dIdx) => {
                    const key = `rpdate:${dIdx}`;
                    return (
                      <th key={d} onClick={() => handleHeaderClick(key)} className="rd-th-date-group" style={{ cursor: "pointer" }}>
                        {formatDateLabel(d)}
                        {sortKey === key && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  const isExpanded = expandedKeys.has(row.property);
                  return (
                    <React.Fragment key={row.property}>
                      <tr onClick={() => handleRowClick(row.property)} className={`rd-matrix-row ${rowBgClass}`}>
                        <td style={{ left: 0, width: RP_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                          {row.property}
                          <ChevronDown size={12} className={`occ-expand-icon ${isExpanded ? "occ-expanded" : ""}`} />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            value={highlightedOtaByProperty[row.property] || ""}
                            onChange={(e) => handlePropertyOtaChange(row.property, e.target.value)}
                            className="rd-dropdown-btn"
                            style={{ width: "auto" }}
                            title="Reference platform for this property — highlights its row and drives its Rate Parity Task"
                          >
                            <option value="">Select platform...</option>
                            {sortOtaNamesFixedOrder(otaOptions).map((ota) => (
                              <option key={ota} value={ota}>{ota}</option>
                            ))}
                          </select>
                        </td>
                        {row.perDate.map((cell, dIdx) => {
                          const thisSort = otaSortByProperty[row.property];
                          const isActiveSortCol = isExpanded && thisSort && thisSort.dateIdx === dIdx;
                          return (
                            <td
                              key={dIdx}
                              onClick={isExpanded ? (e) => handleOtaSortClick(row.property, dIdx, e) : undefined}
                              className="occ-td-adr rp-price-cell"
                              style={isExpanded ? { cursor: "pointer" } : undefined}
                              title={isExpanded ? "Sort this property's OTAs by price for this date" : undefined}
                            >
                              {formatRpPrice(cell.displayValue)}
                              {isActiveSortCol && (
                                <span className="rd-sort-indicator">{thisSort.direction === "asc" ? "▲" : "▼"}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && getOtaSubrows(row).map((sub) => (
                        <tr
                          key={`${row.property}-${sub.ota_name}`}
                          className={`occ-subrow${sub.ota_name === highlightedOtaByProperty[row.property] ? " rp-ota-row-highlighted" : ""}`}
                        >
                          <td style={{ left: 0, width: RP_PROPERTY_COL_WIDTH }} className="occ-subrow-label rd-td-fixed">
                            {sub.ota_name}
                          </td>
                          {sub.perDate.map((cell, dIdx) => {
                            const refValue = row.perDate[dIdx]?.displayValue;
                            const hasComparison =
                              cell && cell.price !== null && cell.price !== undefined &&
                              refValue !== null && refValue !== undefined && refValue !== 0;
                            const pct = hasComparison ? ((cell.price - refValue) / refValue) * 100 : null;
                            return (
                              <td key={dIdx} className="rp-price-cell">
                                {cell ? (
                                  <>
                                    {formatRpPrice(cell.price)}
                                    {cell.is_official_site && <span className="rp-official-badge">official</span>}
                                    {pct !== null && (
                                      <span className={`rp-pms-diff ${pct >= 0 ? "rp-diff-up" : "rp-diff-down"}`}>
                                        {pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
                                      </span>
                                    )}
                                  </>
                                ) : "—"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
const BOOKPERDAY_PROPERTY_COL_WIDTH = 230;
const BOOKPERDAY_METRIC_COL_WIDTH = 62;
const BOOKPERDAY_TOTAL_METRIC_COL_WIDTH = 72;
const BOOKPERDAY_MAX_DATE_COLUMNS = 14;

function bookPerDayCountCellClass(value) {
  if (value === 0) return "occ-cell-low";
  if (value <= 8) return "occ-cell-mid";
  return "occ-cell-high";
}
function formatBookPerDayCurrency(value) {
  if (!value) return "₹0";
  return `₹${Math.round(value).toLocaleString("en-IN")}`;
}

function classifyBookPerDaySource(rawSource) {
  const s = String(rawSource || "").trim().toUpperCase();
  if (!s || s === "NO BOOKING") return "NO BOOKING";
  // Reuses the SAME SUB_SOURCE_OTA / SUB_SOURCE_REVX sets the Revenue
  // Dashboard's classifySubSource uses (defined once, near the top of
  // this file) instead of a separate hand-typed copy — the two dashboards
  // had drifted apart (this one was missing AIRBNB, HOSTEL_WORLD and
  // SIDDARTH), silently misclassifying those sources as HOTEL here while
  // the Revenue Dashboard correctly grouped them as OTA/REVX. Sharing one
  // set means the two can no longer disagree.
  if (SUB_SOURCE_OTA.has(s)) return "OTA";
  if (SUB_SOURCE_REVX.has(s)) return "REVX";
  return "HOTEL";
}
const BOOKPERDAY_SOURCE_GROUP_OPTIONS = ["OTA", "REVX", "HOTEL"];

const BOOKPERDAY_METRIC_DEFS = [
  { key: "bookings", label: "BOOKINGS", totalSortKey: "totalBookings" },
  { key: "roomNights", label: "RN", totalSortKey: "totalRoomNights" },
  { key: "adr", label: "ADR", totalSortKey: "totalAdr" },
  { key: "revenue", label: "REVENUE", totalSortKey: "totalRevenue" },
];

function BookPerDayTab() {
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedSubSources, setSelectedSubSources] = useState([]);
  const [selectedSourceGroups, setSelectedSourceGroups] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("property");
  const [sortDirection, setSortDirection] = useState("asc");
  const [syncing, setSyncing] = useState(false);
  const [syncPopup, setSyncPopup] = useState(null);
  const [lastSyncedAt, recordSync] = useLastSyncedTracking("revx_lastsync_bookperday");

  useEffect(() => {
    fetch(apiUrl("/api/bookperday/summary"))
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setRawRows(json.data); recordSync(json.data);
        } else {
          setLoadError(json.error || "Failed to load data");
        }
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const availableDates = useMemo(() => {
    return Array.from(new Set(rawRows.map((r) => r.booking_date).filter(Boolean))).sort();
  }, [rawRows]);

  useEffect(() => {
    if (availableDates.length > 0 && !fromDate && !toDate) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - BOOKPERDAY_MAX_DATE_COLUMNS);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }, [availableDates]);

  function goToToday() {
    if (availableDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = availableDates.includes(realToday) ? realToday : null;
    const pastOrToday = availableDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0
      ? pastOrToday[pastOrToday.length - 1]
      : availableDates[availableDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  function clearDates() {
    if (availableDates.length > 0) {
      const last = availableDates[availableDates.length - 1];
      const startIdx = Math.max(0, availableDates.length - BOOKPERDAY_MAX_DATE_COLUMNS);
      setFromDate(availableDates[startIdx]);
      setToDate(last);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function clearAllFilters() {
    setSelectedProperties([]);
    setSelectedLocations([]);
    setSelectedSubSources([]);
    setSelectedSourceGroups([]);
    setSelectedMetrics([]);
    setSearchQuery("");
    clearDates();
  }
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }
  async function handleSyncClick() {
    if (syncing) return;
    setSyncing(true);
    setSyncPopup(null);
    try {
      const res = await fetch(apiUrl("/api/bookperday/sync"), { method: "POST" });
      const json = await res.json();
      if (json.success) {
        setRawRows(json.data); recordSync(json.data);
        setSyncPopup({ type: "success", message: "Data has been refreshed." });
      } else {
        setSyncPopup({ type: "error", message: json.error || "Sync failed. Please try again." });
      }
    } catch (err) {
      setSyncPopup({ type: "error", message: err.message });
    } finally {
      setSyncing(false);
    }
  }
  useEffect(() => {
    if (!syncPopup) return;
    const t = setTimeout(() => setSyncPopup(null), 8000);
    return () => clearTimeout(t);
  }, [syncPopup]);

  const dateColumns = useMemo(() => {
    if (!fromDate || !toDate) return [];
    return availableDates.filter((d) => d >= fromDate && d <= toDate);
  }, [availableDates, fromDate, toDate]);

  const filteredRows = useMemo(() => {
    let list = rawRows;
    if (selectedProperties.length > 0) {
      list = list.filter((r) => selectedProperties.includes(r.property_name));
    }
    if (selectedLocations.length > 0) {
      list = list.filter((r) => selectedLocations.includes(r.location));
    }
    if (selectedSourceGroups.length > 0) {
      list = list.filter((r) => selectedSourceGroups.includes(classifyBookPerDaySource(r.source)));
    }
    if (selectedSubSources.length > 0) {
      list = list.filter((r) => selectedSubSources.includes(r.source));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((r) => (r.property_name || "").toLowerCase().includes(q));
    }
    return list;
  }, [rawRows, selectedProperties, selectedLocations, selectedSourceGroups, selectedSubSources, searchQuery]);

  const matrix = useMemo(() => {
    const propertyNames = Array.from(new Set(filteredRows.map((r) => r.property_name).filter(Boolean))).sort();
    return propertyNames.map((property) => {
      const propertyRows = filteredRows.filter((r) => r.property_name === property);
      const perDate = dateColumns.map((d) => {
        const cellRows = propertyRows.filter((r) => r.booking_date === d);
        const roomNights = cellRows.reduce((sum, r) => sum + Number(r.picked_room_nights || 0), 0);
        const revenue = cellRows.reduce((sum, r) => sum + Number(r.room_revenue_before_tax || 0), 0);
        const bookings = cellRows.reduce((sum, r) => sum + Number(r.no_of_bookings || 0), 0);
        const adr = roomNights > 0 ? Math.round(revenue / roomNights) : 0;
        return { bookings, roomNights, adr, revenue };
      });
      const total = perDate.reduce(
        (acc, cell) => ({
          bookings: acc.bookings + cell.bookings,
          roomNights: acc.roomNights + cell.roomNights,
          revenue: acc.revenue + cell.revenue,
        }),
        { bookings: 0, roomNights: 0, revenue: 0 }
      );
      const totalAdr = total.roomNights > 0 ? Math.round(total.revenue / total.roomNights) : 0;
      return { property, perDate, total: { ...total, adr: totalAdr } };
    });
  }, [filteredRows, dateColumns]);

  const sortedMatrix = useMemo(() => {
    const rows = [...matrix];
    rows.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "property") {
        cmp = a.property.localeCompare(b.property);
      } else if (sortKey === "totalBookings") {
        cmp = a.total.bookings - b.total.bookings;
      } else if (sortKey === "totalRoomNights") {
        cmp = a.total.roomNights - b.total.roomNights;
      } else if (sortKey === "totalAdr") {
        cmp = a.total.adr - b.total.adr;
      } else if (sortKey === "totalRevenue") {
        cmp = a.total.revenue - b.total.revenue;
      } else {
        const m = sortKey.match(/^date:(\d+):(bookings|roomNights|adr|revenue)$/);
        if (m) {
          const dIdx = Number(m[1]);
          const metric = m[2];
          cmp = (a.perDate[dIdx]?.[metric] || 0) - (b.perDate[dIdx]?.[metric] || 0);
        }
      }
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return rows;
  }, [matrix, sortKey, sortDirection]);

  const grandTotals = useMemo(() => {
    const perDateTotals = dateColumns.map((_, dIdx) => {
      const sums = matrix.reduce(
        (acc, row) => {
          const cell = row.perDate[dIdx];
          return {
            bookings: acc.bookings + cell.bookings,
            roomNights: acc.roomNights + cell.roomNights,
            revenue: acc.revenue + cell.revenue,
          };
        },
        { bookings: 0, roomNights: 0, revenue: 0 }
      );
      const adr = sums.roomNights > 0 ? Math.round(sums.revenue / sums.roomNights) : 0;
      return { ...sums, adr };
    });
    const grand = matrix.reduce(
      (acc, row) => ({
        bookings: acc.bookings + row.total.bookings,
        roomNights: acc.roomNights + row.total.roomNights,
        revenue: acc.revenue + row.total.revenue,
      }),
      { bookings: 0, roomNights: 0, revenue: 0 }
    );
    const grandAdr = grand.roomNights > 0 ? Math.round(grand.revenue / grand.roomNights) : 0;
    return { perDateTotals, grand: { ...grand, adr: grandAdr } };
  }, [matrix, dateColumns]);

  const propertyOptions = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.property_name).filter(Boolean))).sort(),
    [rawRows]
  );
  const locationOptions = useMemo(
    () => Array.from(new Set(rawRows.map((r) => r.location).filter(Boolean))).sort(),
    [rawRows]
  );
  const subSourceOptions = useMemo(() => {
    let sources = rawRows.filter((r) => r.source && r.source !== "NO BOOKING");
    // Narrow by Property/Location too, matching the Revenue Dashboard's
    // equivalent behavior — previously this only narrowed by Source
    // group, so picking a property still showed every sub-source across
    // ALL properties, including ones that property never actually uses.
    if (selectedProperties.length > 0) {
      sources = sources.filter((r) => selectedProperties.includes(r.property_name));
    }
    if (selectedLocations.length > 0) {
      sources = sources.filter((r) => selectedLocations.includes(r.location));
    }
    if (selectedSourceGroups.length > 0) {
      sources = sources.filter((r) => selectedSourceGroups.includes(classifyBookPerDaySource(r.source)));
    }
    return Array.from(new Set(sources.map((r) => r.source))).sort();
  }, [rawRows, selectedProperties, selectedLocations, selectedSourceGroups]);

  useEffect(() => {
    setSelectedSubSources((prev) => prev.filter((s) => subSourceOptions.includes(s)));
  }, [subSourceOptions]);

  const visibleMetricDefs = useMemo(
    () => (selectedMetrics.length > 0
      ? BOOKPERDAY_METRIC_DEFS.filter((m) => selectedMetrics.includes(m.label))
      : BOOKPERDAY_METRIC_DEFS),
    [selectedMetrics]
  );

  if (loading) {
    return <div className="rd-blank-tab">Loading Book Per Day data...</div>;
  }
  if (loadError) {
    return <div className="rd-blank-tab">Couldn't load Book Per Day data: {loadError}</div>;
  }

  return (
    <>
      {syncPopup && (
        <div className={`rd-sync-toast rd-sync-toast-${syncPopup.type}`}>
          <span>{syncPopup.message}</span>
          <button onClick={() => setSyncPopup(null)} className="rd-sync-toast-close" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
        <div className="rd-filter-row">
          <MultiSelectFilter
            label="Property Name"
            options={propertyOptions}
            selected={selectedProperties}
            onChange={setSelectedProperties}
            onClear={() => setSelectedProperties([])}
          />
          <MultiSelectFilter
            label="Location"
            options={locationOptions}
            selected={selectedLocations}
            onChange={setSelectedLocations}
            onClear={() => setSelectedLocations([])}
          />
          <MultiSelectFilter
            label="Sub Source"
            options={subSourceOptions}
            selected={selectedSubSources}
            onChange={setSelectedSubSources}
            onClear={() => setSelectedSubSources([])}
          />
          <MultiSelectFilter
            label="Source"
            options={BOOKPERDAY_SOURCE_GROUP_OPTIONS}
            selected={selectedSourceGroups}
            onChange={setSelectedSourceGroups}
            onClear={() => setSelectedSourceGroups([])}
          />
          <MultiSelectFilter
            label="Metric"
            options={BOOKPERDAY_METRIC_DEFS.map((m) => m.label)}
            selected={selectedMetrics}
            onChange={setSelectedMetrics}
            onClear={() => setSelectedMetrics([])}
          />
          <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
          <div className="occ-date-and-clear">
            <DateRangeControl
              availableDates={availableDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={clearDates}
              onToday={goToToday}
            />
            <div className="rd-actions occ-actions-row">
              {canUseSync() && (
                <button
                  onClick={handleSyncClick}
                  disabled={syncing}
                  className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`}
                  title="Fetch the latest booking-pickup data now"
                >
                  <RefreshCw size={14} className={syncing ? "rd-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync"}
                </button>
              )}
              <LastSyncedLabel lastSyncedAt={lastSyncedAt} />
              <button onClick={clearAllFilters} className="rd-btn-clear">
                <X size={14} />
                Clear all filters
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
      <div className="rd-matrix-section">
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table occ-table">
              <colgroup>
                <col style={{ width: BOOKPERDAY_PROPERTY_COL_WIDTH }} />
                {dateColumns.map((d) => visibleMetricDefs.map((m) => (
                  <col key={`${d}-${m.key}-col`} style={{ width: BOOKPERDAY_METRIC_COL_WIDTH }} />
                )))}
                {visibleMetricDefs.map((m) => (
                  <col key={`total-${m.key}-col`} style={{ width: BOOKPERDAY_TOTAL_METRIC_COL_WIDTH }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    onClick={() => handleHeaderClick("property")}
                    style={{ left: 0, width: BOOKPERDAY_PROPERTY_COL_WIDTH }}
                    className="rd-th-fixed"
                  >
                    <span className="rd-count-badge">{sortedMatrix.length}</span>
                    PROPERTY NAME
                    {sortKey === "property" && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  {dateColumns.map((d) => (
                    <th key={d} colSpan={visibleMetricDefs.length} className="occ-th-group">
                      {formatDateLabel(d)}
                    </th>
                  ))}
                  <th colSpan={visibleMetricDefs.length} className="occ-th-group rd-total-edge rd-total-header-blank">
                    Total
                  </th>
                </tr>
                <tr>
                  {dateColumns.map((d, dIdx) => (
                    <React.Fragment key={`${d}-sub`}>
                      {visibleMetricDefs.map((m) => {
                        const key = `date:${dIdx}:${m.key}`;
                        return (
                          <th key={m.key} onClick={() => handleHeaderClick(key)} className="occ-th-metric">
                            {m.label}
                            {sortKey === key && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                          </th>
                        );
                      })}
                    </React.Fragment>
                  ))}
                  {visibleMetricDefs.map((m, i) => (
                    <th
                      key={`total-${m.key}`}
                      onClick={() => handleHeaderClick(m.totalSortKey)}
                      className={`occ-th-metric rd-total-grid-dark${i === 0 ? " rd-total-edge" : ""}`}
                    >
                      {m.label}
                      {sortKey === m.totalSortKey && <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedMatrix.map((row, rIdx) => {
                  const rowBgClass = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                  return (
                    <tr key={row.property} className={`rd-matrix-row ${rowBgClass}`}>
                      <td style={{ left: 0, width: BOOKPERDAY_PROPERTY_COL_WIDTH }} className={`rd-td-fixed ${rowBgClass}`}>
                        <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(row.property) }} />
                        {row.property}
                      </td>
                      {row.perDate.map((cell, dIdx) => (
                        <React.Fragment key={dIdx}>
                          {visibleMetricDefs.map((m) => {
                            if (m.key === "bookings") return <td key={m.key} className={`bookperday-cell-center ${bookPerDayCountCellClass(cell.bookings)}`}>{cell.bookings}</td>;
                            if (m.key === "roomNights") return <td key={m.key} className={`bookperday-cell-center ${bookPerDayCountCellClass(cell.roomNights)}`}>{cell.roomNights}</td>;
                            if (m.key === "adr") return <td key={m.key} className="occ-td-adr">{formatBookPerDayCurrency(cell.adr)}</td>;
                            return <td key={m.key} className="occ-td-adr">{formatBookPerDayCurrency(cell.revenue)}</td>;
                          })}
                        </React.Fragment>
                      ))}
                      {visibleMetricDefs.map((m, i) => {
                        const edgeClass = i === 0 ? " rd-total-edge" : "";
                        if (m.key === "bookings") return <td key={m.key} className={`occ-td-t bookperday-cell-center${edgeClass}`} style={{ fontWeight: 700 }}>{row.total.bookings}</td>;
                        if (m.key === "roomNights") return <td key={m.key} className={`occ-td-t bookperday-cell-center${edgeClass}`} style={{ fontWeight: 700 }}>{row.total.roomNights}</td>;
                        if (m.key === "adr") return <td key={m.key} className={`occ-td-t${edgeClass}`} style={{ fontWeight: 700 }}>{formatBookPerDayCurrency(row.total.adr)}</td>;
                        return <td key={m.key} className={`occ-td-t${edgeClass}`} style={{ fontWeight: 700 }}>{formatBookPerDayCurrency(row.total.revenue)}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="booking-total-row">
                  <td style={{ left: 0, width: BOOKPERDAY_PROPERTY_COL_WIDTH }} className="rd-td-fixed">TOTAL</td>
                  {grandTotals.perDateTotals.map((t, dIdx) => (
                    <React.Fragment key={dIdx}>
                      {visibleMetricDefs.map((m) => {
                        if (m.key === "bookings") return <td key={m.key} className="occ-td-t bookperday-cell-center">{t.bookings}</td>;
                        if (m.key === "roomNights") return <td key={m.key} className="occ-td-t bookperday-cell-center">{t.roomNights}</td>;
                        if (m.key === "adr") return <td key={m.key} className="occ-td-t">{formatBookPerDayCurrency(t.adr)}</td>;
                        return <td key={m.key} className="occ-td-t">{formatBookPerDayCurrency(t.revenue)}</td>;
                      })}
                    </React.Fragment>
                  ))}
                  {visibleMetricDefs.map((m, i) => {
                    const edgeClass = i === 0 ? " rd-total-edge" : "";
                    if (m.key === "bookings") return <td key={m.key} className={`occ-td-t bookperday-cell-center${edgeClass}`}>{grandTotals.grand.bookings}</td>;
                    if (m.key === "roomNights") return <td key={m.key} className={`occ-td-t bookperday-cell-center${edgeClass}`}>{grandTotals.grand.roomNights}</td>;
                    if (m.key === "adr") return <td key={m.key} className={`occ-td-t${edgeClass}`}>{formatBookPerDayCurrency(grandTotals.grand.adr)}</td>;
                    return <td key={m.key} className={`occ-td-t${edgeClass}`}>{formatBookPerDayCurrency(grandTotals.grand.revenue)}</td>;
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
const NAV_STORAGE_KEY = "revx_active_nav";
// Retired pages -> the page that replaced them (someone whose last-opened page was an old one
// lands on its replacement, not on the default page).
const RETIRED_NAV = { booking: "bookingmetrics", mmt: "mmtmetrics" };
function getInitialNav() {
  try {
    const stored = localStorage.getItem(NAV_STORAGE_KEY);
    if (stored && RETIRED_NAV[stored]) return RETIRED_NAV[stored];
    if (stored && NAV_ITEMS.some((n) => n.id === stored)) {
      return stored;
    }
  } catch {
  }
  return "rankly";
}
export default function Dashboard() {
  const [activeNav, setActiveNav] = useState(getInitialNav);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    try {
      localStorage.setItem(NAV_STORAGE_KEY, activeNav);
    } catch {
    }
  }, [activeNav]);
  return (
    <div className="rd-app">
      <button className="rd-mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
        <Menu size={20} />
      </button>
      {sidebarOpen && <div className="rd-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <Sidebar
        active={activeNav}
        onSelect={setActiveNav}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="rd-main">
                {activeNav === "rankly" ? (
          <RanklyTab />
        ) : activeNav === "revenue" ? (
          <RevenueDashboardTab />
        ) : activeNav === "occupancy" ? (
          <OccupancyTab />
        ) : activeNav === "booking" ? (
          <BookingDashboardTab />
        ) : activeNav === "bookingmetrics" ? (
          <BookingMetricsDashboard />
        ) : activeNav === "mmt" ? (
          <MMTDashboardTab />
        ) : activeNav === "mmtmetrics" ? (
          <MmtMetricsDashboard />
        ) : activeNav === "mmtcomp" ? (
          <MMTCompDashboardTab />
        ) : activeNav === "rateparity" ? (
          <RateParityTab />
                ) : activeNav === "bookperday" ? (
          <BookPerDayTab />
        ) : activeNav === "agodapickup" ? (
          <AgodaPickupDashboard />
        ) : activeNav === "maindashboard" ? (
          <MainDashboard />
        ) : activeNav === "variancedashboard" ? (
          <VarianceDashboard />
        ) : activeNav === "tasks" ? (
          <Task />
        ) : activeNav === "calendar" ? (
          <Calendar />
        ) : (
          <BlankTab label={NAV_ITEMS.find((n) => n.id === activeNav)?.label} />
        )}
      </main>
    </div>
  );
}
